// ============================================================================
// render-engine.js — Design Profile → SVG STRING (pure, ring-constrained)
// ============================================================================
// REWRITTEN 2026-07-11 for the element/slot model (design-profile.js). The
// design is authored ONLY on the plate's printable ring ("ring B"); the engine
// draws optional flat plate chrome (for the 2D editing view) + the placed
// elements (logo / weight / arched text), each styled with a fill and a stack
// of sharp mitered outlines.
//
// COORDINATE CONTRACT (fixes the old 220-vs-225 margin bug): viewBox 0..450 ==
// plate DIAMETER exactly, so CENTER=225 and PLATE_R=225 map 1:1 onto the 3D
// geometry's planar UV (uv = pos/(2R)+0.5). Ring B (the FLAT design floor, inset
// from the 0.505/0.873 grooves by the slanted drops) spans radius ≈115.4→194.7.
//
// PURE: no window/DOM/three/CSS. The SVG is self-contained (inline attrs), so
// the SAME string feeds the 2D preview (innerHTML) and the 3D texture
// (rasterized) — guaranteeing color fidelity (V2 plan §5). 2D==3D text metrics
// are pinned via `font-kerning:none; text-rendering:geometricPrecision`.
// ============================================================================

import { WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP, resolvePaletteRef, effectiveTextStyle, contrastPoolFrom, fontFamilyFor, fontWeightFor, fontWidthRatio, fontInkOffset, fontCapFrac, strAdvanceFrac, charAdvanceFrac, GRIP_SIZE_OPEN_FACTOR, openShareFactorFor, numberEdgeFractionFor, weightOpenPctFor, DEFAULT_FONT_ID, DEFAULT_ELEMENT_SIZE_SCALE, resolveLogoAspect, normalizeHex, DEFAULT_BODY_COLOR, clampBigNudge, displayWeightText, logoGapFromStructure, outlineThicknessFor, LOGO_BOX_NOMINAL_VB } from './design-profile.js';
import { weightInk, weightCap } from './fonts/ink-weight.js';
// ⚠ THE PREDICATE IS IMPORTED, NOT MIRRORED. This file deliberately mirrors grip-plate's
// MEASUREMENTS in inches (see the SMALL_GRIP_* block below) because it works in inches — but
// `isSmallGrip` is a RULE, not a measurement, and grip-plate names itself "the ONE predicate —
// every branch in this file, in render-engine.js and in the layout gating asks this and nothing
// else". A local re-derivation from the diameter table would be a second way to answer the same
// question, which is how the two drift apart on the day the cliff moves.
import { isSmallGrip } from './geometry/grip-plate.js';
import { DEFAULT_SIZE_PCT, weightSizePctFor } from './design-profile.js';
import { bandsVisibleOnPlate, presentedOuterOutline, renderPaletteFor } from './logo-outline.js';
// Head geometry for the cap's room compensation — see `capHeadDiaIn` for why these are imported
// where the grip's constants are mirrored. `products.js` imports both already, so nothing new
// reaches the entry bundle; `DEFAULT_WEIGHT_BY_CATEGORY` is the reference denomination per model.
import { dumbbellHeadDims } from './geometry/dumbbell.js';
import { barbellHeadDims } from './geometry/barbell.js';
import { DEFAULT_WEIGHT_BY_CATEGORY } from './products.js';

// ── THE WEIGHT LOCKUP IS MEASURED ON ITS OWN STRING (Ryan 2026-07-28) ─────────────────────────────
// `fontInkOffset` / `fontCapFrac` come from `npm run fonts:ink`, which measures 'SPRINGBOROPANTHERS'
// — all letters, no digits. That is the right string for ARC TEXT, because arc text is words.
//   The weight lockup paints "45LB". It used those same two numbers to ink-centre the line on `cy`
// and to place the ruled style's two rules off it — and those rules are symmetric about `cy`, so a
// face whose digits sit at a different height than its capitals turns the whole of that error into
// an UNEVEN GAP, more above than below. Measured across the library, 12 of 132 faces are off by ≥2%
// of the font size; Lobster and Lugrasimo by ~9%, which is a 17% difference between the two gaps.
//   `ink-weight.js` is the same measurement taken on "45LB" (tools/measure-weight-ink.mjs). These two
// helpers are the ONLY thing that should read it: every weight-side caller goes through them, and
// each falls back to the words metric for a face the generator has not seen, so adding a font before
// re-running the tool degrades to the old behaviour rather than to nothing.
const weightInkOf = (font) => { const v = weightInk(font); return v === null ? fontInkOffset(font) : v; };
const weightCapOf = (font) => weightCap(font) || fontCapFrac(font);

/**
 * ⛔ AN OUTLINE MAY NOT SWALLOW THE LETTERFORM IT OUTLINES (Ryan 2026-07-30: *"if someone wants a
 * really small weight number the line thickness stays the same, which can make things look odd"*).
 *
 * `thickness` is stored in ABSOLUTE plate units, so it says nothing about the type it rings. The
 * number's painted size, meanwhile, swings enormously: the size dial takes it down to 40% of the
 * room, and the width-fitted formats (`inline-lb`, `ruled`) paint a wide face far smaller than a
 * narrow one at the SAME dial position. Measured across 1,632 product × format × font combinations
 * with the default single 2.5u ring, as a share of the number's cap-ink height:
 *
 *     median 7.4% at the default dial position, 16.1% at the bottom of the dial
 *     over 15%:  12% of combinations at default  →  55% at the bottom
 *     worst:     36.9% at default (bbhbartle / ruled / bumper)  →  82.9% at the bottom
 *
 * At 83% the ring is nearly as thick as the letter is tall. So thickness needs a ceiling expressed
 * in the type's OWN units, and this is it — the per-size sibling of `maxOutlineTotal`, which caps
 * the stack against the BAND. That one stops the ink leaving the plate; this one stops it eating
 * the glyph, and neither can do the other's job.
 *
 *   ⚠ WHY A CEILING RATHER THAN A PROPORTIONAL RULE. Scaling thickness with the dial was the first
 * design and it is strictly weaker: it needs the dial's SHARE (`sizeScale / edge`), and `sizeScale`
 * is an absolute multiplier whose `edge` moves with font, weight, slot AND the outline stack itself
 * (measured 0.45–1.35) — so recovering the share at paint time re-enters `weightSizes`, which is
 * the function computing it. A ceiling needs none of that: it reads the painted cap height, which
 * is already in hand. And it subsumes the proportional rule, because once the ceiling binds the
 * thickness IS a fixed share of the cap and therefore shrinks with it, dial and all.
 *
 *   ⚠ IT ONLY EVER REDUCES. Below the ceiling nothing moves — which is why the median design, at
 * 7.4%, renders byte-for-byte as it did. There is no floor and none is needed: the cap height is
 * positive, so the result is a positive thickness, never a hairline.
 *
 *   ⛔ MEASURED ON THE WIDEST SINGLE BAND, NOT ON THE SUM — and getting this wrong gutted a feature
 * before the baseline diff caught it. Capping the TOTAL rewrote 100% of four-ring designs at every
 * dial position, because four 2.5u rings sum to 10u and blow any cap-height ceiling on their own.
 * But a stack does not clog a letterform the way one thick ring does: `emitStyledText` paints each
 * band OUTWARD from the glyph edge, so rings two through four sit outside the letter and leave its
 * counters exactly as open as a single ring does. What fills in the hole in a 4 or an 8 is the
 * INNERMOST band's thickness against the glyph's own stroke weight, and that is what this measures.
 * A four-ring lockup is a deliberate banded badge, not an accident to be flattened.
 *   The spill that a big SUM does cause — ink running past the plate edge — already has two owners
 * (`maxOutlineTotal` against the band, and the wall clamp in `weightSizes`). This is not their job.
 *
 * The FACTOR is then applied to the whole stack, so relative band widths survive: a buyer's 4/2/1
 * stays 4/2/1, just smaller.
 */
const WEIGHT_OUTLINE_CAP_SHARE = 0.15;
/** The stored outline stack's summed thickness — the raw sum, BEFORE any ceiling. Was this same
 *  reduce written out at five sites, which is the §0b trap: one of them always drifts. */
function outlineTotal(element) {
  return (element && Array.isArray(element.outlines) ? element.outlines : [])
    .slice(0, 4)
    .reduce((a, o) => a + Math.max(0, Number(o && o.thickness) || 0), 0);
}
/** The widest single band in the stack — what the ceiling is measured against (see above). */
function outlineMaxBand(element) {
  return (element && Array.isArray(element.outlines) ? element.outlines : [])
    .slice(0, 4)
    .reduce((a, o) => Math.max(a, Math.max(0, Number(o && o.thickness) || 0)), 0);
}
/** The factor the whole stack paints at, given the cap-ink height its widest band sits against.
 *  1 when there is nothing to measure or the band already fits. */
function outlineInkScale(maxBand, capH, target = 1) {
  return capH > 0 && maxBand > 0 ? Math.min(target, (WEIGHT_OUTLINE_CAP_SHARE * capH) / maxBand) : target;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 493, 2026-11-01) — THE NUMBER'S RINGS GROW WITH THE LOGO ON A DUMBBELL / BAR / GRIP FACE.
   Ryan, UX run 3 (DB Logo, green with a white outline): *"the outlines on both on the plate match perfectly, but then
   on the dumbbell we have a thin one on the number and a thick one on the logo."*
   A text ring is stored in viewBox units, priced against a logo painting `LOGO_BOX_NOMINAL_VB` wide - the bumper's
   ring-B logo. The logo's own outline (drawn into the artwork, or added by `addOutlineDataUrl`) is a FRACTION of the
   logo, so it grows with the logo; the number's did not. A cap face paints the same logo ~4x that wide, so the two
   outlines that matched on the plate came apart by ~4x on the dumbbell.
   His call (option: keep one setting, the number copies the logo's on-screen weight - not absolute, not per-weight):
   on a cap face the number's stack is multiplied by how much bigger this face paints the logo than the plate does.
   ⚠ EVERY FACE BUT THE BUMPER. The bumper returns 1, so every approved plate (the corpus is all bumper) is
   byte-identical; the bumper's logo paints at ~the nominal by construction of that constant. The GRIP joined the same
   hour (Ryan: *"the outline on the logo on the grip plate seems thicker than the numbers"*) - its face paints DB Logo
   at 80u, 1.54x the nominal, so the same 2.5 read thin there too.
   ⚠ NEVER BELOW 1: a logo dialled small on a dumbbell keeps today's ring rather than thinning to a hairline.
   ⚠ The number's own cap-height ceiling (`WEIGHT_OUTLINE_CAP_SHARE`) still binds - this raises the TARGET that
   `outlineInkScale` is clamped against, so a small number cannot be clogged by a big logo's ring. */
function capTextInkScale(p, elements, zone, matchBox, product, weight) {
  if (product !== 'dumbbell' && product !== 'barbell' && product !== 'grip') return 1;
  const el = elements.find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!el) return 1;
  const lg = el.logo;
  const aspect = (lg.renderUrl && Number.isFinite(lg.renderAspect)) ? lg.renderAspect : resolveLogoAspect(lg);
  const d = logoDrawSize(el, aspect > 0 ? aspect : 1, zone, matchBox, null, logoCeilingFor(p, el, { product, weight }));
  if (!d || !(d.w > 0) || !(d.h > 0)) return 1;
  return Math.max(1, Math.max(d.w, d.h) / LOGO_BOX_NOMINAL_VB);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

// ── plate + ring geometry (viewBox units; 450u == 450mm plate) ───────────────
export const CENTER = 225;
export const PLATE_R = 225; // plate radius == half the viewBox
// The C/B and B/A grooves sit at 0.505 / 0.873 R, but the design lives on the
// FLAT recessed floor, which the geometry insets from each groove by the slanted
// (flat-chamfer) drop's radial run (channelSlantDeg 20°, recess ⅛″ → ≈0.0052 R/side;
// see bumper-plate.js emitSlantedStep). Ring B is that flat floor so artwork never
// rides up a slanted wall.
const RING_B_SLANT_INSET = 0.0052; // fraction of R the slanted drop eats per side
export const RING_B_INNER = (0.505 + RING_B_SLANT_INSET) * PLATE_R; // ≈114.8 — C/B flat-floor edge
export const RING_B_OUTER = (0.873 - RING_B_SLANT_INSET) * PLATE_R; // ≈195.3 — B/A flat-floor edge
export const RING_MID = (RING_B_INNER + RING_B_OUTER) / 2;
export const RING_W = RING_B_OUTER - RING_B_INNER;
export const HUB_R = 0.457 * PLATE_R; // 102.825 — zinc hub outer
export const BORE_R = 0.112 * PLATE_R; // 25.2 — collar hole
export const BOLT_CIRCLE = 0.289 * PLATE_R; // 65 — bolt circle radius
export const BOLT_HEAD_R = 0.0322 * PLATE_R; // ~7.25 — bolt head radius

// ── big-logo geometry (Ryan 2026-07-13, #7/#8) ───────────────────────────────
// The ONLY paintable surface is ring B (the hub + rings A/C never take ink). A
// "big" logo is sized so its height sits FLUSH with the paintable ring's top and
// bottom (top→bottom of ring B, no overshoot), then positioned so its inner
// (toward-center) edge reaches the far edge of the steel hub — and CLIPPED to the
// ring-B annulus. So it fills the near side + top + bottom of the ring while the
// hub + rings A/C stay bare; the weight stays in its normal slot on the far side.
// (BIG_BLEED is a tunable overshoot in viewBox units; 25.4u == 1".)
const BIG_BLEED = 0; // logo height sits FLUSH with the ring top/bottom — no overshoot (Ryan 2026-07-13)
const BIG_HUB_CROSS = 1.0; // logo's inner edge crosses this fraction of the hub's full width (1.0 = exactly the far hub edge, no further — Ryan 2026-07-13)

/** Average glyph-width-to-font-size ratio for the DOM-free auto-fit heuristic. */
export const GLYPH_WIDTH_RATIO = 0.6;

// Negative-space compensation for a size-matched logo. The intake auto-trim hugs the logo's
// BOUNDING BOX, but an irregular mark (a mascot, a diagonal wordmark) still leaves empty corners
// inside that box, so the raw box overstates how "full" the logo reads next to a solid weight
// number — the plain "45" match was the worst case (a bare single-line cap, no headroom). Paint a
// size-matched logo this fraction bigger than the pure match so its VISIBLE mass balances the
// number; still capped at the slot box so it never overflows (Ryan 2026-07-22).
const LOGO_FILL_COMPENSATION = 1.06;

// Retract insets (viewBox units; ~25.4u == 1"). Text/logo pull in from the ring
// edges so nothing kisses a groove.
const TEXT_RADIAL_INSET = 8; // radial breathing room for arced text
export const SLOT_INSET = 7; // point-slot box padding (exported so tests can reconstruct the slot annulus)
// A WIDE logo in a LEFT/RIGHT slot may reclaim most of that inset (Ryan 2026-07-25). At 3 and 9
// o'clock the logo's LONG axis is radial, so it is bounded by the ring's width rather than by the
// generous annulus fit a top/bottom slot enjoys — `slotAnnulusBox` declines those slots outright, so
// a landscape mark got no floor and a cap of the bare slot box, and printed visibly pinched. 1.14
// spends ~4.5 of the 7-unit inset per side; the far corners then sit ~193 against ring B's 195.3
// outer edge, so it comes closer to the rim without ever reaching it.
const SIDE_EDGE_MARGIN = 2; // a side logo never comes closer than this to either ring-B edge
/**
 * The widest a wide logo may be drawn at a SIDE slot, for the angle it actually sits at.
 *
 * A fixed allowance is wrong because the room varies around the ring (Ryan 2026-07-25: the mark at 3
 * and 9 o'clock is as wide as it can go, but at 1:30 / 4:30 / 7:30 / 10:30 it could be wider still).
 * The box is axis-aligned while the ring is not, so at 45° its far corner sits closer to the plate's
 * diagonal and further from the rim — measurably more headroom than on-axis.
 *
 * With c = R|cos θ|, s = R|sin θ| and half-width u, the far corner is (c + u, s + u/aspect), so
 *   (c + u)² + (s + u/a)² = L²   ⇒   u²(1 + 1/a²) + 2u(c + s/a) + (R² − L²) = 0
 * one quadratic against the OUTER limit and the same shape against the INNER one; take whichever
 * binds. At θ = 0 it returns ~76 units (what the flat 1.14 allowance gave); at 45° it returns ~85.
 */
function sideWideMaxW(aspect, zone, thetaDeg) {
  const R = zone.anchorR;
  const t = (thetaDeg * Math.PI) / 180;
  const c = Math.abs(R * Math.cos(t));
  const sn = Math.abs(R * Math.sin(t));
  const declared = zone.band || null; // a cap face states its band; ring faces reconstruct it below
  // ⚠ THE BAND COMES FROM THE ZONE, NOT FROM THE BUMPER'S RING. These two lines read `RING_B_OUTER`
  // and `RING_B_INNER` outright until 2026-07-30, which is correct for exactly one product.
  //   On a grip plate it was catastrophic and silent: its band sits at 0.19R–0.86R while ring B is
  // 0.51R–0.87R, so `near >= Li` failed at a half-width of about ONE UNIT and every wide logo at a
  // lower gap painted 1.8u across — present in the SVG, invisible on the plate. Nothing caught it
  // because no grip layout put a logo at `left`/`right` until Grip Flip did (Ryan: "the bottom left
  // and bottom right on this design need logos. It's empty there now").
  //   Identical arithmetic for the bumper — its `anchorR` IS the ring mid-line and its
  // `boxSide/2 + SLOT_INSET` IS half the ring width, so these resolve to exactly the two constants
  // they replace. Same shape as `ringBandMaxW` below, which had it right all along.
  const half = zone.boxSide / 2 + SLOT_INSET;
  const Lo = (declared ? declared.outer : R + half) - SIDE_EDGE_MARGIN;
  const Li = declared
    ? Math.max(0, declared.inner && declared.inner + SIDE_EDGE_MARGIN)
    : Math.max(0, R - half + SIDE_EDGE_MARGIN);
  // Distances from the plate centre to the box's FAR corner and to its NEAREST point. The nearest
  // point is a clamp, not a corner: once the half-width exceeds `c` the box straddles the axis and
  // its closest approach is the middle of an edge. Getting that wrong made the closed form return
  // zero at 90° and dip below the on-axis value around 22°.
  const fits = (u) => {
    const far = Math.hypot(c + u, sn + u / aspect);
    const near = Math.hypot(Math.max(0, c - u), Math.max(0, sn - u / aspect));
    return far <= Lo && near >= Li;
  };
  if (!fits(0.5)) return 0;
  let lo = 0.5;
  // The same hub assumption `ringBandMaxW` carries, and the same fix: on a face with no hub the
  // anchor radius is not a ceiling at all. It capped a side-slot logo on the cap at exactly twice
  // its own anchor — 137u where the disc had room for 224 — so "logo left, weight right" painted its
  // mark two thirds the size of the identical mark on "logo above, weight below". Ring faces keep
  // their historical interval so their bisection rounds identically; see the note there.
  let hi = zone.hardWall === true ? Lo : R;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid; else hi = mid;
  }
  return 2 * lo;
}
// …and how much of that room it should actually take. The top/bottom floor (SLOT_TANGENTIAL_FILL,
// 0.75) is deliberately gentle because a tangential mark has the whole annulus fit behind it; a side
// mark has only the ring's width, so it needs to claim most of it or the sibling-weight size-match
// simply hands the room back. Measured: 0.75 rendered 57 units wide, 0.90 renders ~68.
const SIDE_WIDE_FILL = 0.9;

/**
 * The widest an axis-aligned box of `aspect` (w/h), centred on a slot's anchor at `thetaDeg`, can be
 * drawn and still sit inside the zone's paintable BAND.
 *
 * Same closed-form-by-bisection test `sideWideMaxW` runs — a box in a circular band is bounded by its
 * FAR CORNER against the outer edge and its NEAREST POINT against the inner one — with two
 * differences that matter:
 *
 *   • ZONE-GENERIC. `sideWideMaxW` hard-codes the BUMPER's `RING_B_INNER`/`OUTER` while taking `R`
 *     from the zone, so it silently measures a grip plate against a bumper's ring. Here the band is
 *     reconstructed FROM the zone: every zone defines `anchorR` as its band's midpoint and `boxSide`
 *     as that band less `2 × SLOT_INSET` (true of ZONE_PLATE and of gripZone alike), so adding the
 *     padding back recovers the real edges. For the bumper the two are arithmetically identical
 *     (`anchorR + boxSide/2 + SLOT_INSET === RING_B_OUTER`), which is why this can be introduced
 *     without moving a single existing plate.
 *   • ANY ORIENTATION. The formula never cared which slot it was: with `c = R|cosθ|`, `s = R|sinθ|`
 *     and half-width `u`, the far corner is `(c + u, s + u/aspect)` whether that `u` runs radially
 *     (left/right) or tangentially (top/bottom). `sideWideMaxW` is only *called* for side slots
 *     because that is the case it was written for.
 *
 * @returns {number} the max width in plate units, or 0 when nothing of this aspect fits
 */
function ringBandMaxW(aspect, zone, thetaDeg) {
  if (!(aspect > 0) || !zone) return 0;
  const R = zone.anchorR;
  const b = zoneBand(zone); // declared on a cap face; reconstructed on the ring faces, as before
  const Lo = b.outer - SIDE_EDGE_MARGIN;
  const Li = b.inner > 0 ? b.inner + SIDE_EDGE_MARGIN : 0;
  const t = (thetaDeg * Math.PI) / 180;
  const c = Math.abs(R * Math.cos(t));
  const sn = Math.abs(R * Math.sin(t));
  const fits = (u) => {
    const far = Math.hypot(c + u, sn + u / aspect);
    // A clamp, not a corner: once the half-extent passes the anchor's own offset the box straddles
    // the axis and its closest approach is the middle of an edge, not a vertex.
    const near = Math.hypot(Math.max(0, c - u), Math.max(0, sn - u / aspect));
    return far <= Lo && near >= Li;
  };
  if (!fits(0.5)) return 0;
  let lo = 0.5;
  // ⛔ `hi = R` IS A HUB ASSUMPTION — "no box can be wider than the anchor radius and still clear the
  // hub" — and it is catastrophic on a face that has no hub. At the cap's `center` slot the anchor
  // radius is ZERO, so the interval starts empty, the bisection collapses toward nothing, and the
  // function reports a wall of 0 for a slot with the entire disc available: it painted the cap's
  // weight numbers at a fifth of their size. A disc's ceiling is its outer edge.
  //   ⚠ AND THE RING FACES KEEP THEIR OWN CEILING, deliberately. Widening the interval there changes
  // no answer — an inner edge is real on a ring, so a half-width past the anchor already fails
  // `fits` — but it changes the bisection's ROUNDING, and the standing baseline duly caught 28
  // bumper renders moving by 6.5e-11 of a font size. Invisible, and still not worth spending: a
  // baseline whose zero-diff claim has an asterisk on it is a baseline nobody trusts next time.
  let hi = zone.hardWall === true ? b.outer : R;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid; else hi = mid;
  }
  return 2 * lo;
}

/**
 * The widest a PLAIN logo may be drawn so that its INK (the artwork's convex hull, measured once in
 * logo-ink-hull.js) stays inside the zone's paintable band at the anchor it actually sits on — the
 * same band and the same `SIDE_EDGE_MARGIN` `ringBandMaxW` holds a box to. See `LOGO_SCALE_REACH`.
 *
 * Returns 0 — "no ink answer, use the box" — whenever the hull cannot be trusted or the face places
 * a mark by rules this does not model: no hull, a hull measured at a different aspect (an added
 * outline ring pads the drawn image), a badge / full-face mark, or a cap face (hard walls, rim
 * hangs, paired drift). Every one of those keeps its old stop, so this can only ever ADD room on a
 * ring face, never move a mark that sits where it did.
 *
 * ⚠ The hull is a superset of the ink, so both tests are safe: the farthest ink is a hull vertex,
 * and the hull's nearest point is never further out than the nearest ink.
 * ⚠ The placement that follows sizing (`cornerBalanceShift`) slides a mark INWARD, and on a
 * drifted (diagonal) mark that can carry its inner corner into the hub — so `fits` tests the hull at
 * the PLACED centre, after that slide, not at the anchor.
 */
const _inkReachMemo = new WeakMap();
function logoInkReachW(element, aspect, zone, thetaDeg) {
  const hull = element && element.logo && element.logo.inkHull;
  if (!hull || !Array.isArray(hull.pts) || hull.pts.length < 3) return 0;
  if (!(aspect > 0) || !(Math.abs((hull.aspect || 0) - aspect) < 1e-3)) return 0;
  if (element.big === true || element.bigBox === true) return 0;
  if (!zone || zone.hardWall === true || zone.maxW > 0 || zone.hangEdge > 0 || zone.sideBalance === 'paired') return 0;
  const R = zone.anchorR;
  if (!(R > 0) || !Number.isFinite(thetaDeg)) return 0;
  const b = zoneBand(zone);
  const Lo = b.outer - SIDE_EDGE_MARGIN;
  const Li = b.inner > 0 ? b.inner + SIDE_EDGE_MARGIN : 0;
  const flip = element.upsideDown === true ? -1 : 1; // rotated 180° about its own centre
  const key = `${aspect}|${thetaDeg}|${R}|${Lo}|${Li}|${flip}|${zone.boxSide}`;
  let memo = _inkReachMemo.get(hull);
  if (memo && memo.has(key)) return memo.get(key);
  const t = (thetaDeg * Math.PI) / 180;
  const ax = R * Math.cos(t);
  const ay = R * Math.sin(t);
  const pts = hull.pts;
  // The inward slide `drawLogo` applies after sizing (`cornerBalanceShift`) fires where the slot's
  // radial runs more vertical than horizontal — measured at the PLACED centre, or a drifted mark is
  // tested where it will not be (a first cut did that and put RW-1444-A's rams snout into the hub).
  const slides = Math.abs(Math.sin(t)) > Math.abs(Math.cos(t));
  const fits = (w) => {
    const h = w / aspect;
    let cx = ax;
    let cy = ay;
    if (slides) {
      const d = cornerBalanceShift(R, w, h, zone.boxSide);
      if (d < -0.01) { cx = ax * (R + d) / R; cy = ay * (R + d) / R; }
    }
    const P = pts.map(([u, v]) => [cx + flip * u * w, cy + flip * v * h]);
    for (const [x, y] of P) if (Math.hypot(x, y) > Lo) return false;
    if (!(Li > 0)) return true;
    // Does the hull cover the plate centre? Then it covers the hub.
    let inside = false;
    for (let i = 0, j = P.length - 1; i < P.length; j = i++) {
      const [xi, yi] = P[i];
      const [xj, yj] = P[j];
      if ((yi > 0) !== (yj > 0) && 0 < ((xj - xi) * (0 - yi)) / (yj - yi) + xi) inside = !inside;
    }
    if (inside) return false;
    for (let i = 0, j = P.length - 1; i < P.length; j = i++) {
      const [x1, y1] = P[j];
      const [x2, y2] = P[i];
      const dx = x2 - x1;
      const dy = y2 - y1;
      const L2 = dx * dx + dy * dy;
      const k = L2 > 0 ? Math.max(0, Math.min(1, -(x1 * dx + y1 * dy) / L2)) : 0;
      if (Math.hypot(x1 + k * dx, y1 + k * dy) < Li) return false;
    }
    return true;
  };
  let out = 0;
  if (fits(1)) {
    let lo = 1;
    let hi = 2 * Lo;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) lo = mid; else hi = mid;
    }
    out = lo;
  }
  if (!memo) { memo = new Map(); _inkReachMemo.set(hull, memo); }
  memo.set(key, out);
  return out;
}

// ── zones: WHERE a slot lands, per product face ──────────────────────────────
// Every product's brand face is planar-UV'd across its own full DIAMETER
// (`planarUV = x/(2R)+0.5` in each geometry module), so this 450u viewBox always
// maps to the whole face and every radius below is a RATIO of that product's own
// R. That's what lets one engine paint a 450mm bumper and a 10.63" grip plate.
//
// A zone says three things: where a slot's anchor sits (`anchorR`), how big a
// point element may be there (`boxSide`), and which screen angle each slot names
// (`angles`). The bumper's numbers are unchanged — they're just named now.
/** Screen degrees (0° = 3 o'clock, +clockwise). */
const PLATE_ANGLES = Object.freeze({ top: 270, bottom: 90, left: 180, right: 0 });

/**
 * The bumper's ring-B zone.
 *
 * ⚠ IT WAS THE DUMBBELL'S AND BARBELL'S TOO UNTIL 2026-07-30, and that was wrong in a way nothing
 * on screen announced. Their brand face is a SOLID DISC with no hub and no cutouts — the geometry
 * planar-UVs the whole 450u viewBox onto the flat art canvas — so every slot was being anchored on a
 * ring at 0.69R with a 68.8u box, on a face with three times that room and nothing in the middle.
 * See `capZone`.
 *
 * `band` states the paintable annulus outright instead of leaving every consumer to reconstruct it
 * from `anchorR ± (boxSide/2 + SLOT_INSET)`. For THIS zone the two are identical by construction —
 * `RING_MID + (RING_W − 2·SLOT_INSET)/2 + SLOT_INSET === RING_B_OUTER` exactly, which a test asserts
 * — so declaring it moves nothing. It exists because the cap's band cannot be reconstructed that
 * way: its box is a taste size, not its band. The grip's is deliberately left un-declared; see
 * `zoneBand`.
 */
const ZONE_PLATE = Object.freeze({
  anchorR: RING_MID,
  boxSide: RING_W - 2 * SLOT_INSET, // ~68.8
  angles: PLATE_ANGLES,
  band: Object.freeze({ inner: RING_B_INNER, outer: RING_B_OUTER }),
});

/**
 * The paintable band a shape must fit inside, for the zone (or slot VIEW) it is given.
 *
 * Declared when the zone says so, reconstructed otherwise. The reconstruction — back the slot
 * padding out of the box and hang it either side of the anchor — is exact for a zone whose box IS
 * its band, which is the bumper and was every zone until 2026-07-30.
 *
 * ⚠ THE GRIP PLATE DELIBERATELY DOES NOT DECLARE ONE, and that is not an oversight. Its box is
 * two-thirds of its band divided by the size-open factor, so the reconstruction recovers a
 * noticeably TIGHTER band than the plate really has (59–177 against a true 42–194 on a 45 lb).
 * Every grip size Ryan has signed off — the 63.6u Grip Flip logo, the re-pegged 65% dial — was
 * measured against that tighter figure. Declaring the true band would quietly grow all of them.
 * When the conservative answer is the approved answer, leave it alone.
 */
function zoneBand(zone) {
  const z = zone || ZONE_PLATE;
  if (z.band) return z.band;
  const half = z.boxSide / 2 + SLOT_INSET;
  return { inner: Math.max(0, z.anchorR - half), outer: z.anchorR + half };
}

// ── grip-plate zone ──────────────────────────────────────────────────────────
// MIRRORED from geometry/grip-plate.js, which is the SOURCE OF TRUTH — the same
// arrangement RING_B_INNER/OUTER already have with bumper-plate.js's 0.505/0.873.
// Mirrored rather than imported so the 2D path (statically imported by main.js so
// the flat SVG paints first) doesn't drag a 61KB mesh generator in ahead of first
// paint. `test/render-engine.smoke.mjs` imports BOTH and asserts these agree, so
// the duplication cannot silently drift — if that test fails, grip-plate.js won.
// Exported ONLY so the smoke test can hold them against grip-plate.js's originals
// — that test is the entire reason this duplication is allowed to exist.
export const GRIP_DIAMETER_IN_LB = Object.freeze({ 2.5: 7.09, 5: 8.66, 10: 10.63, 25: 12.99, 35: 15.35, 45: 17.72 });

/* ── THE CUTOUT-FREE GRIP PLATES (2.5 / 5 lb) ─────────────────────────────────────────────
 * The inch-side mirror of geometry/grip-plate.js's SMALL_GRIP_* block — same physical
 * numbers, the units this file works in. Keep the two in step; the geometry file carries the
 * measurement record and the reasoning.
 *
 * ⛔ ABSOLUTE INCHES, NOT RATIOS OF R. The 2.5 and the 5 were measured independently and
 * agreed on these DISTANCES to within 0.7%; as fractions of their radii the same two facts
 * disagree by a tenth of a radius. A ratio fitted to one is wrong on the other.
 */
export const SMALL_GRIP_MAX_DIA_IN = 8.66; // the 5 lb — everything at or under is cutout-free
/* The steel through the middle, inch-side. Same parts on EVERY plate in the family, which is why
   they are absolute here and become a different fraction of R on each denomination — see
   `drawSmallGripChrome`. Mirrors BORE_RADIUS 0.0252 m / COLLAR_RADIUS 0.03246 m. */
export const BORE_RADIUS_IN = 0.99212598425; // 0.0252 m
export const COLLAR_RADIUS_IN = 1.27795275590; // 0.03246 m
export const SMALL_GRIP_HUB_IN = 1.5275590551181102; // SMALL_GRIP_HUB_RADIUS 0.0388 m — art starts outside the hub ring
export const SMALL_GRIP_RIM_BAND_IN = 0.4409448818897638; // SMALL_GRIP_RIM_BAND 0.0112 m — no-art band in from the rim
export const SMALL_GRIP_INSET_IN = 0.125; // SMALL_GRIP_SAFE_INSET — 1/8" (tried at 1/16"; see the geometry file)
export const GRIP_COLLAR_IN = 1.2779527559055118; // COLLAR_RADIUS 0.03246 m
export const GRIP_SAFE_INSET_IN = 0.375; // GRIP_SAFE_INSET 0.009525 m — 3/8"
export const GRIP_OUTER_RING_RATIO = 0.906; // artwork never enters the outermost ring
export const GRIP_CUTOUT_HALF_DEG = 26.238; // CUTOUT_HALF_DEG — the cutout table's outermost sample

/**
 * How much of a grip gap's radial band the RIM ARC takes, when that gap carries arc text
 * (Ryan 2026-07-30, layouts #3 and #4).
 *
 * The grip plate is the first face in the app where an arc word and a point element share ONE slot:
 * the bumper keeps them apart by construction (arcs live at top/bottom, logo and number at left/
 * right), but a grip plate has only three places to put anything, so "text arched around the rim
 * with the logo below it" means both pieces in the same gap at different radii.
 *
 * A third to the lettering, two thirds to the mark below it. Deliberately generous to the mark: the
 * arc is one line of caps and reads fine at a third of a band that is already ~0.68R deep, while the
 * logo is the piece a buyer actually looks at. The two Tri- layouts are the only consumers.
 */
const GRIP_ARC_BAND_SHARE = 1 / 3;

/** Cap height as a fraction of font size, for sizing decisions taken BEFORE a typeface is known —
 *  the zone is built per denomination, not per font. Matches `fontCapFrac`'s own fallback. */
const CAP_APPROX = 0.72;
/** A default outline stack's outward growth, for the same reason. One ring at the 2.5 the element
 *  model seeds; over-reserving is the safe direction when the alternative is ink past a print
 *  margin. */
const ARC_GROWTH_APPROX = 2.5;
/** A representative name length per gap, for the same before-the-font reason. Between a one-word
 *  Tri-Word gap (~7) and a whole repeated line on Tri-Arch (~19). */
const NOMINAL_ARC_CHARS = 14;
/** An average face's glyph-advance ratio — `fontWidthRatio`'s own fallback. */
const NOMINAL_WIDTH_RATIO = 0.6;

/**
 * The grip plate's zone, for a denomination.
 *
 * WHY PER-WEIGHT: the 3/8" keep-clear is an absolute PRINT MARGIN, not a ratio, so
 * it eats a different fraction of a small plate than a big one (0.042R at 45 lb →
 * 0.071R at 10 lb) — and the steel collar it clears is absolute too. The bumper
 * needs none of this because it's one size at every weight; the grip plate runs
 * 10.63"–17.72". Getting this wrong doesn't misplace art by a hair, it prints the
 * 10 lb over its own collar.
 *
 * THE THREE SLOTS ARE THE THREE GAPS between the hand cutouts. Ryan's reference
 * (Pursuit Fitness, 2026-07-15): "grip plates pretty much always get a logo at the
 * top and 2 weight numbers down below" — logo at 12 o'clock, numbers at ~7 and ~5.
 * Those are exactly the solid arcs between cutouts: grip-plate.js's cutouts are
 * centred at 30/150/270 (math, +Y up), so the gaps fall at 90/210/330 — screen 270
 * (top), 150 (lower-left), 30 (lower-right). The slots aren't invented; they're
 * where the holes aren't.
 *
 * NO `bottom`: screen 90 is a cutout centre (the 6 o'clock hand hole). A grip
 * preset never authors one — see pointAngle for what happens if something does.
 *
 * `boxSide` = the radial room between the collar and the ring limit, less the
 * usual slot padding — the SAME rule the bumper uses (its box is its ring width).
 * The grip plate just has about twice the room, so its elements come out bigger.
 * ⏳ Ryan is eyeballing exactly this (2026-07-15): the reference's own art is
 * conservative — its logo reads ~101u and its "45" ~33u against the ~152u of room
 * here. If he calls it big, this is the number, and the presets' `sizeScale` is
 * the finer dial.
 *
 * ── ARC-CARRYING SLOTS DIVIDE THEIR BAND (Ryan 2026-07-30) ───────────────────
 * `arcSlots` is the set of slots that carry a RENDERING arc word in this design.
 * A slot in that set gets its band split into an outer lettering lane and a
 * reduced inner field for the logo/number below it; a slot NOT in it — and every
 * slot when the set is empty — keeps the full band it has always had, and the
 * returned object is then byte-for-byte what this function returned before rim
 * arcs existed. That is what stops the two arc-free grip layouts regressing, and
 * it is why the split is expressed as an extra `slots` map rather than as new
 * numbers on the zone itself.
 */
/* ── KEEPING A MARK READABLE ON THE SMALL PLATES (Ryan 2026-07-31) ───────────────────────────
   *"As the grip plates go down in weight, they get smaller. But we need to be careful about the
   weight numbers and logos getting smaller by the same % … otherwise we risk them being too small
   to see… if the space permits it, the weight number size and logo size should decrease at a
   smaller rate than the diameter of the weight itself."*

   ⛔ AND THE FIRST ATTEMPT COMPENSATED THE WRONG QUANTITY. `artwork-scale.js` multiplied each
   element's `sizeScale`, which is documented as "0.4..1 fraction of the slot's AUTO-FIT MAX" — a
   SHARE, clamped at 1. Weight elements seed at 0.9, so the ceiling was 1/0.9 = +11.1%, which is
   exactly what every denomination hit; logos carry no `sizeScale` at all (undefined → 1) and moved
   0%. Ryan: *"looking at an old generated PDF and a new one, and not seeing any difference."*
   Raising a share of a box that already shrank cannot recover anything. THE BOX has to grow.

   ⚠ THE GRIP IS SQUEEZED TWICE, which is why it needed this most. `gripZone` builds its band from
   the collar (1.28") and a 3/8" inset — FIXED INCHES, a bigger fraction of a small plate — so the
   printable band collapses FASTER than the plate: 46.5% of the 45's at 10 lb against 60% of the
   diameter. A 2.5" logo authored on the 45 was printing at 1.16".

   The rule is Ryan's: painted ∝ room^0.5, one exponent for the number and the logo alike so the
   ratio he approved on the 45 survives onto every other plate. Room is measured in INCHES, and the
   reference is the denomination the designer opens at — so the factor is exactly 1 on the live
   stage and the on-screen tool cannot move. */
export const GRIP_REFERENCE_LB = 45;
export const ARTWORK_ROOM_EXPONENT = 0.5;

/**
 * ── THE SAME RULE, FOR THE DUMBBELL AND THE FIXED BARBELL (Ryan 2026-08-02) ────────────────────
 * *"on the really small headed fixed barbells and dumbbells, the numbers seem too small… I know the
 * heads are smaller so the logo and weight numbers need to get smaller too, but they seem too small
 * relative to the head height, which would result in them becoming very hard to read."*
 *
 * `capZone` took no weight at all, and said so on purpose: *"every cap head is the same face at
 * every denomination — the geometry scales with it."* True in SVG space, and that is exactly the
 * problem — **a constant share of a shrinking head is `k = 1`, the pure linear shrink** this whole
 * mechanism exists to stop. Measured before the fix: the number painted at font-size 53.03 on the
 * 50 lb dumbbell and 53.03 on the 5 lb, identical units on a face 57% the size.
 *
 * The heads run 7.99″ at the top of the chart down to **4.37″**, a 45% loss — worse than the grip's
 * 40%, from a face less than half the size to start with. And the number is the smallest thing on
 * it: `CAP_LOGO_BOX`'s measurement note puts the mark at 0.37–0.42 of the face and the number at
 * **0.09**, which on a 4.37″ head is well under half an inch of ink.
 *
 * ── WHY THE CAP IS SIMPLER THAN THE GRIP ───────────────────────────────────────────────────────
 * It is not squeezed twice. `CAP_ART_FRAC`'s 4% is a PROPORTIONAL printer's margin, not the grip's
 * fixed 3/8″ that collapses the band faster than the plate collapses. So the room ratio simply IS
 * the head ratio — one clean term, no band to reconstruct.
 *
 * ⚠ A SEPARATE EXPONENT FROM THE GRIP'S, because the two start from very different places: the
 * grip's mark opens at ~0.31 R of its band, the cap's NUMBER at 0.09 of its face. If 0.5 turns out
 * not to be enough here, this constant is the whole fix rather than a re-plan. Starts at 0.5 —
 * Ryan's own pick, and one exponent for the number AND the mark so the ~5:1 ratio he approved on
 * the reference head survives onto every other rung.
 */
export const CAP_ROOM_EXPONENT = 0.5;

/**
 * The head DIAMETER in inches — the cap's equivalent of `gripBandInches`.
 *
 * ⚠ IMPORTED FROM `geometry/`, WHERE THE GRIP'S NUMBERS ARE MIRRORED, AND THE DIFFERENCE IS
 * DELIBERATE. The grip mirror exists so this module doesn't drag a mesh generator onto the
 * first-paint path — but `products.js` (which `main.js` imports directly) already imports both of
 * these functions, so the bundle cost is paid either way and there is nothing to save. Mirroring
 * them here would instead duplicate the barbell's shaft-steel subtraction, which is a physics
 * calculation, not a table — CLAUDE.md §0b's "when two places need the same answer, one of them
 * will be wrong."
 */
export function capHeadDiaIn(model, weight) {
  const w = Number(weight);
  if (!Number.isFinite(w) || w <= 0) return 0;
  // 'lb' explicitly, not by default: `weight` here is always pounds (see `resolveZone`), and naming
  // it at the boundary is what stops a future caller assuming the geometry follows the display unit.
  const dims = model === 'barbell' ? barbellHeadDims(w, 'lb') : dumbbellHeadDims(w, 'lb');
  return dims && dims[0] > 0 ? dims[0] * 2 * 39.3701 : 0; // dims[0] is a RADIUS, in metres
}

/** The printable band, in INCHES, for a grip plate of this outer diameter. The same quantity
 *  `gripZone` builds in SVG units — here in the units the physical result is judged in. */
export function gripBandInches(dia) {
  const r = dia / 2;
  if (dia <= SMALL_GRIP_MAX_DIA_IN) return Math.max(0, r - SMALL_GRIP_RIM_BAND_IN - 2 * SMALL_GRIP_INSET_IN - SMALL_GRIP_HUB_IN);
  const inner = (GRIP_COLLAR_IN + GRIP_SAFE_INSET_IN) / r;
  const outer = GRIP_OUTER_RING_RATIO - GRIP_SAFE_INSET_IN / r;
  return Math.max(0, (outer - inner) * r);
}

/** How much bigger the mark box must be than the linear rule would make it. Never below 1 —
 *  this exists to stop marks getting too small, and shrinking one is not a job it may do. */
export function roomCompensation(roomTarget, roomRef, exponent = ARTWORK_ROOM_EXPONENT) {
  if (!(roomTarget > 0) || !(roomRef > 0)) return 1;
  const ratio = roomTarget / roomRef;
  if (!(ratio > 0) || ratio >= 1) return 1;   // same plate, or a BIGGER one — nothing to rescue
  return ratio ** (exponent - 1);             // painted ∝ room^k, painted-today ∝ room^1
}

/**
 * The reference denomination for a model — the one the designer OPENS at.
 *
 * ⛔ THIS IS WHAT MAKES "WEIGHT UNKNOWN" MEAN "FACTOR EXACTLY 1", and it is the trap this change
 * had to disarm. Several `resolveZone` callers fall back to a literal `45` when no weight reaches
 * them (`opts.weight ?? p.weightValue ?? 45`). That was harmless for as long as `capZone` ignored
 * the weight entirely — the moment it stops ignoring it, a 45 against a 50 lb dumbbell reference
 * silently paints a COMPENSATED face on every path that doesn't carry a denomination: the
 * design-sheet PDF, the OG image and the four preview harnesses all reach the engine that way.
 * Reading the reference from `products.js` rather than restating it also means a change to the
 * sample weight can never leave the on-screen tool sitting at a factor other than 1.
 */
export function referenceWeightFor(model) {
  const row = DEFAULT_WEIGHT_BY_CATEGORY[model] || DEFAULT_WEIGHT_BY_CATEGORY.bumper;
  return row.lb; // POUNDS, always — the display unit never reaches geometry. See `resolveZone`.
}

/* THE CEILING — Ryan's *"if the space permits it"*, and the reason it means something.
   The mark is handed two thirds of the band today (`GRIP_ARC_BAND_SHARE` reserves the outer third
   for the rim lettering lane). The type only occupies the OUTER part of that lane, so there is
   real room inboard of it — this is how much of the band a compensated mark may claim before it
   would crowd the lettering. A constant rather than derived geometry ON PURPOSE: the lettering
   depth is computed further down, after the early return, and hoisting forty lines of tuned code
   to read it would risk far more than it buys. `test/ui-logic.smoke.mjs` asserts the mark's box
   never reaches the lettering field on ANY layout × denomination, which is what makes a constant
   safe here rather than a guess. */
const GRIP_MARK_MAX_BAND_SHARE = 0.86;

function gripZone(weight, arcSlots) {
  const table = GRIP_DIAMETER_IN_LB;
  let dia = table[weight];
  if (dia == null) {
    // Nearest charted plate, mirroring gripPlateDims' own fallback — an uncharted
    // denomination resolves to a real plate rather than NaN-ing the whole render.
    const keys = Object.keys(table).map(Number);
    const nearest = keys.reduce((a, b) => (Math.abs(b - weight) < Math.abs(a - weight) ? b : a), keys[0]);
    dia = table[nearest];
  }
  const rIn = dia / 2;

  /* ── THE CUTOUT-FREE PLATES ARE A BUMPER-SHAPED ZONE ──────────────────────────────────
   *
   * Ryan 2026-07-31, on the 2.5 and 5: *"there's an outer edge which doesn't get art, and
   * inner ring which doesn't get art, then the space inbetween gets the art. a lot like the
   * training bumper in that regard."* That is not just a description — it is the
   * architecture. With no hand holes the face is a PLAIN ANNULUS, which is the shape
   * ZONE_PLATE has always modelled, and it is why four of the six layouts Ryan wants here
   * are bumper presets.
   *
   * So this returns a ZONE_PLATE-shaped zone, and specifically:
   *
   * • PLATE_ANGLES, not GRIP_ANGLES. The grip's three slots (top 270 / left 150 / right 30)
   *   exist to sit in the GAPS BETWEEN HAND CUTOUTS. With no cutouts there are no gaps to
   *   sit in, and Four Corners needs a `bottom` that GRIP_ANGLES does not have.
   *   ⚠ Visible consequence: on these two, Classic Grip's weight numerals sit at dead
   *   left/right rather than lower-left/lower-right. Symmetric here, and consistent with
   *   the other five layouts on the same plate.
   *
   * • The box is the BUMPER's rule (band minus slot padding), not the grip's two-thirds.
   *   The grip reserves its outer third for the rim lettering lane — and these plates never
   *   carry text (Ryan: *"no text in any of the layouts"*), so reserving it would spend a
   *   third of a 33.7 mm band on a lane that can never be used.
   *
   * • It DECLARES its band, where the cutout grip deliberately does not. That refusal
   *   exists to preserve sizes Ryan has already signed off against `zoneBand`'s tighter
   *   reconstruction; nothing has been approved on these two yet, and the band is narrow
   *   enough already without understating it.
   */
  if (dia <= SMALL_GRIP_MAX_DIA_IN) {
    const rInner = ((SMALL_GRIP_HUB_IN + SMALL_GRIP_INSET_IN) / rIn) * PLATE_R;
    const rOuter = ((rIn - SMALL_GRIP_RIM_BAND_IN - SMALL_GRIP_INSET_IN) / rIn) * PLATE_R;
    const smBand = rOuter - rInner;
    /* ── THE MARK GETS THE BAND, NOT THE BAND LESS TWO PADDINGS (Ryan 2026-08-02) ──────────────
     * `SLOT_INSET` is a flat 7 units of RADIAL padding either side of a point slot. It earns that
     * on a ring where slots crowd each other, and it earns nothing here: these plates place at most
     * four pieces at 90° apart (`PLATE_ANGLES`), so the clearance that matters between neighbours is
     * ANGULAR. Radially it was simply duplicating the safe inset already subtracted above — and on
     * the 2.5 lb that duplicate cost 14 of an 84-unit band, 17% of the mark, to protect nothing.
     *   3 rather than 0 so the box still has a hair of daylight off the hub ring and the rim band
     * once a thick outline stack grows the ink outward. */
    const smInset = 5;
    /* Compensated exactly as the cutout plates are, and clamped to the band — see `roomCompensation`.
     * ⚠ IT WILL NOT MOVE THE BOX, AND THAT IS THE POINT OF MEASURING RATHER THAN ASSUMING. On these
     * two the mark's box already IS the whole band, so the clamp binds the instant the factor rises
     * above 1 and `room^0.5` has nothing to grow into. It is wired anyway so the ladder is ONE rule
     * from 45 lb down to 2.5 — a second, quieter rule on the last two rungs is how this file ends up
     * with a landmine — but the size on these plates comes from the band itself (the inset above,
     * `SMALL_GRIP_SAFE_INSET`, and the tangential room below), not from the exponent. */
    const smRaw = Math.max(0, smBand - 2 * smInset);
    const smBox = Math.min(
      smRaw * roomCompensation(gripBandInches(dia), gripBandInches(table[GRIP_REFERENCE_LB])),
      smRaw,
    );
    return {
      anchorR: (rInner + rOuter) / 2,
      boxSide: smBox,
      angles: PLATE_ANGLES,
      band: Object.freeze({ inner: rInner, outer: rOuter }),
      /* ── THE TANGENTIAL ROOM, WHICH NOBODY HAD CLAIMED (Ryan 2026-08-02) ────────────────────
       * Without this the box is a SQUARE of the band's depth, so a mark may only ever be as wide as
       * the band is deep — while each of these four slots has roughly 150 units of usable arc at
       * `anchorR ≈ 147` against a ~70-unit box. More than twice the room, unspent.
       *   It matters most on exactly the plates Ryan is complaining about, because the number here
       * is **"2.5"** — three glyphs and a decimal point, the widest lockup on the whole ladder,
       * sitting on the narrowest band. Same for any wordmark. 1.4 is the cutout grip's own value
       * (`base.inlineBoost` below); the cap's is 2, but the cap is an open disc and this is not.
       *   Safe by construction: `ringBandMaxW` / `sideWideMaxW` solve against the zone's DECLARED
       * band, which this branch declares, so the widened box is still bound by real geometry rather
       * than by the boost. See CLAUDE.md §1 for what happens when they are not. */
      inlineBoost: 1.4,
    };
  }

  const innerRatio = (GRIP_COLLAR_IN + GRIP_SAFE_INSET_IN) / rIn; // clears the collar
  const outerRatio = GRIP_OUTER_RING_RATIO - GRIP_SAFE_INSET_IN / rIn; // stops short of the ring
  const band = (outerRatio - innerRatio) * PLATE_R;
  const angles = GRIP_ANGLES;
  const rOuter = outerRatio * PLATE_R;
  const rInner = innerRatio * PLATE_R;
  const arcBand = band * GRIP_ARC_BAND_SHARE;
  const innerOuter = rOuter - arcBand;
  // ── ONE MARK SIZE FOR THE WHOLE FACE, WHATEVER ELSE IS ON IT (Ryan 2026-07-30) ────────────────
  // *"Classic grip, the logo and numbers are all about 30% too big for the plate. The size should be
  // more similar to the sizes used on the tri-arch designs, but more centered."*
  //
  // The arc-free layouts were handing their pieces the ENTIRE band — a third more than the Tri-
  // layouts give, which is exactly the 30% he read off the screen. So the box is now the same on
  // every grip layout, and only the ANCHOR moves: centred in the whole band when nothing else is in
  // the gap, pushed inboard when a rim word is using the outer third. That is his sentence
  // translated directly — same size, differently placed — and it means a buyer hopping between
  // Classic Grip and Tri-Arch sees their logo stay put rather than jump a third bigger.
  // Divided by the open factor, which is the other half of the 65%-not-75% re-peg — see
  // GRIP_SIZE_OPEN_FACTOR. The room the dial is a share OF grows; the share it opens at shrinks by
  // the same amount; the painted mark does not move. At a full 100% dial the mark still clears the
  // hub below it and the lettering above it, which is what made the headroom safe to hand over.
  const rawBox = (band * (1 - GRIP_ARC_BAND_SHARE) - 2 * SLOT_INSET) / GRIP_SIZE_OPEN_FACTOR;
  /* Compensated for the small plates, and clamped to what the band can actually hold — see
     GRIP_MARK_MAX_BAND_SHARE above. At the reference denomination the factor is 1 and this
     resolves to `rawBox` exactly, so the 45 lb plate — the only one the live designer ever
     shows — is byte-identical to before. The baseline diff proves it rather than asserting it. */
  const pointBox = Math.min(
    rawBox * roomCompensation(gripBandInches(dia), gripBandInches(table[GRIP_REFERENCE_LB])),
    (band * GRIP_MARK_MAX_BAND_SHARE - 2 * SLOT_INSET) / GRIP_SIZE_OPEN_FACTOR,
  );
  const base = {
    anchorR: ((innerRatio + outerRatio) / 2) * PLATE_R,
    boxSide: pointBox,
    angles,
    // A gap between two hand cutouts is wider than it is deep, so a one-liner has more room than the
    // point box admits — but far less than a dumbbell's open face, which is why this is 1.4 and the
    // cap's is 2. See `inlineMaxW` (Ryan 2026-07-30: *"grip plate isn't quite as bad but could still
    // use a little enlargement"*).
    inlineBoost: 1.4,
  };
  if (!arcSlots || !arcSlots.size) return base;

  // The lettering lane is the OUTERMOST slice of the band; the mark keeps what's inboard of it.
  const arcR = rOuter - arcBand / 2;
  // The 3/8" keep-clear off each cutout edge is a DISTANCE, so its angular cost is measured at the
  // radius the letters actually ride — the same reason `gripBrandZone` dilates the cutout keep-out
  // angularly by "the inset's arc-equivalent at each radius" rather than by a constant angle. A
  // constant angle would be too tight near the bore and too loose at the rim; here it would let the
  // 10 lb's lettering run into a hand hole while wasting sweep on the 45.
  const arcRIn = (arcR / PLATE_R) * rIn; // the arc's radius in INCHES, to divide the inset by
  const insetDeg = (GRIP_SAFE_INSET_IN / arcRIn) * (180 / Math.PI);
  // A gap is half the 120° pitch less the cutout's own half-span — derived from the cutout table,
  // never stored, exactly as `geometry/grip-plate.js` derives its raised zones.
  const halfSpan = Math.max(4, (120 / 2) - GRIP_CUTOUT_HALF_DEG - insetDeg);
  // ── WHERE THE LETTERING ACTUALLY ENDS, WHICH IS NOT WHERE ITS LANE ENDS ──────────────────────
  // Ryan 2026-07-30: *"the logos and the weight numbers, when positioned between the donut hole and
  // some text, need to be centered in that gap. Right now they're all too close to the donut hole."*
  //
  // He is right and the cause is the previous change. The lane is a third of the band, but since the
  // type started hanging off the RIM (163) it only occupies the outer part of that lane — so the
  // mark, centred in "everything inboard of the lane", was centred in a region whose outer third was
  // empty air. Measured on a 45: the mark sat 18.8u closer to the hole than the midpoint of the gap
  // a buyer actually sees.
  //
  // So the mark's field now ends where the WORST-CASE word ends, not where the lane does: the
  // largest type the lane can hold (`maxFont`), grown by its outline stack, hung one unit inside the
  // keep-clear. Worst case rather than the live size ON PURPOSE — the fitted size moves with every
  // character typed, and a logo that slides up and down the plate as you spell your school's name
  // would be a far worse bug than the one being fixed.
  //
  // Sized off a REPRESENTATIVE name, not off the largest type the lane could hold. The lane's
  // theoretical maximum is what a one-word gap gets; real wording fits at a third of it, and
  // reserving for the maximum left the mark 13u short of centre — better than the 19u it started at,
  // but still visibly low. The zone is built before any typeface is known (it varies by denomination,
  // not by font), so this uses nominal metrics: an average-width face, the proportional spacing, and
  // a name of `NOMINAL_ARC_CHARS`. That lands within a few units across everything from "ARCADIA" to
  // "MOUNTAIN RIDGE ACADEMY", and it holds still while someone types — which the alternative, sizing
  // off the live text, would not.
  const laneMaxFont = arcBand - 2 * TEXT_RADIAL_INSET;
  const laneArcLen = ((2 * halfSpan - 8) / 360) * 2 * Math.PI * arcR;
  const nominalFont = Math.max(
    ARC_MIN_FONT_LANE,
    Math.min(laneMaxFont, laneArcLen / (NOMINAL_ARC_CHARS * (NOMINAL_WIDTH_RATIO + 3 / ARC_TYPE_REF_FONT))),
  );
  const nominalGrowth = ARC_GROWTH_APPROX * Math.max(0.5, Math.min(1, nominalFont / ARC_TYPE_REF_FONT));
  const textDepth = 1 + CAP_APPROX * nominalFont + 2 * nominalGrowth;
  const markOuter = rOuter - textDepth;
  const slots = {};
  for (const slot of arcSlots) {
    const centre = angles[slot];
    if (centre == null) continue;
    slots[slot] = Object.freeze({
      view: Object.freeze({
        // Centred in the gap the buyer SEES (rInner → where the type ends), while the box keeps the
        // size every grip layout shares — see `pointBox`. Only the anchor moves; a mark that resized
        // itself depending on whether the gap above it happened to hold words would read as a bug.
        anchorR: (rInner + markOuter) / 2,
        boxSide: pointBox,
        angles,
        // All three gaps read tops-OUTWARD — they are rotational copies of the top arch, which is
        // what "mirrored on the other 2 zones" means on a face with 120° symmetry. Only the
        // bumper's bottom band reads inward, and that is what `reverse` exists to say.
        // `outer` is the artwork's HARD limit for this plate — where the 3/8" margin off the
        // outermost ring lands. `drawArcText` hangs the lettering's cap-height off it rather than
        // centring in the lane, so a short word rides the rim instead of floating mid-band.
        arc: Object.freeze({ start: centre - halfSpan, end: centre + halfSpan, r: arcR, band: arcBand, outer: rOuter, reverse: false }),
      }),
    });
  }
  return { ...base, slots };
}

/** Screen degrees for the grip plate's three gaps. Frozen once and SHARED by every zone view, so
 *  `zone.angles` is the same object whether or not a slot divided its band. */
const GRIP_ANGLES = Object.freeze({ top: 270, left: 150, right: 30 });

/** The slots carrying an arc word that will actually paint — an empty line draws nothing, so it
 *  must not claim a lettering lane and shrink the logo under it (the same `renders` test
 *  `matchPresetToComposition` applies in layouts.js). */
function arcSlotsOf(elements) {
  const out = new Set();
  for (const e of elements || []) {
    if (e && e.type === 'arctext' && e.slot && String(e.text || '').trim()) out.add(e.slot);
  }
  return out;
}

// ── dumbbell / fixed-barbell END-CAP zone ────────────────────────────────────
// The face is a solid disc: no hub, no bore, no cutouts, nothing to work around. `geometry/
// dumbbell.js` planar-UVs the brand face across `2 × brandR`, so the 450u viewBox maps exactly onto
// the flat art canvas and the paintable surface is the WHOLE inscribed circle. The fixed barbell
// shares it outright — `createBarbellGeometry` delegates to the dumbbell's head generator and only
// stretches the handle, so its heads ARE these heads.
//
// ⚠ WHY IT CANNOT JUST BE ANOTHER RING ZONE. A ring zone says one thing — "every slot sits on a
// circle of radius `anchorR`, and a piece there may be `boxSide` across" — because on an annulus the
// room and the position are the same fact. On a disc they come apart: WHERE a piece sits is a share
// of the face carved out against its neighbours, and HOW BIG it draws is a design choice about that
// piece. So this zone computes two things per slot and keeps them separate:
//
//   • the FIELD — the radial run it owns, carved from the disc against whatever else is on the face
//   • the BOX   — how large it actually draws there, a taste size clamped to that field
//
// Everything downstream is unchanged: `zoneForSlot` hands back a plain zone with `anchorR` at the
// field's midpoint and `boxSide` set to the box, exactly as the grip's arc-lane split does.
//
// The artwork limit. Unlike the bumper (ring B ends at 0.868R) or the grip plate (0.906R less a 3/8"
// keep-clear), there is no ring beyond this face to stop short of — 1.0 IS the physical edge of the
// canvas. 4% is a printer's margin, about 4 mm on a 200 mm head.
const CAP_ART_FRAC = 0.96;
const CAP_ART_R = CAP_ART_FRAC * PLATE_R; // 216
/**
 * The rim lettering lane, as a share of the art RADIUS.
 *
 * ⚠ THIS, NOT THE ARC LENGTH, IS WHAT CAPS A SHORT WORD (Ryan 2026-07-30: *"the text on Crest,
 * Signet and Sidelines can afford to be a little bit bigger… make it dynamic so short words can be
 * bigger than long words"*). The dynamic half was already there and always has been: `fitArcFont`
 * shrinks a word until it fits its sweep, so a long name scales down by itself. What it cannot do is
 * grow — `maxFont` is the lane depth less its breathing room, so at 0.20R every word up to about
 * fourteen characters hit an identical 27px ceiling and none of them could take the room the rim
 * plainly had. Deepening the lane raises only that ceiling; a long name is still governed by the
 * sweep and does not move at all.
 *   The cost is paid by whatever sits inboard — `outerOf` hands the rest of the face to the point
 * pieces — which is why this is 0.28 and not more: past that the mark under a Crest starts giving up
 * more than the lettering gains.
 */
const CAP_ARC_BAND = 0.33 * CAP_ART_R;
// Clear space between two neighbouring fields.
const CAP_FIELD_GAP = 0.06 * CAP_ART_R;
/**
 * How far below the middle of the face a NUMBER facing a mark begins (Ryan 2026-07-30: *"Stacked,
 * Stacked Flip, Side by Side and Side by Side Flip have the weight number a little too far away from
 * the logo"*).
 *
 * ⚠ THIS FIELD DELIBERATELY OVERLAPS THE MARK'S. Every other field on this face is carved so no two
 * touch, and the obvious fix here — move the dividing line up until the number reaches the mark —
 * was tried and is wrong: the line is also the mark's inner edge, so raising it lifts the mark's
 * anchor, and a mark anchored higher on a DISC is a mark the corner fit makes smaller. Pulling the
 * number up by 50u cost the logo 15% of its size.
 *   The two do not actually collide, because a mark is not bounded by its field — it is bounded by
 * the disc, and on these layouts it paints down to roughly the centre line while its field runs a
 * third of a radius past it. A field is an allowance, not a claim. So the number simply starts a
 * fixed distance below the middle and the mark keeps every bit of the room it had.
 */
const CAP_PAIR_NUM_INSET = 0.13 * CAP_ART_R;
/**
 * The clear space a number keeps from the MARK it faces, once it is following it (Ryan 2026-07-30:
 * *"as the logo grows, the weight number moves slightly to keep the same gap between the two… we
 * need the same thing on stacked, stacked flip, side by side, side by side flip"*).
 *
 * 24u is not a new taste call — it is what the four pairs already showed at the seeded default, so
 * a design nobody has resized looks exactly as it did. What changes is everything either side of
 * that: the gap ran from 64u at a half-size mark to **minus 6u at the top of the dial**, where the
 * number printed over the logo. The same non-negotiable the badge hit, reachable here by turning a
 * dial rather than by picking a layout.
 */
const CAP_PAIR_GAP = 24;
/** How much wider than the point box a "45LB" one-liner may run on a cap face — see `inlineMaxW`.
 *  The disc has the room; the box was never the wall here. */
const CAP_INLINE_BOOST = 2;
/**
 * How much of an axis a MARK takes when it faces a NUMBER across the disc (Ryan 2026-07-30: *"a
 * medium+ sized logo which sits just north of the center point, with a small weight number centered
 * below it"*).
 *
 * The split is deliberately NOT down the middle. Halving the axis puts the logo's centre at 0.5R
 * above the middle of the face, which reads as two stacked halves rather than as a mark with a
 * caption; 0.64 lands its centre just past a fifth of the radius up — "just north of the center
 * point", which is the phrase this number exists to satisfy.
 */
const CAP_MARK_SHARE = 0.68;
/**
 * The taste boxes — a "medium+" mark, a "large" one for the badge layout, and a "small" number, as
 * shares of the art radius so they hold at any head size.
 *
 * ⚠ THE MARK BOXES ARE DELIBERATELY SET ABOVE WHAT THE FACE CAN HOLD, so the DISC decides a mark's
 * size and this only decides a number's. That is the opposite of the bumper, where the ring's width
 * is the whole answer — and it is the right way round here: a taste box tight enough to bind would
 * be a second, invisible limit competing with the geometry, and the first cut of this did exactly
 * that (a "large" centred logo came out the same size as a medium one, because both were pinned to
 * a constant rather than to the room they actually had).
 *
 * Measured, at the sizes the seeder opens them at: medium marks paint 0.37–0.42 of the face
 * diameter, the badge 0.49, and the number 0.09 — a ~5:1 mark-to-number ratio, which is the
 * "medium+ logo, small number" Ryan described.
 */
const CAP_LOGO_BOX = 1.2 * CAP_ART_R;
const CAP_LOGO_BOX_BIG = 1.9 * CAP_ART_R;
// 0.30 → 0.35 → 0.40 → 0.44 across the rounds of Ryan looking at it (2026-07-30: *"a fraction too
// small"*, *"bigger in many instances"*, *"all 10% larger"*). Scoped to THIS face; see the changelog
// for why the bumper's and the grip's were left alone rather than swept along with it.
//   ⚠ DECLARED HERE, ABOVE THE BADGE CONSTANTS, because `CAP_BADGE_RISE` is derived FROM it — the
// rise exists to clear this number, so the two cannot be read in the other order.
const CAP_NUM_BOX = 0.44 * CAP_ART_R;
/**
 * How close a MARK's corners may come to the physical edge of the canvas.
 *
 * Ordinary pieces stop at the 4% print margin (`CAP_ART_R`). A `bigBox` mark does not, and that is
 * the same call the bumper's Badge layouts already make in as many words: *"a rectangle cannot be
 * both flush to a circle's rim and wholly inside it — the far corners always cross"*. The corners of
 * a big centred mark are the part a designer expects to lose to the disc (Ryan 2026-07-30: *"the
 * logo size on Big Badge looks basically no different to Stacked… we can do better"*).
 *
 * ⚠ IT IS PAST THE CANVAS EDGE ON PURPOSE, AND THAT IS SAFE ONLY BECAUSE THE MARK IS CLIPPED. The
 * corner term dominates the square-in-a-circle fit, so this is by far the biggest lever on how large
 * a centred mark can be: at the print margin the badge came out 0.56 of the face against Stacked's
 * 0.37 — a layout named for its size that Ryan could not tell apart from the medium one. At 1.12 it
 * paints 0.64 and its corners trim against the round edge, which is what a big square graphic on a
 * round cap looks like in the first place. `renderDesignToSVGString` gives a `bigBox` mark the same
 * disc clip Full Face gets, so the overhang is cut cleanly rather than painted past the artwork.
 *   The honest ceiling past this is 1.0 — a mark that BLEEDS off the edge — and that layout already
 * exists and is called Full Face. Big Badge is the contained one.
 */
const CAP_BADGE_CORNER_R = 1.12 * PLATE_R;
/**
 * Where a BIG centred mark sits, and how far it may reach into the room below it (Ryan 2026-07-30:
 * *"move logo down slightly on Big Badge, and make it a bit bigger. Move the weight number up
 * slightly to be closer to the bottom of the logo"*).
 *
 * All three of those are one lever, and it runs the opposite way to intuition. The rise was DERIVED
 * — the centre of the window left between the top of the face and the number's field — which put the
 * mark at 0.24R and then punished it twice for being there: a box centred high runs out of window
 * against the number long before it runs out of face, and a square offset from the middle of a
 * circle has a smaller corner fit than a centred one. Dropping the mark toward the middle therefore
 * makes it BIGGER, not smaller, and its lower edge comes down to meet the number without the number
 * having to move at all.
 *
 * `OVERLAP` is what lets the corner fit — the honest geometric maximum — be the binding constraint
 * rather than the number's field. A badge is clipped to the disc and a mark's bottom edge is the
 * emptiest part of almost any logo's bounding box, so a controlled reach into the number's clear
 * space costs nothing real and buys the last 10% of size.
 */
/**
 * ⛔ THE RISE IS WHAT KEEPS THE NUMBER OFF THE MARK, and it is the ONLY thing that can.
 *
 * Once the number is balanced against the mark's TOP edge (see the badge block in
 * `renderDesignToSVGString`), the clear space between them works out to `2·rise − lockupHeight` —
 * the mark's own size cancels, because both its top and its bottom move with it and the number is
 * pinned to the top. So a rise chosen for looks is a rise that decides, invisibly, whether the two
 * pieces collide: at 0.15R the arithmetic came out NEGATIVE and the number printed over the logo.
 *
 * It is therefore derived from the number rather than picked: half its nominal lockup height plus
 * half the clearance. `CAP_NUM_INK_NOMINAL` is `weightLockupBox`'s height as a share of its box,
 * taken by measurement rather than from `stackedLayout`'s algebra — the 0.42 unit-line ratio there
 * is a ceiling the width-match comes in under, so the derived figure disagrees with the real one.
 *
 * ⚠ THE CLEARANCE IS DELIBERATELY TINY, and that is a size decision rather than a spacing one (Ryan
 * 2026-07-30: *"need a small gap, even if very small… in order to preserve logo large size"*). Every
 * unit of clear space costs half a unit of rise, and every unit of rise costs the mark far more than
 * that, because the corner fit falls away as a box moves off the middle of a circle. So the gap is
 * set to the smallest that still reads as a gap, and the size is what the saving buys.
 *
 * ⚠ AND IT IS NOT THE GUARANTEE. A nominal cannot be one: a different typeface or number style
 * changes the real lockup height by a few units either way. The guarantee is a hard clamp at paint
 * time, where the real height is known — this just puts the balanced answer comfortably inside it,
 * so the clamp almost never has to fire and the design stays symmetric.
 */
const CAP_NUM_INK_NOMINAL = 0.7;
const CAP_BADGE_CLEAR = 0.02 * CAP_ART_R;
function capBadgeRise(numEl, numBox = CAP_NUM_BOX) {
  // The outline stack is part of the number's height, not decoration on it: a stroke is centred, so
  // each ring grows the ink outward by its own thickness, and `stackedLayout` ALSO opens the gap
  // between the number and its unit line to make room for a thick one. Both come out of the same
  // clear space, so both belong in the rise.
  //   ⚠ THIS IS WHY THE RISE IS A FUNCTION AND NOT A CONSTANT. With four 4u rings a fixed rise left
  // the number 2.2u on the mark, and pushing it clear at paint time drove its ink 27u past the edge
  // of the artwork — trading one unshippable plate for another. Sizing the mark to leave room is the
  // only move that costs nothing anybody can see: a heavy stack is rare, and the mark it shrinks is
  // the piece with slack.
  //   ⚠ CEILED NOMINALLY, because this runs while the ZONE is being built and `weightSizes` needs a
  // zone — so it cannot ask the renderer what the stack will paint at. It doesn't have to: the rise
  // is already a nominal (`CAP_NUM_INK_NOMINAL × CAP_NUM_BOX` IS the number's nominal ink height),
  // so applying the same ceiling to the same nominal keeps this in step with the paint without
  // needing the real one. Over-reserving is the safe error here, as everywhere on this margin.
  const nominalCap = CAP_NUM_INK_NOMINAL * numBox;
  const growth = outlineTotal(numEl) * outlineInkScale(outlineMaxBand(numEl), nominalCap);
  return (nominalCap + 2 * growth + CAP_BADGE_CLEAR) / 2;
}
// Measured rather than picked, and RE-measured whenever the rise or the number moves: it is the
// value at which the CORNER FIT — the honest geometric maximum for a square on a disc — takes over
// as the binding constraint from the number's field. Below it the field binds and the badge is
// smaller than the face allows; above it the mark's box bottom crosses the number's INK instead of
// meeting it. 0.15 was that point until the mark and its caption both moved up (Ryan 2026-07-30);
// at 0.20 the box bottom lands back on the number's ink top and the badge keeps the size the
// previous round gave it while sitting where this round asked for it.
const CAP_BADGE_OVERLAP = 0.2 * CAP_ART_R;
/** …and the number rides up with it (Ryan 2026-07-30: *"move BOTH the logo and weight number up
 *  slightly on Big Badge"*). Raising the mark alone would have opened the gap under it that the
 *  previous round had just closed. */
const CAP_BADGE_NUM_LIFT = 0.06 * CAP_ART_R;

/** Screen degrees for the cap's slots. `center` names the one place no ring product could offer —
 *  the middle of the face — and its angle is the direction a piece LEANS when it is nudged off
 *  dead-centre (see CAP_BADGE_RISE), not where it sits. */
const CAP_ANGLES = Object.freeze({ center: 270, top: 270, bottom: 90, left: 180, right: 0 });
/** Rim spans per slot. Top and bottom are the bumper's own 140° arches. The sides are narrower
 *  because they answer to pieces at 12 and 6 o'clock rather than to nothing, and they FLOW around
 *  the circle (right runs downward out of the top arch, left runs up into it), which is what makes
 *  a four-arc face read as one continuous ring of words rather than two mirrored ones. */
const CAP_ARC_SPANS = Object.freeze({
  top: Object.freeze({ start: 200, end: 340, reverse: false }),
  bottom: Object.freeze({ start: 20, end: 160, reverse: true }),
  right: Object.freeze({ start: -52, end: 52, reverse: false }),
  left: Object.freeze({ start: 128, end: 232, reverse: false }),
});

/** Does this element put ink on the face? An empty arc line and a logo with no artwork both occupy a
 *  slot in the model and paint nothing, so neither may claim room from its neighbours. */
function capRenders(e) {
  if (!e || !e.slot) return false;
  if (e.type === 'arctext') return String(e.text || '').trim() !== '';
  if (e.type === 'logo') return !!(e.logo && e.logo.dataUrl);
  return e.type === 'weight';
}

/**
 * The dumbbell's / fixed barbell's zone, for the arrangement actually on the face.
 *
 * Takes `elements` rather than a weight: unlike the grip plate, whose 3/8" keep-clear eats a
 * different fraction of a 10 lb than of a 45, every cap head is the same face at every denomination
 * — the geometry scales with it. What this DOES vary by is the arrangement, because on a disc a
 * piece's room is defined by its neighbours and by nothing else.
 */
/* `weight` is POUNDS, always — the display unit never reaches this function. See `resolveZone`. */
function capZone(elements, weight, model) {
  const els = (elements || []).filter(capRenders);
  const point = (slot) => els.find((e) => e.slot === slot && (e.type === 'logo' || e.type === 'weight'));
  const arcAt = (slot) => els.some((e) => e.slot === slot && e.type === 'arctext');
  const isMark = (e) => !!e && e.type === 'logo';
  const centre = point('center');
  /* ── THE ROOM COMPENSATION (Ryan 2026-08-02) — see CAP_ROOM_EXPONENT ────────────────────────
   * INK grows; CLEARANCE does not. `CAP_LOGO_BOX`, `CAP_LOGO_BOX_BIG` and `CAP_NUM_BOX` are the
   * three quantities that decide how big a painted piece may be, so they take the factor. The
   * gaps around them — `CAP_ARC_BAND`, `CAP_FIELD_GAP`, `CAP_PAIR_NUM_INSET`, `CAP_BADGE_CLEAR`,
   * `CAP_BADGE_OVERLAP`, `CAP_BADGE_NUM_LIFT` and the artwork limit `CAP_ART_R` itself — stay
   * fractions of the face, because a margin is a margin at every size. The consequence is
   * deliberate and is Ryan's *"if the space permits it"*: on the smallest head the grown ink eats
   * into its own breathing room until a neighbour's wall stops it, and the walls on this face are
   * HARD ones (`hardWall`), so it stops against real geometry rather than against a constant.
   *   `capBadgeRise` is DERIVED from the number's box, so it follows on its own — which is why it
   * now takes the compensated box rather than reading the module constant behind our back. */
  const comp = roomCompensation(
    capHeadDiaIn(model, weight),
    capHeadDiaIn(model, referenceWeightFor(model)),
    CAP_ROOM_EXPONENT,
  );
  const logoBox = CAP_LOGO_BOX * comp;
  const logoBoxBig = CAP_LOGO_BOX_BIG * comp;
  const numBox = CAP_NUM_BOX * comp;
  const base = {
    anchorR: 0,
    boxSide: logoBox,
    angles: CAP_ANGLES,
    band: { inner: 0, outer: CAP_ART_R },
    // See `inlineMaxW`: a one-liner on this face is bound by the disc, not by the point box.
    inlineBoost: CAP_INLINE_BOOST,
    // Every wall on this face is a HARD one: a mark's neighbours are the buyer's own weight numbers,
    // not the bumper's unpainted rings. See logoDrawSize's `capW`.
    hardWall: true,
    // ⛔ NO FREE ANGLE. The ring's trisect branch swings a side piece tens of degrees to share out
    // an empty span; on a disc that is a different LAYOUT rather than a nudge, so this face is
    // fixed-slot and stays that way. The grip plate needs the same rule and got it by accident (no
    // `bottom` angle); this face HAS a bottom, so it has to say so.
    fixedSlots: true,
    // …⚠ WITH ONE EXCEPTION, AND IT IS A CLEARANCE RATHER THAN A TASTE (Ryan 2026-08-03). With arch
    // text top AND bottom, the longer arch's ends run further down the sides and genuinely squeeze
    // the gap above a side piece — measured at 57 units against 178. `capPairedDrift` evens the two
    // by rotating the pair, which costs nothing radially (a translation cannot: these pieces paint
    // at r≈213 against a 216 margin) and leaves every radial run this zone carves untouched, since
    // radii are rotationally invariant. Only the both-ends-occupied branch opens.
    sideBalance: 'paired',
  };
  const slots = {};

  // ── the rim lanes, first: lettering is measured from the edge inward, and everything else is
  // measured against what it leaves behind.
  const outerOf = (slot) => CAP_ART_R - (arcAt(slot) ? CAP_ARC_BAND : 0);
  for (const slot of ['top', 'bottom', 'left', 'right']) {
    if (!arcAt(slot)) continue;
    const span = CAP_ARC_SPANS[slot];
    slots[slot] = {
      view: {
        ...base,
        arc: Object.freeze({
          start: span.start,
          end: span.end,
          r: CAP_ART_R - CAP_ARC_BAND / 2,
          band: CAP_ARC_BAND,
          outer: CAP_ART_R, // hang the cap height off the artwork limit, as the grip's rim type does
          reverse: span.reverse,
        }),
      },
    };
  }

  // ── the point fields. Each cardinal slot owns a radial run [inner, outer] along its own axis;
  // `inner` may be NEGATIVE, which simply means the field crosses the middle of the face — that is
  // what gives a medium+ mark room to sit just north of centre while its number stays small below.
  const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
  const innerOf = {}; // how far in each occupied cardinal reaches — the centre field's walls
  for (const slot of ['top', 'bottom', 'left', 'right']) {
    const mine = point(slot);
    if (!mine) continue;
    let hangEdge = false;
    const outer = outerOf(slot);
    const opp = point(OPPOSITE[slot]);
    const myBox = isMark(mine) ? (mine.bigBox === true ? logoBoxBig : logoBox) : numBox;
    let inner;
    if (centre) {
      // ⚠ A CARDINAL BESIDE A CENTRED MARK IS ANCHORED OUTWARD, and takes only the depth it needs.
      // The first cut of this gave the centre a fixed share of the face and pushed the cardinals
      // outside it, which read backwards on the layout that needs it most: Big Badge's "large logo
      // centre" came out SMALLER than the medium one on Stacked, because a small number at the
      // bottom had reserved 46% of the radius it was never going to use. A piece takes its own size
      // and what is left over is the mark's.
      inner = outer - (myBox + 2 * SLOT_INSET);
      // A badge's caption rides up with the badge — see CAP_BADGE_NUM_LIFT.
      if (!isMark(mine) && centre.bigBox === true) inner -= CAP_BADGE_NUM_LIFT;
    } else if (opp) {
      const oppOuter = outerOf(OPPOSITE[slot]);
      if (isMark(mine) === isMark(opp)) {
        inner = CAP_FIELD_GAP / 2; // two of a kind (Four Corners) — an even split
        // …and both halves hang OUT to the rim. A matched pair divides the face down the middle, so
        // each piece is centred in a field far deeper than itself and floats in the middle of its own
        // half. See `edgeHangRadius` — it is the slack being spent, not size.
        hangEdge = true;
      } else {
        // A mark facing a number: the mark takes CAP_MARK_SHARE of the whole axis and the number
        // takes the rest, so the dividing line sits BELOW the middle of the face.
        const axis = outer + oppOuter;
        const markOuter = isMark(mine) ? outer : oppOuter;
        const cut = markOuter - axis * CAP_MARK_SHARE; // signed, in the MARK's direction
        // The MARK takes its share of the axis. The NUMBER does not take the remainder — it starts a
        // fixed distance below the middle of the face, overlapping the mark's unused lower reach.
        // See CAP_PAIR_NUM_INSET for why that is the right shape and the tidier one is not.
        inner = isMark(mine) ? cut : CAP_PAIR_NUM_INSET;
      }
    } else {
      inner = 0; // nothing opposite and nothing central — it keeps its own half
    }
    innerOf[slot] = inner;
    const depth = outer - inner;
    const box = Math.min(depth - 2 * SLOT_INSET, myBox);
    // ── WHERE IN ITS FIELD THE PIECE SITS (Ryan 2026-07-30: *"Stacked, Stacked Flip, Side by Side
    // and Side by Side Flip have the weight number a little too far away from the logo"*) ─────────
    //
    // A field is a run, and a piece has to sit somewhere in it. Centring is right for a mark — its
    // field is most of the face and the middle of it is the composition's focal point — and wrong
    // for the small number facing one: the number's field is the outer third of the axis, so
    // centring parked it out near the rim with a hole between it and the mark it belongs to.
    //   It now hangs from the INNER end of its own field instead, which is the edge it shares with
    // the mark, so the pair reads as one lockup and the leftover room falls at the rim where nothing
    // needs it. Only the number-facing-a-mark case moves: a lone piece, a matched pair (Four
    // Corners) and anything beside a centred mark all keep the centred anchor they had.
    const facingMark = !centre && opp && isMark(opp) && !isMark(mine);
    // …and the mirror of that judgement: a number facing RIM TYPE across the face hangs off its own
    // outer INK edge, at the margin the type itself keeps. See `hangOuter` below.
    /* ── ANY NUMBER ON A LETTERED FACE, NOT ONLY ONE FACING THE LETTERING (Ryan 2026-08-04:
     * *"on Signet and Sidelines… make it so the weight numbers are as close to the edge of the
     * weight as the text is to the edge"*) ──────────────────────────────────────────────────────
     *
     * This used to require the arch to sit OPPOSITE, and the note beside it argued the restriction
     * was the whole rule: *"Signet's numbers sit at 3 and 9 with its type at 12 and 6, and
     * Sidelines' the other way round: neither faces anything, and pushing those to the rim would be
     * a different arbitrary rule rather than the same one."* Ryan has now asked for exactly that,
     * and asking for it makes it the same rule after all — not "a number BALANCES an arch across
     * the face" but "on a face that carries rim type, every piece keeps the type's own margin."
     * That reads as one decision on all three arched layouts instead of a special case on Crest.
     *   Faces with no arch anywhere are untouched: nothing sets the margin there, and a number
     * pushed to the rim on Stacked would be a placement nobody asked for. */
    const facingType = !isMark(mine) && !arcAt(slot)
      && ['top', 'bottom', 'left', 'right'].some(arcAt);
    const anchorR = facingMark
      ? inner + box / 2 + SLOT_INSET
      : (inner + outer) / 2;
    const prev = slots[slot] && slots[slot].view;
    slots[slot] = {
      view: {
        ...(prev || base),
        anchorR,
        /**
         * ⚠ ONLY A NUMBER FACING RIM TYPE HANGS OFF THE EDGE, and the restriction is the whole rule
         * (Ryan 2026-07-30: *"on Crest, move the weight so it's as close to the bottom as the text
         * at the top is from the top"*).
         *
         * Rim type is HUNG — `arcOuterRadius` puts its cap height a measured outline stack plus one
         * unit inside the artwork limit — while a point piece is CENTRED in its field. Two different
         * rules on one face, and on Crest that left the number 22u off the bottom against the arch's
         * 3.5u off the top, which is exactly what reads as lopsided.
         *   It is a claim about a number BALANCING an arch across the face, so it applies only where
         * one does. Signet's numbers sit at 3 and 9 with its type at 12 and 6, and Sidelines' the
         * other way round: neither faces anything, and pushing those to the rim would be a different
         * arbitrary rule rather than the same one.
         *
         * ⚠ RESOLVED AT PAINT TIME — see `drawWeight`. A zone is built before the typeface is known,
         * and the first cut placed the anchor here from a NOMINAL lockup height: it landed the house
         * font dead on 3.5u and BBH Bartle 9u short, because `stackedLayout`'s 0.42 unit-line ratio
         * is a ceiling the width-match usually comes in under. So the zone states the target edge and
         * the renderer, which knows the real height, takes the last step.
         */
        hangOuter: facingType ? outer - (ARC_GROWTH_APPROX + 1) : null,
        // The limit a piece may push its CORNERS out to, when its zone says it may — see
        // `edgeHangRadius`. Same reservation the rim type keeps, so a pushed piece lands on the
        // margin everything else on this face respects.
        hangEdge: hangEdge ? outer - (ARC_GROWTH_APPROX + 1) : null,
        // …and a number facing a MARK follows it: see the `hangForWeight` block in
        // `renderDesignToSVGString`. The zone can only place it against a fixed line, because the
        // mark's painted size is not a property of the zone — so it says WHO follows and the
        // renderer, which knows the size, says where.
        followMark: facingMark || null,
        boxSide: Math.max(8, box),
        // The field is a slab of the disc, not an annulus: it reaches the rim (or the lettering
        // lane) but has no inner wall to clear, so the band it is fitted against runs from 0.
        band: { inner: 0, outer },
      },
    };
  }

  // ── the centre field: a WINDOW, not a circle.
  //
  // What bounds a centred mark is different in each direction — the face above it, a number below
  // it, two numbers either side — and no single radius says that. So the window is measured
  // per-edge and handed to the renderer as the three quantities it can actually use:
  //
  //   anchorR  the window's own centre, which is what makes a mark RISE above a number beneath it —
  //            Ryan's *"pushed up ever so slightly so the empty space is a tiny bit more at the
  //            bottom"* falls out of the geometry instead of being a tuned constant.
  //   boxSide  the vertical room, so the mark can never reach past the window's top or bottom.
  //   maxW     the horizontal room. `slotAnnulusBox` bounds corners against the DISC, which is right
  //            and is not the same wall as a number at 3 o'clock; see logoDrawSize's note.
  if (centre) {
    const wall = (slot, free) => (innerOf[slot] != null ? innerOf[slot] - CAP_FIELD_GAP : free);
    const free = ['top', 'bottom', 'left', 'right'].some(arcAt) ? CAP_ART_R - CAP_ARC_BAND - CAP_FIELD_GAP : CAP_ART_R;
    /* ── AN EMPTY END IS NOT A LETTERED ONE (Ryan 2026-08-03, diagnosed 08-04) ───────────────────
     * `free` charges one `CAP_ARC_BAND` the moment ANY slot carries an arc, and `innerOf` is only
     * ever written by a LOGO or a WEIGHT — never by arc text. So on a face with an arch at the top
     * and nothing at the bottom, both vertical walls came back identical and `rise` was 0: the
     * centred mark sat dead centre under the words with the whole lower half of the face empty
     * beneath it, which is the one arrangement this window exists to correct.
     *   `outerOf` already knows the answer per slot — it is the lane reservation, charged only where
     * a lane is used. So the VERTICAL walls read it.
     * ⛔ THE HORIZONTAL ONE DELIBERATELY DOES NOT. `half` bounds a centred mark's WIDTH, and a wide
     * mark's corners are held to the disc rather than to the arc band — so widening it on a face
     * whose top arch sweeps 200°→340° would let those corners run into the lettering's own ends
     * from underneath. Nothing asked for that, and no stock layout would gain from it (Crest is the
     * only one with an empty side, and it is a centred mark under an arch, not a wide one). */
    const freeAt = (slot) => outerOf(slot) - CAP_FIELD_GAP;
    const up = wall('top', freeAt('top'));
    const down = wall('bottom', freeAt('bottom'));
    const half = Math.min(wall('left', free), wall('right', free));
    const badge = isMark(centre) && centre.bigBox === true;
    // A badge sits just off the middle by a FIXED amount and may reach into the room below it; every
    // other centred piece takes the middle of whatever window it was left. See CAP_BADGE_RISE.
    const rise = badge ? capBadgeRise(els.find((e) => e.type === 'weight'), numBox) : (up - down) / 2;
    const reach = badge ? CAP_BADGE_OVERLAP : 0;
    const taste = isMark(centre)
      ? (badge ? logoBoxBig : logoBox)
      : numBox;
    slots.center = {
      view: {
        ...base,
        anchorR: Math.abs(rise),
        // `angles.center` is 270 (up), so a NEGATIVE rise — a mark sharing the face with something
        // above it — has to be expressed by aiming the other way rather than by a negative radius.
        angles: rise < 0 ? { ...CAP_ANGLES, center: 90 } : CAP_ANGLES,
        boxSide: Math.max(8, Math.min(2 * Math.min(up - rise + reach, down + rise + reach), taste)),
        maxW: 2 * half,
        // A BADGE's corners may run to the physical edge; everything else stops at the print margin.
        // This is the single biggest lever on how large a centred mark can be — the corner term is
        // what the square-in-a-circle fit is solved against — and it is why Big Badge was coming out
        // barely larger than the medium marks. See CAP_BADGE_CORNER_R.
        band: { inner: 0, outer: badge ? CAP_BADGE_CORNER_R : CAP_ART_R },
      },
    };
  }

  // ── FULL FACE: THE NUMBER GOES WHERE THE ARTWORK ISN'T (Ryan 2026-07-30) ────────────────────
  //
  // Every other layout on this face gives the number a field of its own. Full Face cannot — the mark
  // bleeds across the whole cap, so wherever the number goes it goes ON the artwork, and a fixed
  // slot is just a guess that is wrong for most logos. `logo.clearSpot` is that guess replaced by a
  // measurement: the emptiest box on the mark, found at upload (logo-structure.findClearSpot),
  // expressed in the image's own normalised box.
  //
  // It arrives as a POINT, and a point is not something the slot model can express — slots are an
  // angle plus a radius. So both are derived from it and written onto the zone: the angle onto the
  // BASE (which is where `pointAngle` reads from) and the radius onto the slot's view. The slot the
  // preset authored is kept as the name of the piece; only where it lands changes.
  //
  // No measurement — an SVG upload, a resumed design saved before this existed, a mark we could not
  // decode — and nothing happens at all: the preset's own slot stands, which is the bottom of the
  // face, which is the right default anyway.
  const face = els.find((e) => e.type === 'logo' && e.big === true && e.bigFace === 'disc');
  const spot = face && face.logo && face.logo.clearSpot;
  const num = els.find((e) => e.type === 'weight');
  if (spot && num && Number.isFinite(spot.u) && Number.isFinite(spot.v)) {
    // Through the box the mark is ACTUALLY drawn in — `fullFaceLogoBox` fits the artwork's aspect
    // inside the face, so a wide mark's box is shorter than the face and a `v` of 0.7 is not 0.7 of
    // the plate. Reading the aspect the same way `drawLogo` does keeps the two in step.
    const lg = face.logo;
    const aspect = (lg.renderUrl && Number.isFinite(lg.renderAspect)) ? lg.renderAspect : resolveLogoAspect(lg);
    /* ⛔ THE NOMINAL BOX, NOT THE ONE THE BUYER HAS BEEN ADJUSTING (Ryan 2026-08-09) ─────────────
     * *"as soon as the logo's size is changed or the logo is repositioned, this dynamic
     * repositioning of the weight number should stop, otherwise the user is constantly fighting it
     * to get the logo and the weight number positioned exactly where they want them."*
     *
     * Exactly right, and this line was the fight. The clear spot is a fraction of the mark's own box,
     * so feeding it the LIVE box — which since earlier today carries the size dial and the nudge —
     * re-derived the number's anchor on every logo edit. Shrink the logo and the number chases it;
     * push the logo up and the number follows. Two controls, one moving target.
     *   Passing no element pins this to the mark's nominal placement, so the measurement answers the
     * question it was written for — WHERE IS THE ARTWORK EMPTY — and stops answering "where is the
     * artwork right now". Note this needs no latch and no stored "frozen" value: until the buyer
     * touches the logo the two boxes are identical (Full Face seeds no size and starts unnudged), so
     * the placement they first see is unchanged and simply stops moving from then on.
     *   ⚠ The DRAW and the size PROBE still get the live element — they are about the mark itself.
     * Only this reader, which places a different piece, is deliberately blind to the adjustment. */
    /* ⛔ THE MARK'S BOX WITHOUT THE BUYER'S OFFSET (Ryan 2026-08-09: *"as soon as the logo's size is
     * changed or the logo is repositioned, this dynamic repositioning of the weight number should
     * stop, otherwise the user is constantly fighting it"*).
     *   The clear spot is a fraction of the mark's own box, so handing it the live box made the
     * number chase the logo: push the logo up and the number followed it up. Zeroing the nudge here
     * pins the placement to where the layout puts the mark, so the two controls stop being one.
     *   ⚠ THE SIZE IS STILL READ, and that is a known half-measure rather than an oversight — see
     * the note to Ryan. Freezing size the same way needs the placement to be REMEMBERED at the
     * moment of the first hand-size, or the number jumps the instant he touches the dial, which is
     * a worse version of the same complaint. */
    const box = fullFaceLogoBox(aspect > 0 ? aspect : 1, { ...face, nudgeU: 0, nudgeV: 0 });
    const dx = (box.x + ((spot.u + 1) / 2) * box.w) - CENTER;
    const dy = (box.y + ((spot.v + 1) / 2) * box.h) - CENTER;
    // Held inside the artwork limit with its own half-box, so a spot found near the edge of a wide
    // mark cannot walk the number off the face.
    const half = numBox / 2 + SLOT_INSET;
    const r = Math.min(Math.hypot(dx, dy), Math.max(0, CAP_ART_R - half));
    const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
    base.angles = { ...base.angles, [num.slot]: deg };
    slots[num.slot] = {
      view: {
        ...((slots[num.slot] && slots[num.slot].view) || base),
        angles: base.angles,
        anchorR: r,
        boxSide: numBox,
        band: { inner: 0, outer: CAP_ART_R },
      },
    };
  }

  return { ...base, slots };
}

/**
 * The zone for a MODEL id ('plate' | 'grip' | 'dumbbell' | 'barbell') — the same
 * ids products.js's MODELS is keyed by. Anything unrecognised reads as the plate,
 * which is what every caller got before zones existed.
 *
 * `elements` is read for one thing only on each face that reads it at all: which grip slots carry
 * arc text, and which cap slots carry anything. Omit it and a grip plate resolves to its undivided
 * band and a cap to its bare disc, which is the right answer for every caller that isn't painting.
 */
/* ⛔ NO `unit` PARAMETER, AND ITS ABSENCE IS THE FIX (Ryan 2026-08-02).
   A zone is geometry, and `weightValue` is ALWAYS POUNDS — see design-profile's "UNIT IS A LABEL,
   NOT A WEIGHT". This used to take the display unit and hand it to `capZone`, which is how flipping
   LB→KG could resize a model; `gripZone` never took one at all, which is how a grip plate came out
   at its 20 LB size on a 20 kg design. Removing the parameter from the whole geometry path (27 arg
   passes) means nobody can re-thread a display unit into it by accident, which the grip's silent
   lb-only assumption proves is worth more than the two lines it saves. */
function resolveZone(product, weight, elements) {
  if (product === 'grip') return gripZone(weight, arcSlotsOf(elements));
  if (product === 'dumbbell' || product === 'barbell') return capZone(elements, weight, product);
  return ZONE_PLATE;
}

/**
 * The weight a zone should be built at when the caller didn't carry one.
 *
 * ⛔ NOT A LITERAL 45. Every `resolveZone` call site used to fall back to `?? 45`, which was a
 * harmless nonsense while only the grip read the weight (45 IS the grip's and the bumper's own
 * reference). Now that the cap reads it too, 45 on a dumbbell is a real denomination five rungs
 * below its reference and would paint a compensated face on every path that doesn't know its own
 * weight. Resolving to the product's reference makes "unknown" mean "factor exactly 1".
 */
function zoneWeight(product, profileWeight, optWeight) {
  const w = optWeight ?? profileWeight;
  return Number.isFinite(Number(w)) && Number(w) > 0 ? w : referenceWeightFor(product);
}

let uidCounter = 0;
function nextUid() {
  uidCounter += 1;
  return `wd-${uidCounter}`;
}

// ── string builders ──────────────────────────────────────────────────────────
function escText(v) {
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(v) {
  return escText(v).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function attrs(map) {
  let out = '';
  for (const key in map) {
    const v = map[key];
    if (v === null || v === undefined) continue;
    out += ` ${key}="${typeof v === 'number' ? v : escAttr(v)}"`;
  }
  return out;
}
function el(tag, map) {
  return `<${tag}${attrs(map)}/>`;
}

// ── arc paths ─────────────────────────────────────────────────────────────────
/** Arc path d — angles in degrees, 0°=+x, positive=clockwise (screen coords). */
function arcPath(cx, cy, r, startAngle, endAngle) {
  const s = (startAngle * Math.PI) / 180;
  const e = (endAngle * Math.PI) / 180;
  const x1 = cx + r * Math.cos(s);
  const y1 = cy + r * Math.sin(s);
  const x2 = cx + r * Math.cos(e);
  const y2 = cy + r * Math.sin(e);
  const large = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}
/** Reversed arc (sweep 0) so bottom-band text reads left-to-right, not flipped. */
function arcPathReverse(cx, cy, r, startAngle, endAngle) {
  const s = (endAngle * Math.PI) / 180;
  const e = (startAngle * Math.PI) / 180;
  const x1 = cx + r * Math.cos(s);
  const y1 = cy + r * Math.sin(s);
  const x2 = cx + r * Math.cos(e);
  const y2 = cy + r * Math.sin(e);
  const large = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 0 ${x2} ${y2}`;
}

// ── slot anchor geometry ──────────────────────────────────────────────────────
/**
 * THE ZONE AS ONE SLOT SEES IT.
 *
 * A zone was three scalars shared by every slot until the grip plate needed an arc word and a logo
 * in the SAME gap at different radii (Ryan 2026-07-30). The obvious shape for that — per-slot
 * `anchorR`/`boxSide` fields every consumer looks up — would have meant touching ~20 direct reads of
 * `zone.anchorR` / `zone.boxSide` scattered through the sizing, drift and focus maths, and any one
 * of them missed is a mark printed over a hand hole with nothing to catch it.
 *
 * So the narrowing happens ONCE, here, and hands back a plain zone with the numbers already right
 * for that slot. Every existing `zone.anchorR` read keeps working untouched; a caller's only job is
 * to resolve before it measures. The returned view carries no `slots` map of its own, so resolving
 * twice is a no-op — which matters, because several of these functions call each other.
 *
 * The bumper returns the very same object it was given (identity, not a copy), so its path cannot
 * drift by so much as a rounding step.
 */
function zoneForSlot(zone, slot) {
  const z = zone || ZONE_PLATE;
  const s = z.slots && z.slots[slot];
  return s ? s.view : z;
}

/** The lettering lane a slot's arc word rides, or null when this zone doesn't divide that slot —
 *  which is every bumper slot, and every grip slot with no arc text. Null means "use the module's
 *  own TOP_ARC / BOTTOM_ARC ring spans", i.e. exactly the behaviour that predates this. */
function slotArc(zone, slot) {
  return zoneForSlot(zone, slot).arc || null;
}

/** Square-ish box side available to a point-slot element (the zone's radial room). */
function slotBoxSide(zone = ZONE_PLATE, slot) {
  return zoneForSlot(zone, slot).boxSide;
}

/**
 * How much of its shape-specific room a point logo must fill (Ryan 2026-07-25). See
 * `slotAnnulusBox` — the floor that stops a wide/tall mark printing microscopically.
 */
const SLOT_TANGENTIAL_FILL = 0.75;
// How much wider than the two outer gaps the MIDDLE gap is aimed (Ryan 2026-07-25) — pure optical
// compensation: the gap between two heavy pieces reads tighter than one facing thin lettering. 1.0
// would be mathematically equal white space, which measured right and looked pinched.
const MID_GAP_BIAS = 1.05;

/** Range of the manual logo-size dial (Ryan 2026-07-25). A MULTIPLIER on the auto-fit, so 1 is
 *  "the size the engine chose"; the result is re-capped at the slot's real room either way.
 *    MIN 0.5 → 0.2 (Ryan 2026-07-25): half-size wasn't nearly small enough for a mark meant to sit
 *  as a discreet accent next to a big number. There's no engineering reason for a floor here — the
 *  art simply prints smaller — so the range now reflects taste, not a limit. The MAX is the one
 *  that's real, and it's belt-and-braces anyway: `drawW > capW` re-caps at the slot's true room, so
 *  the top of the dial can't push art off the paintable surface even if this number were raised. */
// What "100%" on the logo dial means, as a share of the slot's real room (`capW`). See the long note
// in logoDrawSize for why it is 0.9 — it is measured off the logos where the weight size-match does
// not bite, whose 100% has always been exactly this.
export const LOGO_ROOM_FRACTION = 0.9;
export const LOGO_SCALE_MIN = 0.2;
// 110%, not 135%. The hard stop is `capW`, which is 1/0.9 = 111.1% of the room, so a ceiling above
// that would let the dial keep counting while the art had stopped growing — the exact complaint that
// started the weight-number work tonight. 1.1 lands on the 5% grid just under the bound, so every
// click up to the top does something visible.
export const LOGO_SCALE_MAX = 1.1;
/* ── THE BIG-LOGO LAYOUTS GET A HIGHER CEILING (Ryan 2026-08-09) ─────────────────────────────────
 * *"allow the weight logos to be increased on these layouts only to 150%."*
 *
 * "These layouts" is Badge and Full Face — the ones whose mark is placed by a RULE against the ring
 * or the face rather than fitted into a slot. That is what makes the higher ceiling safe here and
 * only here: a plain slot logo is bounded by `capW` at 1/0.9 = 111.1% of its room (the reason
 * `LOGO_SCALE_MAX` is 1.1 — past that the dial counts while the art has stopped growing), whereas a
 * big mark is clipped to its surface, so every click up to this is a real, visible change.
 *   ⚠ 1.5 → 1.2 → 1.5 across three of Ryan's messages on 2026-08-09, and the number is the least
 * important part of it. What settled between them is the CONTRACT: the step stays 0.05 ("keep the
 * smaller increments the same") and 100% keeps meaning the size it has always meant, with this as
 * pure headroom ABOVE it. Both are held by `edgeFraction` on the two dials — see the logo dial in
 * ui/logo-step.js and the number's in ui/design-panel.js. Re-pitching this constant needs nothing
 * else touched precisely because those two re-base themselves from it.
 *   ⚠ THE DIAL'S OWN MAX STAYS SHARED, and it has to move with this or the extra range is
 * unreachable — `createStepper` scans up to `max` and greys the + at the first flat step. A slot
 * logo passed a higher max simply greys earlier, at its own geometric wall, which is the mechanism
 * already in place for every other "the dial ends before the art does" case.
 */
export const BIG_LOGO_SCALE_MAX = 1.5;
/* ── A PLAIN LOGO MAY GROW UNTIL ITS INK TOUCHES (Ryan 2026-09-26) ────────────────────────────────
 * CUSTOM_WEIGHTDESIGNER_BY_CLAUDE. *"make it so the logo size in the WD can go above 100% in 5%
 * increments so long as the logo doesn't expand into the oob area"* — then, asked where it stops:
 * *"grow until it touches the text or touches the edge, never overlap."*
 *   The old stop was the slot's padded image BOX (`capW`, reached at 110%). A mark that has drifted
 * round the ring points a box CORNER at the rim, and round art never fills that corner: RW-1444-A's
 * rams head stopped at ~105% with its ink nowhere near either edge. `logoInkReachW` measures the
 * real thing — the artwork's ink hull (logo-ink-hull.js) against the paintable band, where the mark
 * actually sits — and the arc-text clearance still applies on top.
 *   This is the dial's top only. Where there is no hull (not measured yet, an added outline ring, a
 * cap face, a badge) the stop is exactly the old one, and 100% still means what it always meant —
 * the logo dial re-bases its readout to the legacy wall (ui/logo-step.js `edgeFraction`). */
export const LOGO_SCALE_REACH = 2;

/**
 * The largest ANCHOR-CENTRED box of a given aspect that actually fits the zone's annulus at
 * `slot` (Ryan 2026-07-25). Returns `null` for a slot that isn't near-cardinal.
 *
 * `slotBoxSide` is the ring's RADIAL width, and the old sizing used it as a SQUARE box — the
 * same limit in both directions. That is right radially and badly wrong tangentially: a logo in
 * the TOP slot is bounded above/below by the ring's width, but sideways it has most of the
 * plate's chord to play with. So a short/wide mark was squeezed to `boxSide` wide and
 * `boxSide/aspect` tall — a 3:1 wordmark got ~22u of height on a 450u plate and was unreadable,
 * while the room either side of it sat empty. Same defect rotated 90° for a TALL logo at
 * left/right.
 *
 * Geometry: with the box centred on the anchor at radius `Rmid`, half-radial `r/2` and
 * half-tangential `k·r/2` (k = tangential:radial ratio), the binding limits are the inner ring
 * edge and the OUTER CORNERS, which swing wide as the box grows sideways:
 *     (k·r/2)² + (Rmid + r/2)² ≤ Ro²      →  r = 2·(√(Rmid² + (k²+1)·D) − Rmid) / (k²+1)
 * with D = Ro² − Rmid². Anchor-centred on purpose: `upsideDown` rotates the mark 180° about its
 * ANCHOR, so a box centred anywhere else would flip to the wrong radius.
 *
 * Near-cardinal only. The plate's four slots are exactly cardinal; the grip plate's 30°/150°
 * slots sit diagonal to the box's own axes, where "radial" and "tangential" stop matching width
 * and height — those keep the old square box rather than trust an approximation.
 *
 * TOP/BOTTOM ONLY, deliberately. The mirror case is real — a TALL logo at left/right is squeezed
 * by the very same square box — but its spare room runs VERTICALLY, straight into the top and
 * bottom arc-text bands: at aspect 1:4 the fit is 110u tall, whose corners land on TOP_ARC's own
 * start angle, so a Letterman-style layout would collide. A wide logo at top/bottom has no such
 * neighbour — it grows toward left/right and, even at 4:1, spans only ~21° of the 90° to them, and
 * nothing can occupy the top slot it is already in. Fixing the left/right case needs arc-text
 * awareness this sizing pass doesn't have; it is NOT free the way this one is.
 */
function slotAnnulusBox(zoneIn, slot, aspect) {
  const zone = zoneForSlot(zoneIn, slot); // an arc word above this slot has already taken its lane
  const deg = ((zone.angles && zone.angles[slot]) ?? null);
  if (deg == null || !(aspect > 0)) return null;
  const rad = (deg * Math.PI) / 180;
  const c = Math.abs(Math.cos(rad)), s = Math.abs(Math.sin(rad));
  const widthIsTangential = s > c;                       // top/bottom: radial is vertical
  if (!widthIsTangential) return null;                   // left/right — see the arc-text note above
  if (Math.min(c, s) > 0.34) return null;                // diagonal slot (>~20° off axis) — skip
  const k = widthIsTangential ? aspect : 1 / aspect;     // tangential : radial
  const Rmid = zone.anchorR;
  // The outer wall this box's corners must stay inside. `anchorR + boxSide/2` is that wall on a ring
  // face, where the box IS the band — but on a CAP the box is a taste size and the wall is the
  // field's own edge, so the zone states it. At the cap's `center` slot the two answers differ by
  // more than a hair: reconstructing would let a wide wordmark grow tangentially straight through
  // the weight numbers either side of it.
  //   ⚠ AND IT KEEPS THE SLOT INSET. The reconstruction is `anchorR + boxSide/2`, which is the band's
  // outer edge LESS `SLOT_INSET` — the padding every point element holds off the wall. Reading a
  // declared band without subtracting it back grew every wide bumper logo by 7u, which the standing
  // render baseline caught immediately.
  const Ro = zone.band ? zone.band.outer - SLOT_INSET : Rmid + zone.boxSide / 2;
  const D = Ro * Ro - Rmid * Rmid;
  const kk = k * k + 1;
  const r = Math.min((2 * (Math.sqrt(Rmid * Rmid + kk * D) - Rmid)) / kk, zone.boxSide);
  return widthIsTangential ? { w: r * k, h: r } : { w: r, h: r * k };
}

// ── auto-balancing ring (Fable §2.4) ──────────────────────────────────────────
// When top & bottom arc text differ meaningfully in length, the left/right point
// anchors (logo / weight) drift around the ring so the four gaps stay visually
// equidistant. Near-equal text stays dead-centered (a dead-zone kills fussy
// micro-shifts). A manual `angleDeg` override (Advanced) disengages auto per piece.
//
// This whole mechanism is BUMPER-ONLY in practice, and stays measured against the
// bumper's ring on purpose: it needs a top AND a bottom arc-text to do anything
// (see computeBalanceDelta's early return), and only the plate family authors arc
// text. The grip plate's three slots are fixed gaps between hand cutouts — there
// is no free angle for an anchor to drift INTO — so it must never engage there.

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — ARC TEXT RUNS PAST 100% UNTIL ITS LETTERS FILL THE BAND.
   Ryan, UX run 1 (DENVER / BARBOS): the text dial stopped at 100% with room left in the ring. The old wall is
   `maxFont = (band - 2*inset) * sizeScale` at sizeScale 1 - an EM box, and capitals fill only ~70% of an em, so the
   letters stopped well short of the band. Above 1 the em may now grow until the TYPED letters' ink (cap height +
   the outline stack on both sides + 1u) fills the same band the inset always reserved; the arc's length still limits
   it (`fitArcFont`). Lowercase may carry descenders the cap height does not see, so a word with any lowercase keeps
   the old wall. At sizeScale <= 1 nothing changes (100% is the old wall: `sizeEdgeFor` still scans [0.4, 1]), and the
   grip's lettering lanes keep their own wall - their type hangs off the rim (`arcOuterRadius`). */
export const ARC_TEXT_SCALE_DIAL_MAX = 1.6;
function arcMaxFont(element, lane, font, text, zone) {
  const base = lane.band - 2 * TEXT_RADIAL_INSET;
  const s = element && Number.isFinite(element.sizeScale) ? element.sizeScale : 1;
  if (s <= 1 || slotArc(zone, element && element.slot) || /[a-z]/.test(text)) return Math.min(base * Math.min(s, 1), 72);
  const inkFill = (base - 2 * outlineTotal(element) - 1) / fontCapFrac(font);
  return Math.min(base * s, Math.max(base, inkFill));
}

/** The auto-fit font size one arc word paints at — its own text shrunk to fill its arc
 *  span, capped by sizeScale. Factored so the link-clamp + the extent math below match
 *  what `drawArcText` actually paints, exactly (Ryan 2026-07-22). */
function arcFontSize(element, font, allCaps, solo = false, zone) {
  const raw = element && element.text ? element.text : '';
  const text = allCaps ? raw.toUpperCase() : raw;
  const arc = arcSpanFor(element, solo, zone);
  const span = Math.abs(arc.end - arc.start);
  const lane = arcLaneOf(zone, element && element.slot);
  const maxFont = arcMaxFont(element, lane, font, text, zone);
  // ONE fitter for all three readers of this (the link-clamp, the size dial's probe, and the paint
  // itself). They have to agree exactly or the dial greys against a wall the plate does not have.
  return fitArcToLane(text, element, zone, lane, span - 8, maxFont, font);
}

/**
 * The size a piece ACTUALLY PAINTS AT for a candidate `sizeScale` — the number the plate obeys,
 * not the number on the dial (Ryan 2026-07-25: "I'm clicking + on text and it keeps increasing
 * from 75 to 80 to 85, but the font hasn't increased once, it hit its limit at like 70 or 75").
 *
 * `sizeScale` is a CEILING, not a size. Arc text is fitted to the arc span it has to live in
 * (`fitArcFont`), then capped by `min(RING_W-derived maxFont, 72)` — so once the span is the
 * binding constraint, raising the ceiling changes nothing at all. Two more constraints stack on
 * top: the 72px hard ceiling, and the link-clamp that paints chained top/bottom arcs at the
 * SMALLER of their two fits. Any of the three can be the wall.
 *
 * Rather than have the UI enumerate those rules — a copy that would drift the first time one of
 * them moved — it asks this: "what would actually be painted at X?". The panel steps the dial one
 * notch in a candidate profile and compares; if the painted size doesn't move, the buyer is at the
 * wall and the button greys out. That test stays correct no matter which constraint is biting, and
 * it needs no knowledge of them.
 *
 * @param {object} profile  the design profile (read-only — elements are cloned, never mutated)
 * @param {'arctext'|'weight'} type  which family of piece to probe
 * @param {number} sizeScale  the candidate dial value
 * @param {{product?: string, weight?: number}} [opts]  same two the renderer takes — the grip
 *   plate's slots vary by denomination, so the zone can't be derived from the profile alone.
 * @returns {number} painted size in engine units (0 when the layout has no such piece)
 */
export function paintedTextSize(profile, type, sizeScale, opts = {}) {
  const p = profile || {};
  const font = p.font || DEFAULT_FONT_ID;
  const allCaps = p.allCaps !== false;
  // Clone the pieces of this type at the candidate scale. Everything below reads elements, so a
  // shallow copy per piece is enough and the caller's profile is untouched.
  const els = (p.elements || []).map((e) => (e && e.type === type ? { ...e, sizeScale } : e));
  if (type === 'arctext') {
    const solo = isSoloArc(els);
    const zone = resolveZone(opts.product, zoneWeight(opts.product, p.weightValue, opts.weight), els);
    const clamp = sharedArcFonts(els, font, allCaps, zone);
    const arcs = els.filter((e) => e && e.type === 'arctext' && String(e.text || '').trim());
    if (!arcs.length) return 0;
    // The link-clamped size when the chain is on, else this word's own fit — mirroring exactly
    // what drawArcText does with `fontSizeOverride`.
    return Math.min(...arcs.map((e) => clamp.get(e.sizeScale ?? 1) ?? arcFontSize(e, font, allCaps, solo, zone)));
  }
  if (type === 'weight') {
    const el = els.find((e) => e && e.type === 'weight');
    if (!el) return 0;
    // Same two fallbacks the renderer uses for these (see renderDesignToSVGString).
    const weight = zoneWeight(opts.product, p.weightValue, opts.weight);
    const zone = resolveZone(opts.product, weight, els);
    const box = weightLockupBox(el, weight, font, zone, normUnit(p.unit));
    // The lockup's own width — `weightSizes` reads sizeScale, so this moves with the dial until
    // one of its internal caps (maxW / inlineMaxW, both derived from the slot box) takes over.
    return box ? Math.round(box.w * 1000) / 1000 : 0;
  }
  return 0;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — THE NUMERALS' OWN PAINTED SIZE, so a number-style change can
   keep them where they were. Ryan, UX run 1: switching POUNDS -> plain "45" shrank the 45 (font 41.7 -> 37.1) while the
   dial read 75% on both - each style's 75% is a share of its OWN wall. Same pieces `drawWeight` sizes with. */
export function paintedNumeralSize(profile, sizeScale, opts = {}) {
  const p = profile || {};
  const els = (p.elements || []).map((e) => (e && e.type === 'weight' ? { ...e, sizeScale } : e));
  const el = els.find((e) => e && e.type === 'weight');
  if (!el) return 0;
  const weight = zoneWeight(opts.product, p.weightValue, opts.weight);
  const zone = resolveZone(opts.product, weight, els);
  const ws = weightSizes(el, weight, p.font || DEFAULT_FONT_ID, zone, normUnit(p.unit));
  return ws ? (ws.single ? ws.fontSize : ws.numFont) || 0 : 0;
}

// The dial value at which a piece stops growing — scanned, not solved, for the same reason the
// UI scans: each kind runs into a different rule and several are a min() of two.
// Module scope so `seedDefaultSizes` and `sizeEdgeFor` share ONE copy: they must agree on what
// "100% of the room" means or a re-peg computed through one would land somewhere the other denies.
/* ⛔ THE TOLERANCE IS RELATIVE, AND IT IS THE WHOLE FUNCTION (Ryan 2026-08-02: *"as soon as I add an
   outline to it… the font size itself also seems to jump up significantly"*).

   This looked for a size that stops changing EXACTLY, and an outlined piece never gives it one. Once
   the ring-band ceiling binds, the painted size is `room / natW`, and `room` subtracts the outline's
   ink growth — which is itself a function of the glyph size. So above the wall the result keeps
   creeping by a few THOUSANDTHS of a unit per step while the free growth below it moves ~3.5 units.
   Measured on a 2.5 lb grip: 0.005–0.007 against 3.49, and `1e-9` called both of them "changed".

   The consequence was not subtle, because everything downstream multiplies this: no edge is found,
   `hi` is returned, and `seedDefaultSizes` opens the piece at 75% of the TOP OF THE DIAL instead of
   75% of the real wall. Adding a single 2-unit ring made the number jump 23%. The dial then called
   that 75% too, so nothing on screen admitted it.

   0.5% of the painted size separates the two by more than an order of magnitude at both ends: sixty
   times the drift, a tenth of a real step. An un-outlined piece is unaffected — its plateau is exact,
   so any tolerance finds the same edge (`ruled` / `inline-lb` / the bumper all measured unmoved).

   ⚠ THIS RESIZES PLATES THAT WERE ALREADY SIGNED OFF, and that was put to Ryan rather than decided
   quietly. Measured against the pre-session engine (`a6c6102`), the 45 lb grip already read
   `edge 1.350 / numFont 54.99` — the bug predates this session, so switching it on is not a revert,
   it is a **-22% on every grip number** (and -4 to -7% on a couple of cap layouts; bumpers do not
   move at all, their numbers never reached the wall). Ryan chose it on 2026-08-02 with those numbers
   in front of him: *"turn it on everywhere"*. The 2.5 lb outlined number lands at 36.25 against the
   37.95 of the un-outlined one he had just called "really good", which is the point. */
const EDGE_TOL = 0.005;
/**
 * "These two dial values paint the same size" — the ONE definition, exported because the −/+
 * control asks the same question three times over and used to answer it with `!==`.
 *
 * ⛔ EXPORTED AFTER `ui/stepper.js` SPENT FOUR DAYS SILENTLY UNDOING THE FIX ABOVE (Ryan
 * 2026-08-06: *"the plate spins then stops, the weight number starts smaller, then it snaps to
 * being bigger… after a second"*).
 *   The tolerance landed here on 2026-08-02 and the dial kept its own copy of the scan, still
 * comparing exactly. So the two disagreed on 261 of 1112 (product × denomination × layout)
 * combinations — every outlined piece on a grip, dumbbell or barbell — and the disagreement was
 * a race the buyer could watch: `seedDefaultSizes` wrote 75% of the REAL wall before the draw, the
 * plate wore it, and then the panel rebuild's `seedTo` re-seeded 75% of the TOP OF THE DIAL and
 * the number jumped +35% (grip 45: scale 0.609 → 0.823, painted 40.69 → 54.99). Measured with
 * tools/probe-grip-hop-numsize.mjs; the spread is enumerated by tools/probe-edge-agreement.mjs.
 *   ⚠ So the size that "snapped bigger" was the REGRESSION, not the correction — the approved
 * -22% was on screen for about a second and then got overwritten on every panel rebuild.
 *   The lesson is CLAUDE.md §0b's, exactly: two places needed the same answer and one was wrong.
 * Anything comparing painted sizes goes through this predicate, never through `===`.
 */
export function paintsSame(a, b) {
  return Math.abs(a - b) <= Math.max(1e-9, EDGE_TOL * Math.abs(b));
}
/**
 * The dial value at which `probe` stops growing — scanned down from `hi`.
 *
 * Exported for `ui/stepper.js`, which is the other half of the pair above. A dial that computes
 * its own 100% is a dial that can disagree with the engine that paints it.
 */
export function edgeOf(probe, lo, hi, step) {
  const top = probe(hi);
  let s = hi;
  for (let x = hi; x >= lo; x -= step) {
    const y = Math.round(x * 1000) / 1000;
    if (!paintsSame(probe(y), top)) break;
    s = y;
  }
  return s;
}
const EDGE_RANGE = { weight: [0.4, 1.35, 0.05], arctext: [0.4, 1, 0.05] };

/**
 * The scale at which a text piece stops growing — i.e. what the dials call 100% of the room.
 *
 * Exported because the room MOVES for reasons other than a layout change, and a caller then has to
 * convert a stored scale from the old room to the new one. `sizeScale` is an absolute multiplier
 * while every dial reads as a share, so the two only agree while the geometry holds still.
 *
 * @returns {number} 0 when there is no piece of that type to measure.
 */
export function sizeEdgeFor(profile, type, opts = {}) {
  const p = profile || {};
  if (!Array.isArray(p.elements) || !p.elements.some((e) => e && e.type === type)) return 0;
  const range = EDGE_RANGE[type];
  if (!range) return 0;
  return edgeOf((v) => paintedTextSize(p, type, v, opts), range[0], range[1], range[2]);
}

/**
 * Lay the DEFAULT sizes into a profile: the logo and the weight number at `pct` of the room they
 * have, arc text as large as it fits (Ryan 2026-07-25).
 *
 * PURE STATE, NO RENDER — and that is the entire point of it existing. The dials can seed
 * themselves, but they only exist once their panel is built, which is after the plate has been
 * dressed: the buyer watched the reveal play at the engine's raw 1.0 and then saw everything snap
 * to 75% a beat later ("the logo loads/reveals at 85%, then after a split second it snaps"). Seeding
 * the profile BEFORE the first render means the reveal is correct in its first frame. Committing
 * from the UI that early is not an option — a commit draws the plate, which pre-empts the build.
 *
 * `sizeUserSet` is honoured throughout: a piece the buyer has sized by hand is never touched.
 *
 * @returns {boolean} whether anything changed (so a caller can skip a needless re-render)
 */
/**
 * The logo's GEOMETRY at this slot — the auto fit (weight size-match + legibility floors), the hard
 * cap, and the room the dial's 100% is pegged to. Probed through `logoDrawSize` at scale 1 so there
 * is exactly one implementation of all three, and the seeder cannot drift from the painter.
 */
function logoRoomOf(p, opts = {}) {
  const el = (p.elements || []).find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!el) return null;
  const aspect = el.logo && el.logo.aspectRatio > 0 ? el.logo.aspectRatio : 1;
  const weight = zoneWeight(opts.product, p.weightValue, opts.weight);
  const zone = resolveZone(opts.product, weight, p.elements);
  const font = p.font || DEFAULT_FONT_ID;
  let matchBox = null;
  const weightEl = (p.elements || []).find((e) => e && e.type === 'weight');
  if (weightEl && el.big !== true) matchBox = weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, normUnit(p.unit));
  const d = logoDrawSize({ ...el, sizeScale: 1 }, aspect, zone, matchBox, null);
  return { autoW: d.autoW, capW: d.capW, room: d.capW * LOGO_ROOM_FRACTION };
}

// The dial ceiling the seeder's rule was written against, kept as its own constant so lowering the
// LIVE ceiling to 110% does not silently re-scale every default that was tuned under the old one.
const LOGO_SCALE_MAX_LEGACY = 1.35;

export function seedDefaultSizes(profile, { product, weight, pct = 75, arcPct = 100 } = {}) {
  const p = profile || {};
  const els = p.elements || [];
  if (!els.length) return false;
  const opts = { product, weight };
  let changed = false;
  const setScale = (el, next) => {
    const v = Math.round(next * 1000) / 1000;
    if (!(v > 0) || Math.abs((el.sizeScale ?? 1) - v) < 1e-9) return;
    el.sizeScale = v;
    changed = true;
  };
  const untouched = (el) => el && el.sizeUserSet !== true;

  /* ⚠ FULL FACE IS STILL NOT SEEDED, AND THE REASON CHANGED ON 2026-08-09 WITHOUT THE RULE CHANGING.
     It was excluded because `fullFaceLogoBox` ignored `sizeScale` outright, so seeding wrote a number
     the plate could never obey (Ryan 2026-07-30). That box now honours the dial — but the layout's
     whole identity is a mark that INSCRIBES THE FACE, and seeding it to the usual ~75% share would
     open every Full Face at four-fifths of the thing it is named for.
       So it stays at 1: the readout opens at 100%, the + is correctly at its wall, and − now walks it
     down, which is exactly what Ryan asked for (*"need to be able to increase and decrease it"*).
     The difference from before is that the dial is no longer greyed at BOTH ends — the probe tracks
     the box now, so there is somewhere to go. */
  const logos = els.filter((e) => e.type === 'logo' && e.logo && e.logo.dataUrl
    && !(e.big === true && e.bigFace === 'disc'));
  if (logos.some(untouched)) {
    // ── THE DEFAULT IS WHERE THE SIZE-MATCH STILL LIVES (Ryan 2026-07-28) ─────────────────────
    // The dial's scale is now a share of the slot's ROOM, which knows nothing about the weight — so
    // if the default were also expressed as a plain share, an untouched logo would stop following
    // the number style, and following it is the whole point of the match ("they should only be
    // connected to one another if you don't manually adjust the logo's size").
    //   So the default is computed in PAINTED units from the auto-fit — match, floors and all — and
    // only then converted into the new coordinate. Same painted size the seeder has always produced;
    // the number underneath it is what changed.
    const geo = logoRoomOf(p, opts);
    if (geo && geo.room > 0) {
      // The seeder's own long-standing rule, restated in painted units: `pct`% of the scale at which
      // the auto-fit would stop growing, capped by the room it actually has.
      const grow = Math.min(LOGO_SCALE_MAX_LEGACY, geo.autoW > 0 ? geo.capW / geo.autoW : 1);
      // ── A `bigBox` MARK OPENS AT ITS FULL ROOM, NOT AT THREE-QUARTERS (2026-07-30) ─────────────
      // The house 75% exists so a buyer has somewhere to grow into, which is right for a mark that
      // shares its face with other pieces. It is wrong for the one layout whose entire premise is
      // "large logo centre": three-quarters of the room put the cap's Big Badge at 0.41 of the face
      // against a medium Medallion's 0.42 — a layout named for its size that was not visibly bigger
      // than the layout beside it. Opening at the room itself paints it 0.49, and `LOGO_SCALE_MAX`
      // still leaves ~11% of headroom above that.
      const openPct = logos.some((e) => e.bigBox === true) ? 100 : pct;
      const target = Math.min(geo.capW, geo.autoW * ((openPct * openShareFactorFor(product, weight)) / 100) * grow);
      // SNAPPED TO THE 5% GRID (Ryan 2026-07-28: "the size % should also adjust accordingly, in
      // increments of 5%"). The dial's own step is 0.05, so an unsnapped seed is a value the buyer
      // can see but never return to — the first click would round it away and quietly lose the
      // remainder. Costs at most 2.5% of the default size and makes every number the dial displays
      // a number the dial can reach.
      const snapped = Math.max(LOGO_SCALE_MIN, Math.min(LOGO_SCALE_MAX, Math.round((target / geo.room) / 0.05) * 0.05));
      logos.filter(untouched).forEach((el) => setScale(el, snapped));
    }
  }
  const weights = els.filter((e) => e.type === 'weight');
  if (weights.some(untouched)) {
    const edge = edgeOf((v) => paintedTextSize(p, 'weight', v, opts), 0.4, 1.35, 0.05);
    // ⚠ THE OPENING SHARE IS PER NUMBER STYLE (Ryan 2026-07-28) — 90% for the ruled banner, 95% for
    // the "45LB" one-liner, the house 75% for everything else. Their 100% is not the others' 100%:
    // `plain` and the stacked lockups are capped by the slot box or the ring band, the raw wall,
    // whereas these two are fitted to `inlineMaxW` at 0.88 of the box, so the side margin the others
    // buy with 75% is already built into what these call full size. See `weightSizePctFor`.
    //   `pct` still governs when the caller names one, so "Reset weight" and the seeding call in the
    // upload flow keep passing DEFAULT_SIZE_PCT and get the per-style value from here.
    if (edge > 0) {
      weights.filter(untouched).forEach((el) => {
        // `weightOpenPctFor` is the shared answer the DIAL's `defaultPct` also reads — see its
        // header. It carries the per-style share (75 / 90 / 95) and the cap that keeps a style
        // from being seeded past its own wall once the grip's factor is applied.
        const openPct = pct === DEFAULT_SIZE_PCT ? weightOpenPctFor(el.weightFormat || 'stacked-dash', product) : pct;
        setScale(el, ((openPct * numberEdgeFractionFor(product)) / 100) * edge);
      });
    }
  }
  const arcs = els.filter((e) => e.type === 'arctext' && String(e.text || '').trim());
  if (arcs.some(untouched)) {
    const edge = edgeOf((v) => paintedTextSize(p, 'arctext', v, opts), 0.4, 1, 0.05);
    if (edge > 0) arcs.filter(untouched).forEach((el) => setScale(el, (arcPct / 100) * edge));
  }
  return changed;
}

/**
 * The largest TOTAL outline thickness (summed across a piece's rings) whose ink still fits inside
 * the paintable band — the companion cap to the ring-band ceiling in `weightSizes`.
 *
 * The number cap makes the NUMBER always fit around its outlines. It cannot make the OUTLINES fit:
 * `emitStyledText` strokes at `2 × cumulative thickness` and a stroke is centred on its path, so the
 * ink grows outward by the full sum on every side. Four rings at the UI's old `OUTLINE_MAX` of 20 was 80u (that text ceiling is 5 since 2026-08-07)
 * of growth per side on a band with ~76.5u of usable width — no glyph size can rescue that, and the
 * number cap just shrinks the lettering toward its floor while the rings paint past the ring edge.
 * So thickness needs its own ceiling, and it belongs here with the geometry rather than as another
 * constant in the panel that would drift from it.
 *
 * `MIN_GLYPH_SHARE` is the judgement call: an outline is decoration around lettering, so at least
 * this much of the room stays lettering. At 0.3 a single ring still reaches the UI's own 20 (nothing
 * a buyer can do today gets tighter), and four rings settle at ~6 each — which is exactly where the
 * containment measurement put the last case that stayed inside the ring.
 *
 * @returns {number} max summed thickness in plate units (0 when there's nothing to measure)
 */
/** How much of the true wall a boosted one-liner may take. 0.85 is what the BUMPER already spends
 *  (its 0.88 × box works out at 0.85 of its own wall), so this is that face's own margin carried
 *  over rather than a new number — the side gap stays proportionally what Ryan approved in July. */
const INLINE_WALL_FILL = 0.95;
/** The aspect a "45LB" one-liner actually is — four characters on a line, about 4.5 wide to 1 cap
 *  tall. Nominal because the wall has to be known before the size is. */
const INLINE_NOMINAL_ASPECT = 4.5;
const MIN_GLYPH_SHARE = 0.3;
export function maxOutlineTotal(profile, opts = {}) {
  const p = profile || {};
  const zone = resolveZone(opts.product, zoneWeight(opts.product, p.weightValue, opts.weight), p.elements);
  const el = (p.elements || []).find((e) => e && (e.type === 'weight' || e.type === 'arctext'));
  if (!el) return 0;
  const theta = zone.angles && zone.angles[el.slot];
  if (theta == null) return 0;
  // Solved at aspect 1: the wall barely moves across the aspects a weight lockup actually takes
  // (measured 70.1–70.3u over the whole dial), and a thickness ceiling that shifted as you typed
  // would be worse than one that is a hair conservative.
  const wall = ringBandMaxW(1, zone, theta);
  if (!(wall > 0)) return 0;
  return Math.max(0, (wall * (1 - MIN_GLYPH_SHARE)) / 2);
}

/**
 * The width the LOGO actually paints at for a candidate `sizeScale` — the logo-side twin of
 * `paintedTextSize`, and the same reason (Ryan 2026-07-25: the dial climbs, the art doesn't).
 * The dial is a multiplier on the auto-fit, and `logoDrawSize` re-caps the result at the slot's
 * real room, so past that point every further click is a no-op the buyer can't see.
 *
 * Uses the slot's BASE angle rather than the balance-drifted one: the drift depends on the sizes
 * being computed, and this is a "would it move?" probe, not a layout pass — a degree or two of
 * anchor drift can't change whether the cap is binding.
 *
 * @param {object} profile
 * @param {number} sizeScale  the candidate dial value
 * @param {{product?: string, weight?: number}} [opts]
 * @returns {number} painted width in plate units (0 when there's no logo)
 */
export function paintedLogoWidth(profile, sizeScale, opts = {}) {
  const p = profile || {};
  const el = (p.elements || []).find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!el) return 0;
  const aspect = el.logo && el.logo.aspectRatio > 0 ? el.logo.aspectRatio : 1;
  // ── THE BIG PATHS ANSWER FOR THEMSELVES (Ryan 2026-07-29) ─────────────────────────────────────
  // This probe used to run the PLAIN calculation for every logo, including the two that are not placed
  // by it — and that is what made the Badge size dial lie rather than merely fail. `room · lscale`
  // climbs monotonically across the whole range and never reaches `capW`, so the readout moved, the
  // − and + stayed live, and `drawLogo`'s `big` branch rendered a box that had never heard of
  // `sizeScale`. The probe MUST measure the box the renderer will actually draw; that is the
  // invariant this function exists to hold, and it was quietly false for two of three placements.
  //
  //   · badge — now scales, so the probe reports the scaled width and the stepper's `edgeValue`
  //     locates 100% (the scale clamp at 1) and greys the + there without being told.
  //   · full face — the dumbbell disc INSCRIBES the whole plate; there is no room to grow into and no
  //     sensible smaller size, so `fullFaceLogoBox` ignores the dial and this reports a constant.
  //     `edgeValue` walks that flat answer all the way down, the readout settles on 100%, and BOTH
  //     buttons grey: a control that says "this is the limit, nothing to adjust" instead of one that
  //     moves a number and paints nothing. Not one pixel of the dumbbell family's render changes —
  //     deliberately, since Ryan asked for Badge, and shrinking a full-face logo to reveal plate
  //     underneath is a separate design question nobody has answered.
  if (el.big === true) {
    const b = el.bigFace === 'disc'
      ? fullFaceLogoBox(aspect, { ...el, sizeScale })
      : bigLogoImageBox(el.slot, aspect, { ...el, sizeScale });
    return b ? Math.round(b.w * 1000) / 1000 : 0;
  }
  const weight = zoneWeight(opts.product, p.weightValue, opts.weight);
  const zone = resolveZone(opts.product, weight, p.elements);
  const font = p.font || DEFAULT_FONT_ID;
  // Same size-match the renderer sets up: a plain logo beside a weight is sized off the weight's
  // lockup box, so the probe has to see it or it would measure a different logo entirely.
  let matchBox = null;
  const weightEl = (p.elements || []).find((e) => e && e.type === 'weight');
  // The factory scale mirrors the renderer exactly — see the note there on why the match reads the
  // weight at its default size. A probe that disagreed with the renderer would grey the button on
  // a wall the plate doesn't actually have.
  if (weightEl && el.big !== true) matchBox = weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, normUnit(p.unit));
  // The arc-text ceiling too — the probe MUST see it or the + button stays live at 105% while the
  // plate refuses to grow (Ryan 2026-07-25's "clicking + and the font hasn't increased once", in a
  // new costume). Same single implementation the renderer calls.
  /* ⛔ AT THE ANGLE THE MARK IS PAINTED AT (2026-09-26). This passed `null`, i.e. the slot's base
     angle, while `drawLogo` passes the balance-drifted one — and the stop depends on the angle
     (a drifted box meets the rim at a corner). On RW-1444-A the probe reported 63.1 at 100% while
     the plate painted 48.7. Same drift the arc ceiling already computes, same entry points; and the
     aspect the renderer draws (an added ring pads it), not the artwork's. */
  const allCaps = p.allCaps !== false;
  const arcClamp = sharedArcFonts(p.elements || [], font, allCaps, zone);
  const theta = pointAngle(el, computeBalanceDelta(p.elements || [], font, allCaps, arcClamp, zone, matchBox, { weight, unit: normUnit(p.unit) }), zoneForSlot(zone, el.slot));
  const drawnAspect = (el.logo.renderUrl && Number.isFinite(el.logo.renderAspect)) ? el.logo.renderAspect : aspect;
  const d = logoDrawSize({ ...el, sizeScale }, drawnAspect, zone, matchBox, theta, logoCeilingFor(p, el, opts), opts.legacy === true);
  return d ? Math.round(d.w * 1000) / 1000 : 0;
}

/** Map of sizeScale → the smallest fitted arc font among arcs sharing that scale (only
 *  groups with 2+ arcs). Arcs at the same sizeScale are "linked" — the top/bottom size
 *  chain (design-panel §2.7, default on) keeps their scale equal — so they paint at ONE
 *  size: each fits its own word, then the group clamps DOWN to the smallest, matching a
 *  long top word to a short bottom word exactly (Ryan 2026-07-22). Turning the chain off
 *  and setting different sizes splits the scales, so those arcs fit independently again. */
function sharedArcFonts(elements, font, allCaps, zone) {
  const solo = isSoloArc(elements);
  const byScale = new Map();
  (elements || []).forEach((e) => {
    if (!e || e.type !== 'arctext' || !String(e.text || '').trim()) return;
    const k = e.sizeScale ?? 1;
    if (!byScale.has(k)) byScale.set(k, []);
    byScale.get(k).push(arcFontSize(e, font, allCaps, solo, zone));
  });
  const out = new Map();
  byScale.forEach((arr, k) => { if (arr.length > 1) out.set(k, Math.min(...arr)); });
  return out;
}

/** Angular half-extent (deg) of an arc-text's RENDERED width — the same fit math
 *  `drawArcText` uses, so the estimate matches what actually paints. `fontOverride`
 *  carries the link-clamped size when the arc is size-matched to its sibling. */
function arcHalfExtentDeg(element, font, allCaps, fontOverride = null, solo = false, zone) {
  const raw = element && element.text ? element.text : '';
  const text = allCaps ? raw.toUpperCase() : raw;
  if (!text) return 0;
  const wr = fontWidthRatio(font);
  const fontSize = fontOverride != null ? fontOverride : arcFontSize(element, font, allCaps, solo, zone);
  const ls = arcLetterSpacing(element, zone, fontSize);
  const arcLen = text.length * (fontSize * wr + ls); // rendered length along the ring
  // Degrees per unit of arc length depend on the radius the word actually rides — the grip's
  // lettering lane is a different circle from the bumper's ring mid-line.
  return ((arcLen / (2 * Math.PI * arcLaneOf(zone, element && element.slot).r)) * 360) / 2;
}

// ── THE TEXT CAN CLAW BACK THE LOGO'S BONUS HEADROOM ─────────────────────────────────────────────
// Ryan 2026-07-29: *"if I increase the upper arch text so the words become much larger and things
// become a potential issue with them overlapping or underlapping (like words on top of the logo),
// then as we type in these troublesome words, they should force the logo to be smaller in 5%
// increments until it reaches 100% again, where this should be the upper limit of the safe zone."*
//
// That sentence contains the whole design, and the floor is the important half. 100% is defined as
// the edge of the safe zone, so **the text may only ever take back the 100→110% bonus** — never
// push a logo below 100%. Which means this can only ever UNDO a choice the buyer made past the
// safe line, and a design that never went above 100% is untouchable by it. That is what keeps the
// rule from fighting the arrangement rules underneath it.
//
// It is a CEILING, not a mutation. `sizeScale` keeps the buyer's 110%; the paint obeys the ceiling.
// Delete the long word and the logo springs back on its own, with nothing to restore — same
// reasoning as `contextual-contrast-is-a-view-not-a-mutation`.
//
// WHY ANGULAR AND NOT A BOX INTERSECTION: both pieces live in the same ring band, so they cannot
// pass each other radially — the only way they meet is around the ring. `arcHalfExtentDeg` already
// reports how far a word actually reaches from its centre (it grows with both the typed length and
// the arch size dial, which is exactly Ryan's two triggers), and the tangential projection below is
// the same `bw·|sin θ| + bh·|cos θ|` the balance code uses on a side piece. So the test is one
// subtraction in degrees against a clearance.
const MIN_ARC_GAP_DEG = 4; // white space a side logo keeps between itself and a word's last letter
const LOGO_CEILING_STEP = 0.05; // Ryan's "5% increments"
/**
 * Smallest angular gap (deg) between a side-slotted logo and any arc word it faces. Negative = they
 * overlap. `Infinity` when there is no arc text, or the logo is not at a side slot — nothing to hit.
 *
 * ⚠ DELIBERATELY ONE-WAY: the drift `theta` comes from `computeBalanceDelta`, which sizes the logo
 * to place it. If the ceiling fed back into that, the two would chase each other (a smaller logo
 * drifts further, which frees room, which permits a bigger logo…). The drift is computed from the
 * UNCLAMPED size and treated as fixed here — the ceiling only ever shrinks the art at a position
 * already chosen, which is the conservative direction: less ink at the same anchor.
 */
function arcGapDegFor(logoEl, box, theta, elements, zoneIn, font, allCaps, arcClamp) {
  if (!logoEl || logoEl.slot !== 'left' && logoEl.slot !== 'right') return Infinity;
  if (!box || !(box.w > 0)) return Infinity;
  // ON THE GRIP PLATE THERE IS NOTHING TO CLEAR. A rim arc and the mark under it are in the SAME
  // gap at different radii — they are separated radially by construction (`gripZone` hands each its
  // own lane), never angularly. Measuring an angular gap between two pieces that are stacked would
  // report a collision that cannot happen and shrink the logo for no reason.
  if (slotArc(zoneIn, logoEl.slot)) return Infinity;
  const zone = zoneForSlot(zoneIn, logoEl.slot);
  const t = (theta * Math.PI) / 180;
  const tang = box.w * Math.abs(Math.sin(t)) + box.h * Math.abs(Math.cos(t));
  const logoHalf = (Math.atan2(tang / 2, zone.anchorR) * 180) / Math.PI;
  const solo = isSoloArc(elements);
  let worst = Infinity;
  for (const e of elements || []) {
    if (!e || e.type !== 'arctext' || !String(e.text || '').trim()) continue;
    const centre = zone.angles[e.slot];
    if (centre == null) continue;
    const arcHalf = arcHalfExtentDeg(e, font, allCaps, arcClamp ? arcClamp.get(e.sizeScale ?? 1) ?? null : null, solo, zoneIn);
    // Angular separation of the two centres, taken the short way round the ring.
    /* ⛔ THIS WAS INVERTED (fixed 2026-09-26). `|((c − θ + 540) mod 360) − 180|` already IS the
       short-way separation; a second line then did `sep = 180 − sep`, which is only right at exactly
       90° apart — the one case a side logo facing a top/bottom word sits at before it drifts. On
       RW-1444-A the rams head had drifted to ~229°, 139° from "NCU RAMS", and this scored it 41°:
       a 40° OVERLAP that did not exist, which pinned the logo at 100%. */
    const sep = Math.abs(((centre - theta + 540) % 360) - 180);
    const gap = sep - logoHalf - arcHalf;
    if (gap < worst) worst = gap;
  }
  return worst;
}
/**
 * The largest scale on the 5% grid, in [1, LOGO_SCALE_MAX], at which this logo still clears the arc
 * text — i.e. how much of its bonus headroom the words have left it. Walks DOWN from the top so the
 * answer is always the most generous one that fits.
 *
 * Returns 1 when even 100% is crowded: the safe zone wins, and the arrangement rules
 * (`computeBalanceDelta`'s drift, the arc spans themselves) are what handle it from there.
 */
function logoTextCeiling(logoEl, aspect, theta, elements, zone, font, allCaps, arcClamp, matchBox) {
  if (!logoEl || (logoEl.slot !== 'left' && logoEl.slot !== 'right') || logoEl.big === true) return LOGO_SCALE_REACH;
  if (!(elements || []).some((e) => e && e.type === 'arctext' && String(e.text || '').trim())) return LOGO_SCALE_REACH;
  for (let s = LOGO_SCALE_REACH; s > 1; s -= LOGO_CEILING_STEP) {
    const v = Math.round(s * 1000) / 1000;
    const box = logoDrawSize({ ...logoEl, sizeScale: v }, aspect, zone, matchBox, theta);
    if (arcGapDegFor(logoEl, box, theta, elements, zone, font, allCaps, arcClamp) >= MIN_ARC_GAP_DEG) return v;
  }
  return 1;
}
/** Diagnostic twin of `logoCeilingFor`: the actual angular gap (deg) at a given scale, so a probe
 *  can report HOW MUCH clearance there is rather than only whether the ceiling bound. */
export function logoArcGapDeg(profile, sizeScale, opts = {}) {
  const p = profile || {};
  const elements = Array.isArray(p.elements) ? p.elements : [];
  const el = elements.find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!el || !el.logo) return Infinity;
  const weight = zoneWeight(opts.product, p.weightValue, opts.weight);
  const zone = resolveZone(opts.product, weight, p.elements);
  const font = p.font || DEFAULT_FONT_ID;
  const allCaps = p.allCaps !== false;
  const arcClamp = sharedArcFonts(elements, font, allCaps, zone);
  const aspect = (el.logo.renderUrl && Number.isFinite(el.logo.renderAspect)) ? el.logo.renderAspect : resolveLogoAspect(el.logo);
  let matchBox = null;
  const weightEl = elements.find((e) => e && e.type === 'weight');
  if (weightEl && el.big !== true) matchBox = weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, normUnit(p.unit));
  const theta = pointAngle(el, computeBalanceDelta(elements, font, allCaps, arcClamp, zone, matchBox, { weight, unit: normUnit(p.unit) }), zone);
  const box = logoDrawSize({ ...el, sizeScale }, aspect, zone, matchBox, theta);
  return arcGapDegFor(el, box, theta, elements, zone, font, allCaps, arcClamp);
}

/**
 * The arc-text ceiling for a profile's logo — the ONE entry point, so the renderer and the dial's
 * probe cannot disagree. A probe that computed its own answer would grey the + on a wall the plate
 * does not have (or worse, leave it live on one it does).
 *
 * @returns {number} a scale in [1, LOGO_SCALE_MAX]; LOGO_SCALE_MAX means "nothing in the way".
 */
export function logoCeilingFor(profile, element, opts = {}) {
  const p = profile || {};
  const elements = Array.isArray(p.elements) ? p.elements : [];
  const el = element || elements.find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!el || !el.logo) return LOGO_SCALE_REACH;
  const weight = zoneWeight(opts.product, p.weightValue, opts.weight);
  const zone = resolveZone(opts.product, weight, p.elements);
  const font = p.font || DEFAULT_FONT_ID;
  const allCaps = p.allCaps !== false;
  const arcClamp = sharedArcFonts(elements, font, allCaps, zone);
  const aspect = (el.logo.renderUrl && Number.isFinite(el.logo.renderAspect)) ? el.logo.renderAspect : resolveLogoAspect(el.logo);
  let matchBox = null;
  const weightEl = elements.find((e) => e && e.type === 'weight');
  if (weightEl && el.big !== true) matchBox = weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, normUnit(p.unit));
  const theta = pointAngle(el, computeBalanceDelta(elements, font, allCaps, arcClamp, zone, matchBox, { weight, unit: normUnit(p.unit) }), zone);
  return logoTextCeiling(el, aspect, theta, elements, zone, font, allCaps, arcClamp, matchBox);
}

/**
 * ── THE CAP'S OWN ANSWER TO "EVEN THE SIDES OUT" (Ryan 2026-08-03) ──────────────────────────────
 *
 * On a dumbbell / fixed-barbell face carrying arch text top AND bottom, the `left`/`right` pieces
 * sit dead centre and read closer to the LONGER arc. That is real clearance rather than an optical
 * effect: a longer word spans more degrees, so its ENDS run further down the sides and genuinely
 * squeeze the gap above a side piece. Measured on Signet at 50 lb, house font, "ARCADIA HIGH
 * SCHOOL" over "EST 1974": **57 units of white space to the top arch against 178 to the bottom.**
 *
 * ⛔ IT CANNOT BE A VERTICAL TRANSLATION, which is what the 2026-08-03 handoff planned. Measured
 * first: a side piece on this face already paints out to **r = 212–214 against the 216 print
 * margin**, because its box is fitted to the disc. Sliding it down moves ink straight through that
 * margin — the honest allowance is ~13 units against an imbalance of 121. A ROTATION spends nothing
 * radially: the piece keeps its radius exactly, and mirroring the two sides about the vertical axis
 * keeps the pair level, which is what reads as "evened out".
 *
 * ⚠ SO THE `fixedSlots` REFUSAL WAS RIGHT ABOUT THE GRIP AND WRONG ABOUT THIS FACE. Its reasoning —
 * *"a drifted piece would simply rotate out of its own field"* — is a claim about the RADIAL runs
 * `capZone` carves, and every one of them is rotationally invariant: `inner`/`outer` are radii, the
 * centre window is measured per-edge from those same radii, and a mirrored pair keeps its
 * separation. Nothing in the zone notices. The grip plate's refusal stands untouched, because there
 * the free angle really is missing: its slots are the gaps BETWEEN hand cutouts.
 *
 * ⛔ AND THE RING'S ARITHMETIC IS NOT TRANSPLANTABLE, which is why this is a solve rather than a
 * reuse. On an annulus every piece shares one band, so half the difference in angular half-extents
 * exactly halves the gap — measured here it lands the house font within 4 units and **overshoots
 * the widest face in the library (BBH Bartle) to +54 from −51, worse than doing nothing.** A cap's
 * side piece sits INBOARD of the lettering, so the distance it gains per degree is not the arc's.
 * Same goal as `computeBalanceDelta`'s paired branch, solved against this face's geometry:
 * bisect the drift until the two white gaps are equal.
 *
 * @returns {number} degrees; positive drifts the pair DOWNWARD (right anchor +, left anchor −).
 */
const CAP_DRIFT_MAX_DEG = 30;  // the ceiling the ring's paired branch already clamps to
const CAP_DRIFT_DEAD_U = 6;    // units of white space — under this the face already reads even
function capPairedDrift(elements, font, allCaps, arcClamp, zone, matchBox, ctx, diag = null) {
  const solo = isSoloArc(elements);
  const arcAt = (slot) => (elements || []).find(
    (e) => e && e.type === 'arctext' && e.slot === slot && String(e.text || '').trim(),
  );
  // TWO ARCHES OR NOTHING. "Balance between the two" is only defined when there are two; with one
  // arch the opposite reference would have to be the bare face, and how far a piece should swing
  // into empty room is a different question with a different answer (the ring trisects, which is a
  // look, not a clearance). A top-only arch keeps the placement it has today.
  const top = arcAt('top');
  const bot = arcAt('bottom');
  if (!top || !bot) return 0;

  const clampOf = (e) => (arcClamp ? arcClamp.get(e.sizeScale ?? 1) ?? null : null);
  /** The INNER end of an arc's lettering on one side — the corner of the last glyph a side piece
   *  actually crowds — as a point in centre-relative coordinates. Both arches are measured the same
   *  way, so whatever this model gets wrong about a glyph's own width cancels in the difference. */
  const inkEnd = (e, side) => {
    const over = clampOf(e);
    const size = over != null ? over : arcFontSize(e, font, allCaps, solo, zone);
    const lane = arcLaneOf(zone, e.slot);
    // `arcOuterRadius` is the line the type RIDES; the edge facing inward is half a cap height in.
    const rRide = arcOuterRadius(lane, size, font, e);
    const rIn = rRide - (size * fontCapFrac(font)) / 2;
    // ⚠ NOT `arcHalfExtentDeg`, AND BOTH DEPARTURES ARE MEASURED ONES. That helper is exact for the
    // face it was written for and wrong twice over here, in the same direction:
    //   · it converts to degrees at the LANE's nominal radius. On the bumper the type rides that
    //     line, so it is right; this face HANGS its lettering off the artwork limit, ~12% further
    //     out, where the same length subtends ~12% fewer degrees.
    //   · it multiplies the character COUNT by the face's AVERAGE advance. `strAdvanceFrac` — the
    //     per-string sum the weight lockup has always used — knows that "EST 1974" is mostly narrow
    //     glyphs, and the average over-states it by 12%.
    // Together they reported the long arch at 74° and the short one at 31°; measured off the raster
    // they are 64° and 23°, and this lands 64.8 and 23.4. Corrected HERE rather than in the helper
    // because every bumper drift is calibrated against the helper as it stands.
    const raw = allCaps ? String(e.text || '').toUpperCase() : String(e.text || '');
    const ls = arcLetterSpacing(e, zone, size);
    const inkLen = strAdvanceFrac(raw, font) * size + ls * Math.max(0, raw.length - 1);
    const half = rRide > 0 ? ((inkLen / (2 * Math.PI * rRide)) * 360) / 2 : 0;
    if (diag) diag.arcs[e.slot] = { size: +size.toFixed(1), rIn: +rIn.toFixed(1), half: +half.toFixed(1) };
    // Which end faces this side: the top arch runs 200°→340°, so its right-hand end is the LARGER
    // angle and the bottom's (20°→160°) is the smaller one.
    const dir = (e.slot === 'top' ? 1 : -1) * (side === 'right' ? 1 : -1);
    const a = ((zone.angles[e.slot] + dir * half) * Math.PI) / 180;
    return { x: rIn * Math.cos(a), y: rIn * Math.sin(a) };
  };

  /** The piece at a side slot, with the box it will actually paint.
   *  ⚠ MEASURED ONCE, AT THE UNDRIFTED ANGLE, and then held fixed through the solve — the same
   *  one-way discipline `arcGapDegFor` states: a box that changed with the drift would let the two
   *  chase each other. A logo's box does vary with theta, by a unit or two on this face. */
  const sideOf = (slot) => {
    const e = (elements || []).find(
      (x) => x && x.slot === slot && (x.type === 'logo' || x.type === 'weight') && x.big !== true,
    );
    if (!e) return null;
    const view = zoneForSlot(zone, slot);
    let aR = view.anchorR || 0;
    const base = zone.angles[slot];
    if (!(aR > 0) || !Number.isFinite(base) || Number.isFinite(e.angleDeg)) return null;
    let w = view.boxSide;
    let h = view.boxSide;
    if (e.type === 'logo' && e.logo) {
      const a = (e.logo.renderUrl && Number.isFinite(e.logo.renderAspect)) ? e.logo.renderAspect : resolveLogoAspect(e.logo);
      const d = logoDrawSize(e, a, zone, matchBox, base);
      if (d && d.w > 0) { w = d.w; h = d.h; }
    } else if (e.type === 'weight') {
      // The REAL lockup, not the slot square: this face's boxes are taste sizes deliberately set
      // above what the disc can hold, so the square would over-state a "50 / -LB-" by 3×.
      const lock = ctx
        ? weightLockupBox(e, ctx.weight, font, zone, ctx.unit)
        : (matchBox && matchBox.w > 0 ? matchBox : null);
      if (lock && lock.w > 0) { w = lock.w; h = lock.h; }
    }
    // ⛔ …AND THE RADIUS IT PAINTS AT, NOT THE ONE ITS FIELD IS CENTRED ON. Both painters push a
    // piece outward before drawing it, and the first cut of this solved against `view.anchorR`:
    // on Signet without a logo that is 111 while the number paints at 181, so the model believed it
    // had 47 units of room where the plate had none and drove the drift onto its 30° clamp. Same
    // helpers, same order as `drawWeight` — rim push first, then the hang.
    if (view.hangEdge > 0 && w > 0 && h > 0) aR = edgeHangRadius(w, h, base, view.hangEdge, aR);
    if (view.hangOuter > 0) {
      // The same radial reach `weightPaintAnchor` hangs on — see the note there.
      const t2 = (base * Math.PI) / 180;
      aR = Math.max(0, view.hangOuter - (Math.abs(Math.cos(t2)) * w + Math.abs(Math.sin(t2)) * h) / 2);
    }
    const limit = (view.band && view.band.outer > 0) ? view.band.outer : CAP_ART_R;
    return { aR, base, slot, w, h, limit };
  };

  /** White space between a side piece drifted by `d` and one arch end.
   *  The radius follows `capDriftPullIn` — a swung piece comes back in a couple of units to keep
   *  its corner inside the artwork, and a model that ignored that would solve for a position the
   *  painter is not going to use. */
  const gapAt = (s, pt, d) => {
    const theta = s.base + (s.slot === 'right' ? d : -d);
    const a = (theta * Math.PI) / 180;
    const r = d ? driftMarginRadius(s.w, s.h, theta, s.limit, s.aR) : s.aR;
    const cx = r * Math.cos(a);
    const cy = r * Math.sin(a);
    const dx = Math.max(0, Math.abs(pt.x - cx) - s.w / 2);
    const dy = Math.max(0, Math.abs(pt.y - cy) - s.h / 2);
    return Math.hypot(dx, dy);
  };

  const drifts = [];
  let worst = 0;
  for (const slot of ['left', 'right']) {
    const s = sideOf(slot);
    if (!s) continue;
    const pTop = inkEnd(top, slot);
    const pBot = inkEnd(bot, slot);
    // Monotone in `d`: drifting downward walks away from the top arch and toward the bottom one.
    const f = (d) => gapAt(s, pTop, d) - gapAt(s, pBot, d);
    const f0 = f(0);
    if (Math.abs(f0) > worst) worst = Math.abs(f0);
    let lo = -CAP_DRIFT_MAX_DEG;
    let hi = CAP_DRIFT_MAX_DEG;
    if (f(lo) >= 0) { drifts.push(lo); continue; }
    if (f(hi) <= 0) { drifts.push(hi); continue; }
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (f(mid) < 0) lo = mid; else hi = mid;
    }
    drifts.push((lo + hi) / 2);
    if (diag) {
      const d = drifts[drifts.length - 1];
      const v = zoneForSlot(zone, slot);
      diag.sides.push({
        slot, aR: +s.aR.toFixed(1), w: +s.w.toFixed(1), h: +s.h.toFixed(1),
        view: { hangOuter: v.hangOuter, hangEdge: v.hangEdge, followMark: v.followMark, boxSide: +v.boxSide.toFixed(1) },
        modelTop0: +gapAt(s, pTop, 0).toFixed(1), modelBot0: +gapAt(s, pBot, 0).toFixed(1),
        modelTopD: +gapAt(s, pTop, d).toFixed(1), modelBotD: +gapAt(s, pBot, d).toFixed(1),
        endTop: [+pTop.x.toFixed(1), +pTop.y.toFixed(1)], endBot: [+pBot.x.toFixed(1), +pBot.y.toFixed(1)],
        d: +d.toFixed(2),
      });
    }
  }
  if (!drifts.length || worst < CAP_DRIFT_DEAD_U) return 0;
  // ONE drift for the pair, because a level pair is what reads as evened out (the ring's paired
  // branch makes the same call for the same reason). Two side pieces of different shapes each want
  // a slightly different answer; the mean splits it and neither ends up crooked.
  return drifts.reduce((a, b) => a + b, 0) / drifts.length;
}

/**
 * Diagnostic twin of `capPairedDrift` — the same call the renderer makes, with the model's own
 * numbers handed back (each arch's measured ink end, each side piece's box, and the white space the
 * model believes it has before and after the drift). `tools/probe-cap-arch-balance.mjs` measures the
 * PIXELS; this says what the engine thought it was doing, and the two disagreeing is the bug report.
 */
export function capBalanceDiag(profile, opts = {}) {
  const p = profile || {};
  const elements = Array.isArray(p.elements) ? p.elements : [];
  const font = p.font || DEFAULT_FONT_ID;
  const allCaps = p.allCaps !== false;
  const weight = zoneWeight(opts.product, p.weightValue, opts.weight);
  const zone = resolveZone(opts.product, weight, elements);
  const arcClamp = sharedArcFonts(elements, font, allCaps, zone);
  const weightEl = elements.find((e) => e && e.type === 'weight');
  const matchBox = weightEl
    ? weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, normUnit(p.unit))
    : null;
  const diag = { sides: [], arcs: {} };
  const d = capPairedDrift(elements, font, allCaps, arcClamp, zone, matchBox, { weight, unit: normUnit(p.unit) }, diag);
  return { drift: +Number(d || 0).toFixed(2), ...diag };
}

/** The auto-balance drift (deg) applied to a right anchor (+) / left anchor (−).
 *  0 unless there's a top AND bottom arc-text of differing length AND a non-big
 *  left/right point anchor to move. */
const ZERO_DRIFT = Object.freeze({ left: 0, right: 0 });
function computeBalanceDelta(elements, font, allCaps, arcClamp = null, zone = ZONE_PLATE, matchBox = null, ctx = null) {
  // ⛔ NEVER ON A FIXED-SLOT FACE. The header above this section has said since 2026-07-15 that the
  // grip plate's three slots are gaps between hand cutouts with no free angle to drift INTO — but
  // nothing enforced it, and the rule held only because a grip zone has no `bottom` angle, so
  // `eBot` below was always undefined and every branch fell through to zero.
  //   That accident died the moment a grip layout could put arc text at `top` (2026-07-30): `eTop`
  // goes truthy, the one-end-occupied branch fires, and it swings BOTH weight numbers tens of
  // degrees off their gap centres — straight onto the hand holes. So the rule is now stated, in the
  // one place it has to hold, instead of being an emergent property of a missing table entry.
  //   The test was "has this zone no `bottom` angle?", which is true of the grip plate and was
  // therefore a workable stand-in for "are these slots fixed?" — right up to the dumbbell's cap
  // zone, which is every bit as fixed and DOES have a bottom. `fixedSlots` says the thing itself.
  //   ⚠ …AND ONE FACE OPTS BACK IN, FOR ONE BRANCH (Ryan 2026-08-03). `sideBalance: 'paired'` is
  // the cap's: see `capPairedDrift` for why a rotation is sound there and why the arithmetic below
  // is not. `fixedSlots` stays TRUE on that zone and still means what it says — the trisect branch
  // at the bottom of this function may never touch it.
  const fixed = zone && (zone.fixedSlots === true || (zone.angles && zone.angles.bottom == null));
  const paired = zone && zone.sideBalance === 'paired';
  if (fixed && !paired) return ZERO_DRIFT;
  const sideSlot = (e) => e && (e.slot === 'left' || e.slot === 'right') && (e.type === 'logo' || e.type === 'weight') && e.big !== true;
  const sides = elements.filter(sideSlot);
  if (!sides.length) return ZERO_DRIFT;
  // Use the link-clamped size so the drift reflects what actually paints (a size-matched
  // bottom word is narrower than its own solo fit).
  const fontFor = (e) => (arcClamp ? arcClamp.get(e.sizeScale ?? 1) ?? null : null);
  // WHAT OCCUPIES THE TOP AND BOTTOM SLOTS — text OR a logo OR a weight (Ryan 2026-07-25). This used
  // to look only for ARC TEXT, so the "Letterman" layout (logo top · weights left+right · arc text
  // bottom) read as having nothing up top and shoved both weights ~50° upward, into the logo. Any
  // piece parked at 12 or 6 o'clock bounds the gap the side pieces have to share, so all of them
  // count. Half-extent in DEGREES about the ring, so a wide logo pushes harder than a narrow number.
  const occupant = (slot) => elements.find((e) => e && e.slot === slot && e.big !== true
    && ((e.type === 'arctext' && String(e.text || '').trim()) || e.type === 'logo' || e.type === 'weight'));
  const halfDegOf = (e, slot) => {
    if (!e) return 0;
    if (e.type === 'arctext') return arcHalfExtentDeg(e, font, allCaps, fontFor(e), isSoloArc(elements), zone);
    // A logo/weight is a BOX on the ring: convert its drawn tangential width to an angle at the
    // anchor radius. Mirrors the renderer's own sizing — the annulus fit with the tangential floor,
    // never below the square slot box — closely enough to place a neighbour beside it.
    const aspect = (e.type === 'logo' && e.logo && e.logo.aspectRatio > 0) ? e.logo.aspectRatio : 1;
    const fit = slotAnnulusBox(zone, slot, aspect);
    const w = Math.max(fit ? fit.w * SLOT_TANGENTIAL_FILL : 0, zone.boxSide);
    return (Math.atan2(w / 2, zone.anchorR) * 180) / Math.PI;
  };
  // Angular half-width of a SIDE piece from its REAL drawn box, projected onto the tangential axis
  // at the angle it sits: an SVG box is axis-aligned while the radial direction rotates with the
  // drift, so the tangential extent is w·|sin θ| + h·|cos θ|.
  const sideHalfDeg = (e, thetaDeg) => {
    if (!e) return 0;
    let bw = zone.boxSide;
    let bh = zone.boxSide;
    if (e.type === 'logo' && e.logo) {
      const a = (e.logo.renderUrl && Number.isFinite(e.logo.renderAspect)) ? e.logo.renderAspect : resolveLogoAspect(e.logo);
      const d = logoDrawSize(e, a, zone, matchBox, thetaDeg);
      bw = d.w; bh = d.h;
    } else if (e.type === 'weight' && matchBox && matchBox.w > 0) {
      bw = matchBox.w; bh = matchBox.h; // the weight lockup's real box, not the slot square
    }
    const t = (thetaDeg * Math.PI) / 180;
    const tang = bw * Math.abs(Math.sin(t)) + bh * Math.abs(Math.cos(t));
    return (Math.atan2(tang / 2, zone.anchorR) * 180) / Math.PI;
  };
  const eTop = occupant('top');
  const eBot = occupant('bottom');
  if (!eTop && !eBot) return ZERO_DRIFT;
  // A `paired` face gets exactly the branch its name promises, and its own solve. Everything below
  // is the RING's — the trisect in particular would swing a cap's numbers ~43° into empty face on
  // nothing more than an emptied bottom line.
  if (paired) {
    if (!(eTop && eBot)) return ZERO_DRIFT;
    const d = capPairedDrift(elements, font, allCaps, arcClamp, zone, matchBox, ctx);
    return d ? { left: d, right: d } : ZERO_DRIFT;
  }
  if (eTop && eBot) {
    // Each side piece sits at the MIDPOINT of its gap, which is exactly half the difference between
    // what the two ends reach — the original rule, now fed by any occupant rather than text alone.
    const diff = halfDegOf(eTop, 'top') - halfDegOf(eBot, 'bottom');
    if (Math.abs(diff) < 3) return ZERO_DRIFT; // dead-zone: |top − bottom| < ~6°
    const d = Math.max(-30, Math.min(30, diff / 2)); // clamp so anchors never cross an occupied zone
    return { left: d, right: d };
  }
  // ── ONE end occupied, the other genuinely EMPTY (Ryan 2026-07-25) ───────────────────────────
  // Nothing bounds the far side, so the side pieces don't have a gap to centre in — they share the
  // whole free span with each other. Two of them divide it into THREE gaps:
  //   free = 360 − 2·half,  gap = free / 3, which lands on a clean  delta = 30 + half/3
  //   (40° half-extent → 43.3°, gaps of 93.3° all round; previously 50 / 180 / 50).
  // Signed so they always move AWAY from the occupied end: down for a top-only, up for bottom-only.
  const only = eTop || eBot;
  const half = halfDegOf(only, eTop ? 'top' : 'bottom');
  const away = only === eTop ? 1 : -1; // +ve angles run clockwise from 3 o'clock, so +ve = downward
  const hasLeft = sides.some((e) => e.slot === 'left');
  const hasRight = sides.some((e) => e.slot === 'right');
  if (hasLeft && hasRight) {
    // Two side pieces share the free span, and they are neighbours of each other.
    //   SYMMETRIC and measured CENTRE TO CENTRE (Ryan 2026-07-25). Both constraints hold at once
    // this way, which they cannot edge-to-edge: with one drift `d` the three gaps are
    //     text-end -> right centre : d + edge
    //     right centre -> left     : 180 - 2d
    //     left centre -> text-end  : d + edge      (equal to the first, by symmetry)
    // so setting them equal gives 3d = 180 - edge, i.e. d = 30 + half/3 — and every width term
    // CANCELS. The pieces stay level (one drift, mirrored) AND the gaps come out equal regardless of
    // the logo being 17.7° of ring against the number's 24.8°.
    //   An earlier pass measured edge-to-edge and subtracted a third of the pieces' half-width;
    // that forced a choice between level and even, and got both slightly wrong.
    //   EQUALISE THE WHITE SPACE, not the centre distances (Ryan 2026-07-25, measured off his own
    // reference dots). Centre-to-centre came out even — 72.3 / 73.6 / 74.3, spread 2° — and still
    // looked wrong, because the gap between the two PIECES loses both their widths while a gap to
    // the text loses one piece and one thin letter:
    //     arc 72.3 / 73.6 / 74.3   ->   white 55.9 / 52.4 / 61.5
    // The number is also fatter than the logo (12.4° of ring vs 8.9°), which is what makes
    // logo->text the roomiest of the three.
    //   The pieces must stay LEVEL, so there is one mirrored drift and the two outer white gaps
    // must differ by the width difference — that part is unavoidable. Aim the AVERAGE outer gap at
    // the middle one:  d + edge - h = 180 - 2d - 2h  ->  d = (180 - edge - h) / 3.
    //   `h` depends on the angle being solved for, so take one pass at h = 0 and re-measure there;
    // it converges immediately.
    //   …and then deliberately OVER-open the middle by MID_GAP_BIAS (Ryan 2026-07-25). Two heavy
    // pieces facing each other read tighter than a heavy piece facing thin lettering, even at
    // identical measured white space — the same reason typographers letterspace round shapes wider
    // than flat ones. Equal white space measured correct and still looked pinched, so the middle gap
    // is aimed a few percent wide to cancel the illusion.
    //   Solving  w_mid = k · w_outer  with  w_outer = d + edge − h  and  w_mid = 180 − 2d − 2h:
    //     d = (180 − k·edge − (2 − k)·h) / (k + 2)
    // At k = 1 this is the plain equal-white-space solution, so the bias is a single dial.
    const edge = 90 - half;
    const d0 = (180 - edge) / 3;
    const hAvg = (sideHalfDeg(sides.find((e) => e.slot === 'left'), 180 - d0)
      + sideHalfDeg(sides.find((e) => e.slot === 'right'), d0)) / 2;
    const k = MID_GAP_BIAS;
    const d = Math.max(0, Math.min(60, (180 - k * edge - (2 - k) * hAvg) / (k + 2)));
    return { left: away * d, right: away * d };
  }
  // Only ONE side piece. Trisecting is meaningless here and centring it opposite the words would
  // swing a 'left' piece all the way to 6 o'clock, which reads as a different layout rather than a
  // nudge. Fall back to the two-arc rule with the empty arch treated as a zero-width word — the
  // same "split the difference" Ryan described, against nothing.
  const d1 = away * Math.max(0, Math.min(30, half / 2));
  return { left: d1, right: d1 };
}

/** Absolute ring angle (deg) for a point element — manual override wins, else the
 *  slot base with the left/right auto-balance drift folded in.
 *
 *  A slot the ZONE DOESN'T DEFINE falls back to `top`. That case is the grip
 *  plate's missing `bottom` (screen 90 is its 6 o'clock hand cutout), and it
 *  should be unreachable: grip presets author only top/left/right, and switching
 *  products swaps in that family's own preset. If it ever IS reached, landing the
 *  piece on the top gap is wrong-but-visible, where the old `?? 0` would have put
 *  it at 3 o'clock — half onto a hand hole, and it'd read as intentional. */
function pointAngle(element, balanceDelta, zone = ZONE_PLATE) {
  if (Number.isFinite(element.angleDeg)) return element.angleDeg;
  // ⚠ THE SLOT'S OWN TABLE, NOT THE ZONE'S (2026-08-04). Every view spreads the base zone, so for
  // all but one slot in the app these are the same object — and the exception is the whole point:
  // `capZone`'s centre window flips `angles.center` to 90 to express a mark that sinks BELOW the
  // middle (`anchorR` is a magnitude and cannot carry the sign). Reading the base zone here meant
  // that flip was written and never read, so a negative rise sent the mark UP by exactly the amount
  // it should have come down. Nothing had reached it before: `rise` could only go negative once an
  // EMPTY end stopped being charged for a lettering lane it does not use, three lines of this
  // session ago. Same shape as `full-face`, which writes its angle onto the base *and* the view
  // precisely because of this.
  const angles = zoneForSlot(zone, element.slot).angles || zone.angles;
  const base = angles[element.slot] ?? angles.top ?? zone.angles.top;
  // `balanceDelta` is a PAIR (Ryan 2026-07-25) — the two side pieces can be different widths, so
  // they need independent drifts to sit on equal gaps. A bare number still works (both sides same).
  const d = (balanceDelta && typeof balanceDelta === 'object') ? balanceDelta : { left: balanceDelta || 0, right: balanceDelta || 0 };
  if (element.slot === 'right') return base + (d.right || 0);
  if (element.slot === 'left') return base - (d.left || 0);
  return base;
}

/**
 * WHERE THE WEIGHT NUMBER IS, for the camera to aim at (Ryan 2026-07-28: "it needs to know where the
 * weight number is on the weight so it zooms to that area centering it perfectly… since the weight
 * can often be displayed in different places, and it can also sometimes be displayed in 2 places on
 * the same weight, we need it to intelligently know where to zoom in, and zoom in on just one").
 *
 * Returned as a fraction of the plate RADIUS in 3D screen convention — x right, **y UP** — because
 * the caller is a camera, not a canvas. That flip is the one conversion this function exists to own:
 * the ring angles are authored in SVG's y-DOWN space (`PLATE_ANGLES`: bottom is +90°), so a consumer
 * doing its own trig would put every number on the wrong side of the plate and only notice on the
 * layouts where it matters.
 *
 * It reads the LIVE angle (`effectivePointAngle`), not the slot's nominal one, so auto-balance drift
 * — which moves a side piece by several degrees to even out the gaps — is included. Aiming at the
 * nominal angle would leave the number visibly off-centre at 220%.
 *
 * WHICH one, when a layout carries two (the mirrored left/right pair): a fixed slot preference, not
 * document order. The choice has to be STABLE across edits — this is called again on every click
 * while the buyer is tapping through number styles, and a rule that could change its mind would
 * swing the camera between the two numbers mid-session.
 *
 * @returns {{u:number, v:number, slot:string}|null} null when the design has no weight element.
 */
const WEIGHT_FOCUS_SLOT_ORDER = Object.freeze({ right: 0, left: 1, bottom: 2, top: 3 });
export function weightFocusPoint(profile, product) {
  const p = profile || {};
  const elements = Array.isArray(p.elements) ? p.elements : [];
  const weights = elements.filter((e) => e && e.type === 'weight' && e.slot);
  if (!weights.length) return null;
  const rank = (e) => WEIGHT_FOCUS_SLOT_ORDER[e.slot] ?? 9;
  let pick = weights[0];
  for (const e of weights) if (rank(e) < rank(pick)) pick = e;
  const weight = zoneWeight(product, p.weightValue, undefined);
  const zone = resolveZone(product, weight, p.elements);
  const a = (effectivePointAngle(p, pick, product) * Math.PI) / 180;
  // ⚠ WHERE IT PAINTS, not where its field is centred (2026-08-04). The slot's view was already an
  // improvement on the base zone — but `anchorR` is only the FIRST of the radial rules, and every
  // one after it moves the number: the rim push, the hang off the artwork limit, the arch-balance
  // drift's margin bill. On Crest the hang alone had put the camera 25 units off the number it was
  // asked to frame, on a 225-unit radius, ever since `hangOuter` shipped. `weightPaintAnchor` is
  // the painter's own answer, so the two cannot drift apart again.
  const font = p.font || DEFAULT_FONT_ID;
  const allCaps = p.allCaps !== false;
  const arcClamp = sharedArcFonts(elements, font, allCaps, zone);
  const matchBox = weightLockupBox({ ...pick, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, normUnit(p.unit));
  const delta = computeBalanceDelta(elements, font, allCaps, arcClamp, zone, matchBox, { weight, unit: normUnit(p.unit) });
  const at = weightPaintAnchor(pick, delta, zone, weight, font, normUnit(p.unit));
  /* ⛔ READ THE POINT, DO NOT REBUILD IT FROM ITS RADIUS AND ANGLE (Ryan 2026-08-09: *"this works
   * with left/right movement, but up or down and the camera doesn't budge to keep it centered"*).
   *
   * 🔑 THE SYMPTOM NAMES THE BUG EXACTLY. This used to take the radius from `weightPaintAnchor` and
   * then re-project it through `a`, the slot's own angle — so the camera could only ever aim at a
   * point ON THAT RAY. For a number in the right-hand slot `a` is 0°, which makes `v` identically
   * `-r·sin(0)` = 0: a vertical nudge moved the number and left the aim pinned to the horizontal
   * axis, while a horizontal one changed `r` and therefore appeared to work.
   *   The anchor already IS a point. `at.cx/cy` carry the rim push, the hang, the drift AND the
   * buyer's offset, so reading them directly is both simpler and the only version that can follow a
   * piece off its own ray. Byte-identical without a nudge — `at` is built as CENTER + r·(cos a,
   * sin a), so this recovers exactly the old numbers — which is why the 08-08 verification of this
   * function's aim still stands rather than needing to be redone. */
  const ru = (at.cx - CENTER) / PLATE_R;
  const rv = (at.cy - CENTER) / PLATE_R;
  // The lockup's OWN bounding box, so the camera can frame the number the way it frames the whole
  // product — filled to the same proportion, with the same air around it — instead of guessing a
  // magnification (Ryan 2026-07-28: "have it fill as much of the viewable window as possible while
  // maintaining the typical padding zones… but this time the padding isn't around the whole weight,
  // it's around the text/weight numbers"). Half-extents, in plate radii, to match `u`/`v`.
  const box = weightLockupBox(
    pick, p.weightValue ?? 45, p.font || DEFAULT_FONT_ID, zone, normUnit(p.unit),
  );
  return {
    u: ru,
    v: -rv,   // camera is y-up, the viewBox is y-down
    slot: pick.slot,
    fitU: box ? (box.w / 2) / PLATE_R : null,
    fitV: box ? (box.h / 2) / PLATE_R : null,
  };
}

/**
 * WHERE THE LOGO IS, same contract as `weightFocusPoint` — a centre and half-extents as fractions of
 * the plate radius, in camera convention (y up), or null when no logo has landed yet.
 * (Ryan 2026-07-28: "when on the logo tab, if someone adjusts the LOGO SIZE +/- selector, I want it
 * to zoom in on the logo in the same way we zoom in on the weight on the weight tab.")
 *
 * THREE PLACEMENTS, and it has to read the same helpers the renderer draws with or the camera will
 * aim confidently at the wrong place:
 *   · full-face (the dumbbell family's disc) — the logo IS the face. Reported at its true size, which
 *     means the fit asks for roughly the normal framing and the zoom is correctly almost nothing.
 *   · big — positioned by `bigLogoImageBox`, a box against the ring with no anchor point at all.
 *   · plain — anchored at its slot like the weight, drifting with the same auto-balance, and sized by
 *     `logoDrawSize` including the size-match to a sibling weight lockup.
 */
export function logoFocusPoint(profile, product) {
  const p = profile || {};
  const el = (p.elements || []).find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!el) return null;
  const weight = zoneWeight(product, p.weightValue, undefined);
  const zone = resolveZone(product, weight, p.elements);
  const font = p.font || DEFAULT_FONT_ID;
  const logo = el.logo || {};
  const aspect = (logo.renderUrl && Number.isFinite(logo.renderAspect))
    ? logo.renderAspect
    : resolveLogoAspect(logo);
  // From a box in viewBox units (origin top-left, y DOWN) to the camera's centre-origin, y-up frame.
  //   CLAMPED TO THE FACE, because a BIG logo's box is deliberately oversized and then clipped to
  // ring B — `bigLogoImageBox` for a left slot reports a box running from u −2.3 to +0.5, most of
  // which is off the plate entirely. Framing that would centre the camera on empty studio and pull
  // back to fit ink that is never drawn. The intersection with the plate is what the buyer can see,
  // so that is what gets framed. A plain logo sits inside the face already and passes through
  // untouched.
  /* ⛔ …AND "THE FACE" MEANS THE PAINTABLE SURFACE, NOT THE WHOLE PLATE (Ryan 2026-08-09: *"keep it
   * centered, but only within the paintable canvas area on the weight. So if only 10% of the logo is
   * still on the weight, center that 10%, not the other 90% that is hanging off it"*).
   *   The clamp above already framed the intersection rather than the box — the correction is which
   * boundary it intersects WITH. A badge is clipped to ring B (0.868R), so clamping at the plate's
   * own edge kept a rim of dead rubber in frame on every side; at the extremes Ryan is now able to
   * reach, that rim is a real share of what he is looking at. A full-face disc IS clipped at the
   * plate edge, so its limit stays 1 and nothing about that layout moves.
   *   ⚠ `limit` is a fraction of PLATE_R because `u`/`v` are, so the two cannot fall out of step. */
  const fromBox = (b, limit = 1) => {
    if (!b) return null;
    const lo = (v) => Math.max(-limit, v);
    const hi = (v) => Math.min(limit, v);
    const u0 = lo((b.x - CENTER) / PLATE_R);
    const u1 = hi((b.x + b.w - CENTER) / PLATE_R);
    const v0 = lo((b.y - CENTER) / PLATE_R);
    const v1 = hi((b.y + b.h - CENTER) / PLATE_R);
    if (!(u1 > u0) || !(v1 > v0)) return null; // entirely off the face — nothing to look at
    return {
      u: (u0 + u1) / 2,
      v: -((v0 + v1) / 2), // y-down in the viewBox, y-up for the camera
      fitU: (u1 - u0) / 2,
      fitV: (v1 - v0) / 2,
    };
  };

  if (el.big === true && el.bigFace === 'disc') return fromBox(fullFaceLogoBox(aspect, el));
  // `el` passed, not just its slot (Ryan 2026-07-29) — the badge box now moves with the size dial and
  // the nudge, and a close-up that framed the 100% box would drift off a shrunken or nudged mark. The
  // camera reads the same helper the renderer draws with, which is the contract this function opens
  // with; it would have been broken by the size change on its own.
  if (el.big === true) return fromBox(bigLogoImageBox(el.slot, aspect, el), RING_B_OUTER / PLATE_R);

  // Plain: the same anchor + size the renderer computes, drift and weight-match included.
  const allCaps = p.allCaps !== false;
  const weightEl = (p.elements || []).find((e) => e && e.type === 'weight');
  const matchBox = weightEl
    ? weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, normUnit(p.unit))
    : null;
  // The link-clamp + the denomination, so the drift here is the one that PAINTS. Both used to be
  // omitted, which cost nothing while the drift was a bumper-only affair sized off arc extents the
  // clamp barely moves — but a cap's drift is solved against the real lockup box, and a camera
  // aiming at an un-drifted anchor frames the wrong part of the face.
  const delta = computeBalanceDelta(
    p.elements || [], font, allCaps, sharedArcFonts(p.elements || [], font, allCaps, zone), zone, matchBox,
    { weight, unit: normUnit(p.unit) },
  );
  const theta = pointAngle(el, delta, zone);
  const { cx, cy } = pointAnchor(el, delta, zone);
  const d = logoDrawSize(el, aspect, zone, matchBox, theta);
  if (!d) return null;
  return fromBox({ x: cx - d.w / 2, y: cy - d.h / 2, w: d.w, h: d.h });
}

/**
 * WHERE THE ARCHED TEXT IS, same contract as `weightFocusPoint` — a fraction of the plate radius in
 * camera convention (y up), or null (Ryan 2026-07-28: "since text can be arched on either just top
 * or just bottom, it should zoom to just top or just bottom if the text is only in one of those
 * places. If text is on both top and bottom, then it should auto-zoom to just the top").
 *
 * TOP WINS when both are filled — Ryan's rule, and it is the right default anyway: the top arch is
 * the org name in every stock preset, so it is the line a buyer is judging a typeface by.
 *
 * EMPTY LINES DO NOT COUNT. An arctext element exists in the layout whether or not anything has been
 * typed into it, and an empty one renders nothing — aiming at it would zoom the camera onto a blank
 * stretch of ring. So the slot is chosen by what is actually SET, which is also what makes the
 * top-vs-bottom rule behave: type only in the bottom line and it goes there, exactly as asked.
 *
 * Unlike the weight there is no drift to account for: `pointAngle`'s auto-balance moves the LEFT and
 * RIGHT pieces to even out the gaps between them, and the arches are neither.
 */
export function arcTextFocusPoint(profile, product) {
  const p = profile || {};
  const elements = Array.isArray(p.elements) ? p.elements : [];
  const filled = (slot) => elements.some(
    (e) => e && e.type === 'arctext' && e.slot === slot && String(e.text || '').trim(),
  );
  // Top first, then bottom — Ryan's rule. `left`/`right` follow for the GRIP PLATE, whose three gaps
  // are top / lower-left / lower-right and which has no `bottom` at all (screen 90 is a hand hole).
  // Without them a grip design whose only words sit in the two lower gaps returned null and the
  // "zoom to the arch" close-up silently never fired.
  const slot = ['top', 'bottom', 'left', 'right'].find(filled) || null;
  if (!slot) return null;
  const zone = resolveZone(product, zoneWeight(product, p.weightValue, undefined), p.elements);
  const view = zoneForSlot(zone, slot);
  const a = ((view.angles[slot] ?? view.angles.top) * Math.PI) / 180;
  const lane = arcLaneOf(zone, slot);
  const r = lane.r / PLATE_R;
  // HOW MUCH ROOM THE ARCH NEEDS, so the camera can work out how close it may come. An arch is not a
  // point: a solo line is fitted to a 170° sweep (`TOP_ARC_SOLO`), which at this radius is a chord
  // wider than the plate's own radius — zoom in as hard as the weight number allows and the ends of
  // the word leave the frame, which is what the first pass at a flat 170% did.
  //   Measured from the SPAN, not from the string. `fitArcFont` shrinks the type to fit that sweep,
  // so the sweep is the true worst case — and a span-based number holds still while the buyer taps
  // through typefaces, where a string-based one would breathe in and out as each font's glyph widths
  // changed the arc it happens to occupy.
  const solo = elements.filter((e) => e && e.type === 'arctext' && String(e.text || '').trim()).length === 1;
  const span = arcSpanFor({ slot }, solo, zone);
  const half = (Math.abs(span.end - span.start) / 2) * (Math.PI / 180);
  // Half the LANE's width, as the allowance for glyph height either side of the anchor circle. On a
  // grip plate that lane is a third of one gap's band, not the bumper's whole ring — using the ring
  // here would frame a box several times taller than the word and never zoom in properly.
  const glyph = (lane.band / 2) / PLATE_R;
  // THE ARCH'S BOUNDING BOX, and the aim is its CENTRE — not the apex. An arch is a crescent: the
  // ends of a 170° sweep sit most of a radius below the top of it, so aiming at the apex leaves the
  // word hanging in the bottom of the frame and forces the camera further back to keep the ends in.
  // Framing the box centre is what lets it fill the window (Ryan 2026-07-28).
  const sin = Math.sin(half);
  const cos = Math.cos(half);
  const near = r * cos - glyph;        // the ends, nearest the plate's middle
  const far = r + glyph;               // the apex, out at the rim
  const mid = (near + far) / 2;
  // ── AIMED ALONG THE SLOT'S OWN DIRECTION, not decomposed as "x from the angle, y from the radius".
  // The previous form (`u: r·cos a`, `v: ±mid`) is only correct where cos a is 0 — which every
  // bumper arch is, being dead top or dead bottom, so it was never wrong there. The grip plate's
  // lower gaps sit at 30° and 150°, where that form would put the aim point out at ~1.3× the lane
  // radius, off the plate entirely. Both expressions agree exactly at the cardinals, so the bumper
  // is unmoved.
  const cosA = Math.cos(a);
  const sinA = Math.sin(a);
  const tang = r * sin + glyph;        // half-extent ALONG the arc
  const radial = (far - near) / 2;     // half-extent ACROSS it
  return {
    u: mid * cosA,
    v: -mid * sinA,                    // camera convention is y-UP; the angles are authored y-DOWN
    slot,
    // The frame is axis-aligned while the word's box is not, so each half-extent picks up a share of
    // both. Reduces to (tang, radial) at top and bottom, again leaving the bumper untouched.
    fitU: radial * Math.abs(cosA) + tang * Math.abs(sinA),
    fitV: radial * Math.abs(sinA) + tang * Math.abs(cosA),
  };
}

/**
 * HOW FAR OUT A PIECE OF THIS SIZE CAN SIT AND STILL KEEP ITS CORNERS INSIDE `limit`.
 *
 * A piece is sized against the fit at the anchor its zone gave it, and then usually paints SMALLER
 * than that fit — the dial opens at a share of the room, and the legibility floors shape the rest.
 * That leaves slack: the piece could sit further out than it does and still clear the edge. On a
 * ring face nobody notices, because the band is narrow and the anchor is the band's middle. On a
 * DISC it is glaring — Four Corners' logos sat 48u inside the artwork limit and its numbers 105u,
 * with a wide bare rim around them (Ryan 2026-07-30: *"push the logos and weight numbers a little
 * closer to the edge"*).
 *
 * The binding constraint is the FAR CORNERS, not the far edge: at a top slot the box's corners are
 * at `(±w/2, a + h/2)`, so `a ≤ √(limit² − (w/2)²) − h/2`. At a side slot the roles swap. Solving it
 * exactly is what lets a piece move out without giving up a single unit of size — which is the whole
 * point, since shrinking it to reach the rim would defeat the request.
 *
 * @returns {number} the largest anchor radius, or the current one when the piece cannot move out.
 */
/**
 * ⚠ EXACT IN THE ANGLE, AND THAT IS WHAT MAKES AN INWARD ANSWER MEANINGFUL (2026-08-04).
 *
 * The first form picked `along`/`perp` by quadrant — right on a cardinal, and blind to everything
 * between two of them, so it returned the SAME radius at 0° and at 18°. A drifted piece keeps an
 * axis-aligned box while its anchor swings off-axis, and its outer corner reaches further out the
 * further it swings: measured, the cap's arch-balance drift walked a weight number's ink from 215
 * to 218 through a 216 print margin, silently.
 *
 * Solving the corner exactly is one quadratic. The box's outer corner sits at
 * `(R·cos t + w/2, R·sin t + h/2)` (absolute values — the far corner is whichever one that is), so
 *     R² + R·(w·|cos t| + h·|sin t|) + (w² + h²)/4 − limit² = 0
 * and the positive root is the largest anchor radius that keeps every corner inside `limit`.
 *
 * ⚠ IT AGREES WITH THE OLD FORM AT EVERY CARDINAL, exactly — at t = 0 it collapses to
 * `√(limit² − h²/4) − w/2`, which is what the quadrant version computed. So no undrifted piece
 * anywhere in the app moves; the answer only differs where the old one was answering a question it
 * could not see.
 *
 * ⚠ TWO POLICIES READ IT, AND THEY WANT OPPOSITE SIGNS — so the solver is one function and each
 * policy is its own named wrapper. `edgeHangRadius` may only ever move a piece OUT (its historic
 * rule, and what keeps Four Corners exactly where Ryan signed it off); `driftMarginRadius` may only
 * ever move one IN, which is the bill a rotation runs up. Both at a cardinal are no-ops.
 *
 * @returns {number|null} the anchor radius at which this box's corners sit ON `limit`.
 */
function cornerFitRadius(w, h, thetaDeg, limit) {
  if (!(w > 0) || !(h > 0) || !(limit > 0)) return null;
  const t = (thetaDeg * Math.PI) / 180;
  const b = w * Math.abs(Math.cos(t)) + h * Math.abs(Math.sin(t));
  const disc = b * b - 4 * ((w * w + h * h) / 4 - limit * limit);
  if (!(disc > 0)) return null;
  const r = (-b + Math.sqrt(disc)) / 2;
  return r > 0 ? r : null;
}

/** ⛔ NEVER INWARD. This is permission to use room the piece already had, not a re-placement: a
 *  piece whose corners are already at the limit stays exactly where its zone put it. */
function edgeHangRadius(w, h, thetaDeg, limit, current) {
  const r = cornerFitRadius(w, h, thetaDeg, limit);
  return r == null ? current : Math.max(current, r);
}

/** ⛔ NEVER OUTWARD — the mirror rule, for the one thing that can move a piece off its own axis.
 *  A drifted piece keeps an axis-aligned box while its anchor swings, so its outer corner reaches
 *  further out than it did on the cardinal: measured, the cap's arch-balance drift walked a weight
 *  number's ink from 215 to 218 through a 216 print margin. This gives that back — a couple of
 *  units of radius, invisible next to the swing itself, and the margin is a manufacturing promise. */
function driftMarginRadius(w, h, thetaDeg, limit, current) {
  const r = cornerFitRadius(w, h, thetaDeg, limit);
  return r == null ? current : Math.min(current, r);
}

/**
 * The radius a side piece must come back to, if the arch-balance drift has swung its corner past the
 * artwork limit. Returns `current` untouched for everything else — a piece on its own axis, a face
 * with no drift, a product that is not a cap.
 *
 * ⚠ MEASURED AGAINST THE ARTWORK LIMIT (`band.outer`), NOT THE TIGHTER KEEP-CLEAR that `hangEdge`
 * reserves. The keep-clear is a target for a piece's outer EDGE and this is a bound on its CORNER;
 * holding a corner to an edge target would undo the hang entirely — on Signet it would pull a
 * rim-flush number 9 units back off the rim to protect space nothing is printed in. The promise
 * that has to hold is the 4% print margin, and this is exactly that promise.
 */
function capDriftPullIn(zone, element, view, thetaDeg, box, current) {
  if (!zone || zone.sideBalance !== 'paired' || !box || !(box.w > 0) || !(current > 0)) return current;
  if (!element || (element.slot !== 'left' && element.slot !== 'right')) return current;
  const base = (view && view.angles ? view.angles : zone.angles)[element.slot];
  if (!Number.isFinite(base) || Math.abs(thetaDeg - base) < 0.01) return current;
  const limit = (view && view.band && view.band.outer > 0) ? view.band.outer : CAP_ART_R;
  return driftMarginRadius(box.w, box.h, thetaDeg, limit, current);
}

/**
 * HOW FAR INWARD A MARK MUST SLIDE so its outer CORNERS and its inner mid-edge have equal clearance.
 *
 * On a round face a wide mark's outer corners reach further out than its top edge does: the mid-top
 * can look comfortably inside while the two corners are about to leave. `slotAnnulusBox` solves the
 * same triangle when SIZING, but applies it as a floor/ceiling and deliberately lets corners
 * overhang the square box — so the placement has to finish the job.
 *
 * BALANCE THE CLEARANCES, don't merely avoid a breach. Measured on the Blue Sky wordmark (~4.3:1,
 * drawn ≈112×26 at anchorR 155.0 with the slot spanning 121.8→188.3): the outer CORNERS had 10.9 of
 * clearance while the inner edge had 20.1. Nothing was clipping, so an "only move it if the corners
 * cross Ro" test correctly did nothing, and the mark sat visibly lopsided with its corners nearly out
 * while the room below went unused. The right target is EQUAL clearance at the two places that run
 * out first: the outer corners, and the inner mid-edge.
 *     Ro − √(hw² + (Rc + hh)²)  =  (Rc − hh) − Ri
 * Squaring and using (Rc+hh)² − (Rc−hh)² = 2·drawH·Rc collapses to a closed form, no solver:
 *     Rc = (S² + S·drawH − hw²) / (2(drawH + S)),  S = Ro + Ri = 2·anchorR
 * Self-checking: at hw → 0 it returns exactly anchorR, and every positive width pulls it INWARD, so
 * this can never push a mark outward. A near-square logo moves a fraction of a unit; the wordmark
 * above moves ~4.8, landing at 15.4 / 15.3 clearance.
 *
 * ⚠ FACTORED OUT BECAUSE A SECOND CALLER NEEDS THE SAME ANSWER (2026-07-30). A number that follows
 * its mark has to know where the mark ACTUALLY lands, and this shift is the difference between the
 * anchor its zone gave it and the place it is painted. Measuring against the un-shifted anchor made
 * the "constant" gap drift by 11u across the size dial — the mark was sliding out from under the
 * number. One implementation, two readers, no possibility of disagreement.
 *
 * @returns {number} the (non-positive) radial shift to apply along the slot's own direction.
 */
function cornerBalanceShift(aR, drawW, drawH, boxSide) {
  if (!(aR > 0) || !(drawW > 0) || !(drawH > 0)) return 0;
  const Ro = aR + boxSide / 2; // the annulus the mark has to stay inside
  const Ri = aR - boxSide / 2;
  const hw = drawW / 2;
  const hh = drawH / 2;
  const S = Ro + Ri;
  const Rc = (S * S + S * drawH - hw * hw) / (2 * (drawH + S));
  // Never outward, and never so far in that the inner edge drops into the hub.
  const target = Math.max(Math.min(Rc, aR), Ri + hh);
  return target - aR; // ≤ 0 — inward only
}

/** Point anchor (cx,cy) at a ring angle, on the zone's anchor circle. */
function anchorAtAngle(angleDeg, zone = ZONE_PLATE) {
  const a = (angleDeg * Math.PI) / 180;
  return { cx: CENTER + zone.anchorR * Math.cos(a), cy: CENTER + zone.anchorR * Math.sin(a) };
}

/** Anchor for a point element, honoring override + auto-balance. Resolved through the SLOT's view of
 *  the zone, so a mark sharing its gap with a rim arc sits on the reduced inner field's centre line
 *  rather than the whole band's — otherwise it would ride out under the lettering. */
function pointAnchor(element, balanceDelta, zone = ZONE_PLATE) {
  return anchorAtAngle(pointAngle(element, balanceDelta || 0, zone), zoneForSlot(zone, element && element.slot));
}

/** The current EFFECTIVE ring angle (deg) of a point element in a profile, drift
 *  included — so the Advanced angle stepper (§2.4) can start where the piece
 *  actually is, even while auto-balance is steering it.
 *  @param {string} [product] model id ('plate' | 'grip' | 'dumbbell' | 'barbell') */
export function effectivePointAngle(profile, element, product) {
  const p = profile || {};
  const elements = Array.isArray(p.elements) ? p.elements : [];
  const font = p.font || DEFAULT_FONT_ID;
  const allCaps = p.allCaps !== false;
  const weight = zoneWeight(product, p.weightValue, undefined);
  const zone = resolveZone(product, weight, p.elements);
  // Fed the same clamp, lockup and denomination the paint uses — on a cap the drift is SOLVED from
  // the real boxes, so a thinner call here would answer a different question than the plate shows
  // and the Advanced stepper would open on a number the piece is not standing at.
  const weightEl = elements.find((e) => e && e.type === 'weight');
  const matchBox = weightEl
    ? weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, normUnit(p.unit))
    : null;
  const delta = computeBalanceDelta(
    elements, font, allCaps, sharedArcFonts(elements, font, allCaps, zone), zone, matchBox,
    { weight, unit: normUnit(p.unit) },
  );
  return pointAngle(element, delta, zone);
}

/** Base ring angle (deg) for a slot — exported so the panel can bound the stepper.
 *  @param {string} [product] model id; the zone decides where a slot actually sits.
 *  @param {number} [weight] denomination — only the grip's zone varies by it. */
/**
 * WHICH SLOTS THIS PRODUCT ACTUALLY HAS, in the order they read down the face.
 *
 * `design-profile.js`'s `SLOTS` is the BUMPER's four and is what the Advanced position ring, the
 * "every slot is full" test and `freeSlot()` all iterated — so on a grip plate they offered
 * `bottom`, which is not an empty slot but a hand cutout. Nothing caught it because `pointAngle`
 * quietly falls a stray `bottom` back to `top`, so the piece landed in the wrong gap rather than
 * off the plate (2026-07-30).
 *
 * The zone is the only thing that knows, which is exactly why the answer comes from here.
 */
export function productSlots(product, weight = 45) {
  return Object.keys(resolveZone(product, weight).angles);
}

export function slotBaseAngle(slot, product, weight = 45) {
  const zone = resolveZone(product, weight);
  return zone.angles[slot] ?? zone.angles.top;
}

// ── big-logo placement ───────────────────────────────────────────────────────
/**
 * A BIG LOGO IS SIZED BY THE DIAL TOO (Ryan 2026-07-29: "the size toggle doesn't work for the logos
 * on the BADGE layout types. It should…").
 *
 * It never was, and the failure mode was worse than a dead control: `drawLogo` branches on `big` and
 * places the mark with `bigLogoImageBox`, which was a pure function of slot and aspect — `sizeScale`
 * did not enter it. Meanwhile the greying probe (`paintedLogoWidth`) went down the PLAIN path, where
 * `room · lscale` climbs across the whole range, so the readout moved, the −/+ never greyed, and the
 * render was byte-identical. A control that responds and lies. Same species as the close-up floor in
 * 152 — the value was computed and then the branch that would have used it was never taken.
 *
 * ⚠ THE SCALE HOLDS THE ANCHOR (Ryan's call, asked explicitly). Each of these boxes is an ANCHOR plus
 * a fill rule, and the anchor is the whole look: "Big Badge Rise" and "Crown" are named for the edge
 * that sits flush with the rim. Scaling about the box CENTRE would pull the mark off that rim and read
 * as "it moved and shrank" — the layouts stop looking like themselves at anything under 100%. So the
 * fill rule shrinks and the anchor is re-applied, which costs nothing: the placement expressions below
 * are already written as anchor-minus-size, so scaling w/h BEFORE they run is the entire mechanism.
 * Where the room opens up is the hub side, which is not paintable anyway.
 *
 * ⚠ AND THERE IS NO HEADROOM ABOVE 100%, unlike the plain path. There the dial multiplies
 * `capW · LOGO_ROOM_FRACTION`, so 0.9 → 1.1 has ~11% left to give. A badge box is already sized to
 * the plate's own limit (`wMax` / `fitHeight`), so the scale is clamped at 1 and the dial only goes
 * down. Nothing extra is needed to express that: `paintedLogoWidth` reports the clamped width, the
 * stepper's `edgeValue` scans down from the top, finds where the paint stops changing, and greys the
 * + at 100% by itself.
 *
 * Draw box for a big logo, sized to the aspect and positioned by `slot`, then
 * CLIPPED to ring B by the caller (the only paintable surface). Width/height keep
 * the logo aspect (no distortion). Returns the <image> box {x,y,w,h}.
 *
 *  • left / right  — HORIZONTAL: height fills the whole ring (top→bottom, flush),
 *    the inner (toward-center) edge reaches BIG_HUB_CROSS across the steel hub;
 *    the weight sits in its normal slot on the far side.
 *  • top / bottom  — VERTICAL, centered: the FAR side sits flush with the ring
 *    edge (top of ring for 'top', bottom for 'bottom'); the NEAR side reaches the
 *    INNER edge of the paintable ring (≈½" past the steel hub, across ring C);
 *    the weight sits top/bottom-center on the far end.
 *
 * @param {string} slot
 * @param {number} aspect
 * @param {object} [element] the logo element — read for `sizeScale` and the nudge. Omitted (or a
 *   bare object) gives the full-size, un-nudged box, which is what the 100% geometry tests want.
 */
function bigLogoImageBox(slot, aspect, element = null) {
  const a = aspect > 0 ? aspect : 1;
  // Clamped at `BIG_LOGO_SCALE_MAX` (1 until 2026-08-09 — see that constant for why a big mark can
  // take a ceiling a slot logo cannot). LOGO_SCALE_MIN is the same floor the plain path uses, so the
  // two dials bottom out together rather than at two different "smallest".
  const sRaw = Number(element && element.sizeScale);
  const s = Number.isFinite(sRaw) ? Math.min(BIG_LOGO_SCALE_MAX, Math.max(LOGO_SCALE_MIN, sRaw)) : 1;
  // The nudge is applied to the FINISHED box, after the anchor has been honoured — it is an offset
  // from where the layout puts the mark, not an input to the placement rule.
  const du = clampBigNudge(element && element.nudgeU) * PLATE_R;
  const dv = clampBigNudge(element && element.nudgeV) * PLATE_R;
  const placed = (b) => keepMarkOnSurface({ x: b.x + du, y: b.y + dv, w: b.w, h: b.h }, RING_B_OUTER, du, dv);
  const outer = RING_B_OUTER + BIG_BLEED; // flush ring edge (+ any overshoot)
  /* ── DEAD CENTRE, AS BIG AS THE SURFACE ALLOWS — "Biggest Badge" (Ryan 2026-08-10) ─────────────
   * *"the logo dead center, with its upper edge of the logo on the upper edge of the customizable
   * surface. Same on the right edge, bottom edge, and left edge. So it's big and smack dab in the
   * middle of the plate… good for folks who are struggling to envision that if they pick the big
   * badge, they can just nudge their logo to center, but until they click on this layout style they
   * won't know it's possible."*
   *
   * 🔑 SO THE SPEC IS ALREADY REACHABLE AND THAT IS THE POINT — this is the nudge-to-centre result
   * promoted to a preset, not a new look. Which also settles the geometry: Big Badge's box is
   * already the full ring height (`h = 2 * outer`), so centring it is the whole of the change.
   *
   * ⚠ A BOX CANNOT TOUCH ALL FOUR EDGES UNLESS IT IS SQUARE, so "same on every edge" resolves to
   * the largest CENTRED box of the artwork's aspect inside the printable square — it touches top and
   * bottom when the mark is tall, left and right when it is wide, and all four when it is square.
   * That is what a buyer means by "as big as it goes, in the middle", and it is the same
   * width-versus-height choice the top/bottom branch below already makes; the only difference is
   * that here both limits are the same number, so the test collapses to `a > 1`.
   *
   * ⛔ NO TALL-LOGO BLEND HERE, deliberately. That blend exists because a left/right box runs the
   * full vertical diameter while pinned OFF-CENTRE at the hub edge, so out at ±195 the circle has no
   * width left and the mark's ends are eaten. Centred, the widest part of the circle is exactly where
   * the mark is, and a narrow tall box clears it comfortably. Applying the blend anyway would shrink
   * a tall logo for a problem this slot does not have.
   */
  if (slot === 'center') {
    const span = 2 * outer;                       // the printable ring's own diameter, both axes
    const h = (a > 1 ? span / a : span) * s;
    const w = (a > 1 ? span : span * a) * s;
    return placed({ x: CENTER - w / 2, y: CENTER - h / 2, w, h });
  }
  if (slot === 'top' || slot === 'bottom') {
    // ── A WIDE LOGO IS FITTED BY ITS WIDTH INSTEAD (Ryan 2026-07-28) ──────────────────────────
    // "on Big Badge Rise and Big Badge Crown, for logos wider than they are tall, should we fit the
    // logo width-wise? That way we increase the chance of the full thing being visible."
    //
    // The default fit here is HEIGHT-driven — the box spans ring edge → paintable inner edge (310 of
    // a 450px plate) and the width simply follows the aspect. That is right up to a point and then
    // fails outright: a 2:1 logo asks for 620px of width on a 450px plate, so a third of it is off
    // the disc before ring-B clipping is even considered.
    //
    // THE THRESHOLD IS NOT A TASTE CALL. Width passes the paintable ring's own diameter (2 × 195.3 =
    // 391) exactly when a > 391/310 ≈ 1.26, near enough 5:4 — so that is the trigger, and it needs no
    // constant of its own: the comparison below IS the definition.
    //
    // ⚠ THE FAR EDGE STAYS FLUSH WITH THE RIM (Ryan's call, asked explicitly). A rectangle cannot be
    // both flush to a circle's rim and wholly inside it — the far corners always cross. So this stops
    // a wide logo running off the PLATE; it does not promise every pixel survives the ring-B clip.
    // Insetting from the rim would save those corners and cost the badge its flush top edge, which is
    // the whole look of these two layouts.
    const near = RING_B_INNER; // reach the paintable ring's inner edge (≈½" past the metal hub)
    const fill = outer + near; // ring edge (far) → paintable-ring inner edge (near)
    const wMax = 2 * outer;    // the paintable ring's own diameter — the widest the plate can show
    const wide = fill * a > wMax;
    // `s` shrinks the fill rule; the anchor below is then re-applied to the smaller box, so the flush
    // rim edge does not move. Horizontally the box stays centred on the plate, which is this slot's
    // other half of the anchor.
    const h = (wide ? wMax / a : fill) * s;
    const w = (wide ? wMax : fill * a) * s;
    // Anchored by the FAR edge either way, so a width-fitted logo keeps the flush rim its layout is
    // named for and gives its room back on the hub side, where the ring is not paintable anyway.
    const y = slot === 'bottom' ? CENTER + outer - h : CENTER - outer;
    return placed({ x: CENTER - w / 2, y, w, h });
  }
  const cross = (2 * BIG_HUB_CROSS - 1) * HUB_R; // 1.0 ⇒ +HUB_R (the far hub edge)
  // ── AND THE MIRROR, FOR A TALL LOGO ON BIG BADGE / BIG BADGE FLIP (Ryan 2026-07-28) ──────────
  // "I feel like there's an opposite of this we need on 2 of the big badge layouts for logos taller
  // than they are wide — just not sure what that looks like exactly."
  //
  // It is these two, and the literal mirror is a no-op: the cross-axis cap for a left/right logo
  // would be `h ≤ 2 × outer`, which is already exactly what it takes. The real failure is different.
  // A tall logo is NARROW, so it never overflows sideways — instead its top and bottom are eaten,
  // because this box runs the full vertical diameter while sitting OFF-CENTRE (pinned at the far hub
  // edge), and out at ±195 from the middle the circle has no width left to give.
  //
  // So the fix is to stop filling the full ring height and take the height the circle actually has at
  // this box's outermost edge. `fitHeight` solves that exactly — the largest box of this aspect whose
  // outer corners land on the rim rather than past it.
  //
  // ⚠ EASED IN FROM 1:1 RATHER THAN SWITCHED ON AT IT. Applied strictly below square, a 0.99 logo
  // would render a third shorter than a 1.00 one — two logos a buyer would call the same shape,
  // looking nothing alike. Worse, square is the shape this layout was designed around and its
  // generous crop IS the Badge look; nothing about it should move. The blend reaches full effect at
  // 3:4, by which point a logo is unambiguously tall.
  const TALL_FULL = 0.75;   // at or below this aspect, take the fitted height in full
  const fitHeight = () => {
    // Two regimes, by whether the box is wider than the hub crossing it is pinned to.
    //   Wider  → the outer edge is the far corner: (h/2)² + (h·a − cross)² = outer²
    //   Narrow → the INNER edge is, and it sits at a fixed x, so the chord there is the answer.
    const A = 0.25 + a * a;
    const B = -2 * a * cross;
    const C = cross * cross - outer * outer;
    const disc = B * B - 4 * A * C;
    if (!(disc > 0)) return 2 * outer;
    const hQ = (-B + Math.sqrt(disc)) / (2 * A);
    if (hQ * a >= 2 * cross) return hQ;
    return 2 * Math.sqrt(Math.max(0, outer * outer - cross * cross));
  };
  let h = 2 * outer; // full ring height (top→bottom)
  if (a < 1) {
    const t = Math.min(1, (1 - a) / (1 - TALL_FULL));
    h = h * (1 - t) + fitHeight() * t;
  }
  // The dial rides on top of the tall-logo blend rather than replacing it — the blend decides what
  // 100% IS for this aspect, and the scale is a share of whatever that turned out to be.
  h *= s;
  const w = h * a;
  const y = CENTER - h / 2;
  const onLeft = slot !== 'right';
  // The INNER edge stays pinned at the hub crossing, so a fitted logo shrinks away from the rim and
  // otherwise stays exactly where it was — the change reads as "it got shorter", not "it moved".
  //   That sentence was written for the tall-logo blend and it is exactly why the SIZE DIAL was given
  // the same treatment (Ryan 2026-07-29): the property being described — shrink away from the rim,
  // don't move — is the one that keeps a Badge layout recognisable, and it comes free from placing by
  // the anchor after the size is known.
  const x = onLeft ? CENTER + cross - w : CENTER - cross; // left: right edge=inner; right: left edge=inner
  return placed({ x, y, w, h });
}
/**
 * ⛔ A MARK MAY LEAVE THE SURFACE, BUT NEVER ENTIRELY (Ryan 2026-08-09).
 *
 * *"so long as a part of it is always on the canvas. Meaning you can't fully move it off, but you
 * can move it from far left to far right etc. so long as some of it is still on the printable area."*
 *
 * The mark is CLIPPED to its surface, so pushing it off is not a rendering error — it simply
 * disappears, which is the one outcome that reads as the tool being broken. The rule is therefore a
 * floor on how much must remain, not a fence around where it may go.
 *   Expressed on the box's CENTRE against the printable radius: hold that inside and at least half
 * the mark is always on. Simpler than a true box/disc intersection and strictly safer, while still
 * granting the whole travel Ryan asked for — at the limit the mark hangs half off the edge, which is
 * as far as anyone would want to push a badge before it stops reading as a badge.
 *   ⚠ IT CLAMPS ALONG THE OFFSET, not toward the centre: the direction is the buyer's, only the
 * distance was out of bounds.
 */
function keepMarkOnSurface(box, outerR, du, dv) {
  if (!box || !(outerR > 0) || (!du && !dv)) return box;
  /* How much of the mark must still be on the surface. A thin sliver, deliberately: Ryan's test is
     *"just the left edge of the logo is on the far right edge of the paintable surface"*, so the
     rule has to permit almost-all-the-way-off and refuse only all-the-way. */
  const keep = 0.04 * PLATE_R;
  /* Does the box still reach the printable disc at this fraction of the offset? Measured on the
     box's NEAREST POINT to the centre, which is what "part of it is on" actually means — the first
     version of this held the box's CENTRE inside the radius instead, and that is a different and
     much stricter promise: it stops at half-off, which is exactly where Ryan ran into the wall. */
  const reaches = (t) => {
    const cx = (box.x + box.w / 2) - du * (1 - t) - CENTER;
    const cy = (box.y + box.h / 2) - dv * (1 - t) - CENTER;
    const nx = Math.max(0, Math.abs(cx) - box.w / 2);
    const ny = Math.max(0, Math.abs(cy) - box.h / 2);
    return Math.hypot(nx, ny) <= outerR - keep;
  };
  if (reaches(1)) return box;
  /* ⚠ BISECTED ALONG THE OFFSET, not solved. The constraint is a box against a circle, so the
     admissible travel has no closed form worth writing — and the axis-aligned shortcut is wrong on a
     diagonal nudge, which is every nudge that combines two arrows. 24 halvings resolve it to well
     under a pixel on a 450 viewBox, and `t = 0` is the layout's own placement, which is valid by
     construction — so the search always has a floor to fall back to. */
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (reaches(mid)) lo = mid; else hi = mid;
  }
  return { ...box, x: box.x - du * (1 - lo), y: box.y - dv * (1 - lo) };
}

/**
 * Full-disc box for a genuinely full-face logo (dumbbell "Full Face" layout,
 * `element.bigFace === 'disc'`) — centered, inscribing the whole plate
 * diameter (aspect preserved; corners are trimmed by the disc clip below, not
 * by this box). No ring/hub reasoning: only correct on a product with no
 * physical hub covering the center (Fable §9.1 dumbbell family).
 */
function fullFaceLogoBox(aspect, element = null) {
  const a = aspect > 0 ? aspect : 1;
  const d = 2 * PLATE_R;
  /* ── IT SCALES AND IT MOVES, LIKE EVERY OTHER MARK (Ryan 2026-08-09: *"on the full face layouts,
   * the size is locked at 80%. Need to be able to increase and decrease it, and move it around too
   * in case it's off center") ──────────────────────────────────────────────────────────────────
   * This box was a constant, and that is the whole defect. The SIZE DIAL was not broken and nothing
   * was disabling it: `createStepper` greys both ends when `paintedAt` reports the same width at
   * every value on the dial, which is exactly what a function ignoring `sizeScale` does. The control
   * was honestly reporting that nothing it could do would change anything.
   *   It also answers the question the old comment on `paintedLogoWidth` left open — *"shrinking a
   * full-face logo to reveal plate underneath is a separate design question nobody has answered"*.
   * Ryan has now answered it, and answering it is what makes the NUDGE meaningful here too: at 100%
   * the disc fills the face and there is nowhere to go, but a mark dialled back has room around it,
   * and centring it by eye is the reason he asked.
   *   Clamped at 1 like the badge box: past that the disc leaves the plate on every side at once. */
  const sRaw = Number(element && element.sizeScale);
  const s = Number.isFinite(sRaw) ? Math.min(BIG_LOGO_SCALE_MAX, Math.max(LOGO_SCALE_MIN, sRaw)) : 1;
  const w = (a >= 1 ? d : d * a) * s;
  const h = (a >= 1 ? d / a : d) * s;
  const du = clampBigNudge(element && element.nudgeU) * PLATE_R;
  const dv = clampBigNudge(element && element.nudgeV) * PLATE_R;
  return keepMarkOnSurface({ x: CENTER - w / 2 + du, y: CENTER - h / 2 + dv, w, h }, PLATE_R, du, dv);
}

/** Circle sub-path (for composing an even-odd annulus clip). */
function circleSubPath(cx, cy, r) {
  return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0`;
}
/** Even-odd annulus path: the paintable ring B (RING_B_INNER..RING_B_OUTER). */
function ringBAnnulusPath() {
  return `${circleSubPath(CENTER, CENTER, RING_B_OUTER)} ${circleSubPath(CENTER, CENTER, RING_B_INNER)}`;
}

// ROUND ∪ MITER@2 — the acute-cusp spike fix (Ryan 2026-07-25). Every outline band is painted TWICE
// in the same colour: once round-joined, then the mitered copy over it. The union degrades corner by
// corner, and the two styles below are the whole mechanism — no mask, no filter, no geometry.
//
//   THE GEOMETRY THAT DECIDES IT. A corner's outline runs 1/sin(θ/2) × the band thickness past the
// ink — a pure function of the ANGLE, and the only thing that sorts a point the letter really has
// from a spear the join grew. A RIGHT ANGLE runs √2 ≈ 1.41×. Measured on the Montserrat "45" at 3u
// (tools/probe-cusp-blame.mjs): the crossbar cusp runs 4.3×, the "4" apex fin 1.9×, the diagonal
// junction 1.5×, every square corner 1.37× or less.
//
//   WHY THE LIMIT IS 2. It has to clear √2 or ordinary square corners — the "-LB-" dashes, the 5's
// terminals, the 4's stem — start bevelling, which is the "it lopped the 4's point off" failure.
// 2 is the first round number above that floor, so square corners keep a true miter POINT and
// anything sharper than ~60° is caught. 3 and 4 sit ABOVE the fins and leave them standing.
//
//   WHY THE ROUND COPY. A bevel is not a shortened point, it is a chord at sin(θ/2) × thickness from
// the vertex — 0.22× at the crossbar cusp — so limit 2 ALONE pinches the band to under a quarter of
// its weight right where it turns, and at 6u outlines that reads as a V-notch between the 4 and the
// 5. The round arc is the true constant-distance offset, so it holds full band thickness there.
// Painting both resolves per corner without a decision: wherever the miter is within the limit it
// strictly CONTAINS the round arc, so the sharp point wins; wherever the limit fires, the bevel is
// strictly INSIDE the arc, so the round cap wins. Round alone was rejected on look (it softens the
// square corners too) — here it only ever shows up where a point is geometrically impossible.
//
//   Do NOT reach for a mask/clip trim instead. A mask CAN cap the tip at a constant distance with an
// arc instead of a bevel, and it DOES survive the Image → canvas texture path (entry 61's claim that
// masks are dropped there is a false negative — tools/probe-raster-primitives.mjs). But its usable
// window is 1.41×–1.9×, so it barely shortens the fins, and it collapses further as the outline
// thickens (the worst run falls 4.33× → 2.80× → 1.58× at 1.5u/3u/6u). Rendered side by side in
// tools/probe-cusp-joins.mjs, it loses to this at every thickness.
const OUTLINE_STROKE_STYLE = 'stroke-linejoin:miter;stroke-miterlimit:2;paint-order:stroke';
// The under-copy. Same band, round-joined, so an over-limit corner degrades to a full-thickness arc
// instead of a pinched bevel. Painted FIRST; the mitered copy overpaints it wherever the miter wins.
const OUTLINE_CUSP_STYLE = 'stroke-linejoin:round;paint-order:stroke';

// ── multi-outline mitered text ────────────────────────────────────────────────
/**
 * Emits the same glyph run stacked N+1 times: outermost outline first → fill
 * last, each outline a `fill:none` stroke of width 2×cumulativeThickness with
 * mitered joins, so the strokes straddle the path and overpaint inward, leaving
 * concentric sharp outlines that conform to the letterform. `makeTextEl(paint)`
 * builds one copy (straight <text> or arc <text><textPath>) given paint attrs.
 *
 * @param {(paint:{fill:string,stroke:string|null,strokeWidth:number})=>string} makeTextEl
 * @param {string} fillColor
 * @param {Array<{color:string,thickness:number}>} outlines  resolved colors, THIN→THICK
 * @returns {string}
 */
// The acute-cusp "spikes" are handled by MITERLIMIT 2 in OUTLINE_STROKE_STYLE above — read that note
// before touching the join, and re-measure with tools/probe-cusp-blame.mjs rather than by eye.
//   Corrections to entry 61's findings, both re-measured 2026-07-25:
//   • <mask> is NOT dropped by the Image → canvas texture path. That was a false negative — the
//     reference raster it diffed against had never actually had the masks stripped. Masks (in
//     <defs>, inline, via style, on a <g>, userSpaceOnUse) and clipPath all survive:
//     tools/probe-raster-primitives.mjs. Any future trim CAN use one. It still has to earn its keep.
//   • The miterlimit IS the lever, once it is set from the geometry rather than tried by eye. Its
//     fallback is a bevel, but a bevel only fires above the limit, and √2 is the line every square
//     corner sits under.
// Still true from that session: clipPath can't do this job (clip geometry ignores stroke, so it
// clips away the outline itself), and feMorphology's structuring element is a SQUARE, so an
// erode→dilate opening chamfers everything — an uglier bevel by another name.
function emitStyledText(makeTextEl, fillColor, outlines) {
  let cum = 0;
  const cums = outlines.map((o) => (cum += Math.max(0, o.thickness)));
  let strokes = '';
  for (let k = outlines.length - 1; k >= 0; k--) {
    const band = { fill: 'none', stroke: outlines[k].color, strokeWidth: 2 * cums[k] };
    // Round under, miter over — same colour, so the pair unions into "sharp where a point is
    // possible, full-thickness arc where it isn't". See OUTLINE_STROKE_STYLE.
    strokes += makeTextEl({ ...band, style: OUTLINE_CUSP_STYLE }) + makeTextEl(band);
  }
  return strokes + makeTextEl({ fill: fillColor, stroke: null, strokeWidth: 0 });
}

/**
 * Resolves an element's fill + outline colors against the palette, AFTER applying
 * the plate-dye contrast peel (§4.2) for `bodyColor` — so a ring that would merge
 * with a dyed base isn't drawn. Peel is a pure view (design-profile.effectiveTextStyle);
 * the stored design is untouched, so it's reversible + non-cumulative across dyes.
 * `bodyColor` omitted / black → no peel.
 */
function resolveStyle(element, palette, bodyColor) {
  const eff = effectiveTextStyle(element, palette, bodyColor);
  const fill = resolvePaletteRef(eff.fillRef, palette);
  const outlines = eff.outlines.slice(0, 4).map((o) => ({
    color: resolvePaletteRef(o.colorRef, palette),
    thickness: Math.max(0, Number(o.thickness) || 0),
  }));
  return { fill, outlines };
}

// ── element: arched text ──────────────────────────────────────────────────────
const TOP_ARC = { start: 200, end: 340 }; // centered on 270° (12 o'clock)
const BOTTOM_ARC = { start: 25, end: 155 }; // centered on 90° (6 o'clock), drawn reversed
// SOLO SPANS (Ryan 2026-07-25) — when the design carries only ONE arc word, the opposite half of the
// ring is free, so the word may wrap further: out to 15° / 165°, i.e. 15° clear of the horizontal.
//   Two things come of it. Long names get ROOM instead of shrinking. And a moderate name stops
// filling the span at all — it hits the ring-width font cap first, so its ends finally move with its
// length, where before every word was pinned to the arc boundary and "BIG JOHNS" and "BIG JOHNS GYM"
// occupied the identical sweep (Ryan noticed the ends staying planted). The side pieces already
// dodge whatever the word does, via the auto-balance drift.
//   NEVER when both arcs carry text: two words wrapping this far would meet at 3 and 9 o'clock.
const TOP_ARC_SOLO = { start: 185, end: 355 };   // ends 5° clear of the horizontal
const BOTTOM_ARC_SOLO = { start: 5, end: 175 };  // ditto, mirrored
/**
 * The arc a word is fitted to. `solo` = this is the design's only arc word.
 *
 * A ZONE THAT DIVIDES THE SLOT WINS OUTRIGHT, and `solo` does not apply to it. The widened solo span
 * exists because a lone word on a bumper may borrow the opposite half of a continuous ring; a grip
 * gap has no opposite half to borrow — 4° past its own boundary is a hand hole, not free ring. Its
 * span is the gap, at every denomination, whatever else is on the plate.
 */
function arcSpanFor(element, solo, zone) {
  const own = slotArc(zone, element && element.slot);
  if (own) return own;
  const bottom = element && element.slot === 'bottom';
  if (solo) return bottom ? BOTTOM_ARC_SOLO : TOP_ARC_SOLO;
  return bottom ? BOTTOM_ARC : TOP_ARC;
}
/** The radius an arc word's baseline rides, and the radial band it may fill. The bumper's ring
 *  mid-line and full ring width unless the zone hands this slot a narrower lettering lane. */
function arcLaneOf(zone, slot) {
  const own = slotArc(zone, slot);
  return own ? { r: own.r, band: own.band, outer: own.outer } : { r: RING_MID, band: RING_W, outer: null };
}

/**
 * THE RADIUS A WORD ACTUALLY RIDES, once its size is known (Ryan 2026-07-30: *"on all 'Tri' designs,
 * need the text closer to the outer edge of the plate"*).
 *
 * The lane is a fixed third of the band, and the lettering was centred in it — so it only touched the
 * rim when the fitted font happened to fill the lane. A long name shrinks to fit its 60° of sweep,
 * and the shorter it got the further it floated INWARD from the edge it is supposed to hug.
 *
 * Hanging it off the artwork limit instead makes the outer edge the fixed thing and lets the inboard
 * side take up the slack, which is what a designer setting type around a rim actually does. Measured
 * on the CAP height, not the em box, because caps are all these plates ever paint and half an em is
 * mostly air above them — the difference is what "closer to the edge" is asking for.
 *
 * WHAT IS RESERVED AT THE LIMIT IS THE OUTLINE STACK, MEASURED — not a flat guess. An SVG stroke is
 * centred on its path, so each ring grows the ink outward by its own full thickness; a bare word needs
 * nothing and a four-ring lockup needs ten units. A flat `TEXT_RADIAL_INSET` was both at once: too
 * mean for the common one-ring case (which is what still held the type off the rim) and too generous
 * to be safe for the heavy one. Read from the STORED stack rather than the peeled one, because the
 * plate-dye peel can drop a ring at paint time and over-reserving is the only acceptable error at a
 * print margin — the same rule, for the same reason, as `weightSizes`.
 *
 * Bumper arcs pass `outer: null` and are returned unchanged — they ride the ring mid-line as always.
 */
function arcOuterRadius(lane, fontSize, font, element, inkScale = 1) {
  if (!lane || !(lane.outer > 0) || !(fontSize > 0)) return lane ? lane.r : RING_MID;
  const growth = outlineTotal(element) * inkScale;
  // +1u on top of the stack itself, so the ink lands just INSIDE the keep-clear rather than exactly
  // on it. Cap height is a measured per-font average, not a per-glyph truth, and a margin you are
  // sitting precisely on is one rounding step from being over — but ONE unit is 0.04" on a 45 and is
  // plenty for that (Ryan 2026-07-30: *"might have a tiny bit more room to push the text closer to
  // the edge… if you do have a little space, inch that direction"*). The bigger win is above it:
  // `growth` now carries the THINNED outline, so a one-ring word reclaims another unit and a bit.
  const capTop = lane.outer - growth - 1;
  // Never further IN than the lane's own centre — that is the size at which the lane is already full,
  // and past it this would start pushing type outward through the margin it just reserved.
  return Math.max(lane.r, capTop - (fontSize * fontCapFrac(font)) / 2);
}
/** Does this band read with its letters' tops pointing INWARD (drawn reversed)? True only for the
 *  bumper's bottom arch; the grip's three gaps are rotational copies of a top arch, so all read
 *  tops-outward. Was a bare `slot === 'bottom'` test in three places. */
function arcReadsInward(zone, slot) {
  const own = slotArc(zone, slot);
  return own ? own.reverse === true : slot === 'bottom';
}
/** Is `element` the ONLY arc word carrying text in this design? */
function isSoloArc(elements) {
  return (elements || []).filter((e) => e && e.type === 'arctext' && String(e.text || '').trim()).length === 1;
}

/**
 * The font size at which the stored `letterSpacing` and outline thicknesses mean exactly what they
 * say. Both were chosen against the BUMPER's arc text, which paints somewhere around here; below it
 * they are proportionally too big, and on the grip plate's ~60° gaps that is most of the time.
 */
const ARC_TYPE_REF_FONT = 44;

/**
 * The letter-spacing an arc word paints at.
 *
 * ABSOLUTE on the bumper — unchanged, and every plate in the app depends on it. **PROPORTIONAL on a
 * face whose lane makes the type small** (Ryan 2026-07-30). 3 units between letters is a reasonable
 * gap beside a 44px cap and an enormous one beside a 13px cap, where the glyph itself is only ~8
 * units wide. On "ARCADIA HIGH SCHOOL" in a grip gap the fixed spacing was eating **57 of the 172
 * units of arc** — a third of the plate's lettering budget spent on air.
 */
function arcLetterSpacing(element, zone, fontSize) {
  const base = Number.isFinite(element && element.letterSpacing) ? element.letterSpacing : 3;
  if (!slotArc(zone, element && element.slot)) return base;
  return base * Math.min(1, fontSize / ARC_TYPE_REF_FONT);
}

/**
 * Fit an arc word to its span. `wr` is the font's measured avg glyph-advance ratio (wide faces like
 * Michroma get a bigger wr → shrink more, no overflow).
 *
 * `lsPerEm` switches the spacing from ABSOLUTE to a fraction of the font size, which changes the
 * algebra rather than just the number: with spacing proportional, every term scales with `f`, so the
 * fit is a division instead of the subtract-then-divide below. Passing it is what lets a long name
 * shrink to fit a grip gap instead of shrinking to a floor and then overflowing.
 */
function fitArcFont(text, letterSpacing, spanDeg, radius, maxFont, wr, lsPerEm = null) {
  const arcLen = (spanDeg / 360) * 2 * Math.PI * radius;
  if (!text.length) return maxFont;
  if (lsPerEm != null) {
    const fitted = arcLen / (text.length * (wr + lsPerEm));
    return sized(fitted, maxFont);
  }
  const needed = text.length * (maxFont * wr + letterSpacing);
  if (needed <= arcLen) return maxFont;
  const fitted = (arcLen / text.length - letterSpacing) / wr;
  return sized(fitted, maxFont);
}
/**
 * ⛔ THERE IS NO SIZE FLOOR HERE ANY MORE, AND THAT IS THE FIX (Ryan 2026-07-30).
 *
 * `fitArcFont` used to end `Math.max(minFont, Math.min(maxFont, fitted))` — 18px on the bumper's
 * arch, 9px in a grip gap. But `fitted` is BY DEFINITION the largest size that fits the path, so a
 * floor can only ever bind by raising the type ABOVE what fits, and an SVG `<textPath>` renders only
 * the glyphs that fit and silently drops the rest — off both ends, since these are centred. The
 * floor therefore had no valid regime at all: every time it did anything, it dropped letters.
 *
 * Measured across the library before removing it: **24 of 136 faces truncated before reaching the
 * bumper's own 24-character cap**, and "ARCADIA HIGH SCHOOL" in BBH Bartle asked for 507u of a 379u
 * arch — 134%, i.e. a quarter of the name gone. The grip plate only looked healthy because
 * 2026-07-30 had already given it proportional spacing and a much lower floor; it was the same
 * defect, two faces deep instead of twenty-four.
 *
 * A floor is a legibility wish and this is a geometric constraint; the constraint wins. What keeps
 * the type from getting silly is the CHARACTER CAP, which is the control that can say no before the
 * words are typed rather than after they are painted.
 *
 * The 0.5 is not a floor in disguise — it is a guard against emitting a NEGATIVE `font-size`, which
 * is invalid SVG. The ring path's letter-spacing is absolute (3u), so `arcLen/n − ls` goes negative
 * once a line is long enough for the SPACING alone to fill the arch (~126 characters on the bumper).
 * Nothing a buyer can type comes near it; only the app's own cross-product line join could, and it
 * tops out at 74.
 */
function sized(fitted, maxFont) {
  return Math.max(0.5, Math.min(maxFont, Math.round(fitted * 10) / 10));
}

/**
 * The size an arc word paints at, fitted to the room it actually has.
 *
 * ⚠ THE FLOOR IS WHY "ARCADIA HIGH SCHOOL" CAME OUT AS "ADIA HIGH SCH" (Ryan 2026-07-30). An SVG
 * `<textPath>` renders only what fits along its path and silently drops the rest — and because these
 * are centred (`startOffset: 50%`), the overflow falls off BOTH ends, which is why the front of the
 * name went missing too. 19 characters at the 18px floor needed 262 units of a 172-unit arc.
 *
 * 18px is right for the bumper's 140° arch and far too high for a 60° gap, so the floor comes from
 * the ZONE. With proportional spacing on top, that name now fits at ~13px instead of being cut in
 * two places — which is the whole of Ryan's *"make the font slightly smaller so all the words can
 * fit in"*, and it means no character limit has to do the job that arithmetic should.
 */
// ⚠ NO LONGER A FIT FLOOR — see `sized`. This is now used for ONE thing: `gripZone` estimating how
// deep a lettering lane's type will run before any typeface is known, so it can centre the mark
// below it. A nominal size for a sizing decision, not a limit on a painted one.
const ARC_MIN_FONT_LANE = 9;
function fitArcToLane(text, element, zone, lane, spanDeg, maxFont, font) {
  const lsBase = Number.isFinite(element && element.letterSpacing) ? element.letterSpacing : 3;
  const laned = !!slotArc(zone, element && element.slot);
  const fitAt = (radius) => fitArcFont(
    text, lsBase, spanDeg, radius, maxFont,
    fontWidthRatio(font),
    laned ? lsBase / ARC_TYPE_REF_FONT : null,
  );
  if (!laned) return fitAt(lane.r);
  // ⚠ FIT AT THE RADIUS IT WILL ACTUALLY RIDE, which is not the lane's centre line. The type is hung
  // off the rim (`arcOuterRadius`), so it paints on a LONGER circle than the one it was measured
  // against — and a longer circle is more room. Measuring at the centre line could only ever
  // under-fill, so it was safe, but it was giving away nearly a quarter of the arc: 137 units of word
  // on 177 units of path, a whole step of legibility surrendered for nothing.
  //
  // The two depend on each other — a bigger font hangs further IN, which shortens the circle, which
  // wants a smaller font — so this is a fixed point. Iterating it OSCILLATES around the answer and
  // every other step lands above it, which would overflow; so instead, binary-search the largest size
  // that survives its own consequences. `f − fit(radiusFor(f))` rises monotonically with `f` (the
  // first term grows, the second shrinks), which is exactly what makes the search valid.
  const radiusFor = (f) => arcOuterRadius(lane, f, font, element, Math.max(0.5, Math.min(1, f / ARC_TYPE_REF_FONT)));
  const holds = (f) => f <= fitAt(radiusFor(f)) + 1e-6;
  let lo = fitAt(lane.r);            // safe by construction: the shortest circle it could ride
  if (holds(maxFont)) return maxFont;
  let hi = maxFont;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (holds(mid)) lo = mid; else hi = mid;
  }
  return Math.round(lo * 10) / 10;
}

function drawArcText(defs, body, element, palette, uid, font, allCaps = true, bodyColor = null, fontSizeOverride = null, solo = false, zone) {
  // Caps toggle (Fable §2.7): ALL CAPS is the default varsity look; "As typed"
  // preserves the buyer's own casing (e.g. a lowercase studio wordmark).
  const raw = element.text || '';
  const text = allCaps ? raw.toUpperCase() : raw;
  if (!text) return;
  // `isBottom` was three separate `slot === 'bottom'` tests; it now asks the ZONE whether this band
  // reads tops-inward, because on the grip plate the answer isn't a slot name. All three grip gaps
  // are rotational copies of a top arch and read tops-OUTWARD.
  const isBottom = arcReadsInward(zone, element.slot);
  const arc = arcSpanFor(element, solo, zone);
  const span = Math.abs(arc.end - arc.start);
  const lane = arcLaneOf(zone, element.slot);
  const maxFont = arcMaxFont(element, lane, font, text, zone);
  // Link-clamped size (top/bottom matched) wins; else fit this word alone.
  const fontSize = fontSizeOverride != null ? fontSizeOverride : fitArcToLane(text, element, zone, lane, span - 8, maxFont, font);
  const ls = arcLetterSpacing(element, zone, fontSize);
  // OUTLINES THIN WITH THE TYPE (Ryan 2026-07-30: *"the outline on the text… probably needs to get a
  // fraction thinner. Sometimes those small words with outlines that aren't thin enough looks a
  // little goofy"*). The stored 2.5 was chosen against a bumper arch; on a 13px grip word it is a
  // fifth of the cap height and the letterforms clog. Scaled ONLY where the zone gives a lane, so no
  // bumper plate moves — and floored, so an outline never becomes a hairline nobody can see.
  const inkScale = slotArc(zone, element.slot) ? Math.max(0.5, Math.min(1, fontSize / ARC_TYPE_REF_FONT)) : 1;
  // Fitted against the lane's centre line, then hung off the rim — see arcOuterRadius. Moving OUTWARD
  // only lengthens the arc the word has to run along, so a word fitted at the smaller radius can
  // never overflow at the larger one; the order is safe in that direction and only that one.
  const radius = arcOuterRadius(lane, fontSize, font, element, inkScale);

  // Per-font vertical re-centering (Ryan 2026-07-13): `dominant-baseline:central`
  // trusts each font's declared metrics, so many faces (Special Elite worst) land
  // their caps off the ring mid-line. Shift the PATH radius by the measured ink
  // offset × fontSize so the visible letters sit dead-center in the band — outward
  // ink pushes the top word out / bottom in, so we move the top path IN and the
  // bottom path OUT by the same amount. 0 for well-behaved fonts (they don't move).
  const flipped = element.upsideDown === true;
  const inkShift = fontInkOffset(font) * fontSize;
  // ink re-centering flips with the text — the baseline swaps sides when the
  // band is upside-down, so invert the shift then.
  const pathRadius = radius + (isBottom ? 1 : -1) * (flipped ? -1 : 1) * inkShift;

  const pathId = `${uid}-arc`;
  // Normal: TOP reads via the forward arc, BOTTOM via the reversed arc — both
  // right-side-up. `upsideDown` swaps that per band, so the letters hang inverted
  // at that slot (classic seal look: right-side-up top + upside-down bottom).
  const useReverse = isBottom ? !flipped : flipped;
  const d = useReverse
    ? arcPathReverse(CENTER, CENTER, pathRadius, arc.start, arc.end)
    : arcPath(CENTER, CENTER, pathRadius, arc.start, arc.end);
  defs.push(el('path', { id: pathId, d, fill: 'none' }));

  const { fill, outlines: storedOutlines } = resolveStyle(element, palette, bodyColor);
  // `inkScale` is 1 everywhere but a grip gap — see above.
  const outlines = inkScale === 1 ? storedOutlines : storedOutlines.map((o) => ({ ...o, thickness: o.thickness * inkScale }));
  const family = fontFamilyFor(font);
  const fweight = fontWeightFor(font);
  const make = ({ fill: f, stroke, strokeWidth, style }) =>
    `<text${attrs({
      'font-family': family,
      'font-size': fontSize,
      'font-weight': fweight,
      'letter-spacing': ls,
      // Center the GLYPH HEIGHT on the ring mid-line (not the baseline), so the
      // arch is vertically centered in the ring band — even spacing above/below
      // (Ryan 2026-07-11). Baseline-on-path pushed top text out / bottom text in.
      'dominant-baseline': 'central',
      fill: f,
      stroke: stroke || null,
      'stroke-width': stroke ? strokeWidth : null,
      style: style || OUTLINE_STROKE_STYLE,
    })}><textPath${attrs({ href: `#${pathId}`, startOffset: '50%', 'text-anchor': 'middle' })}>${escText(
      text,
    )}</textPath></text>`;
  body.push(emitStyledText(make, fill, outlines));
}

// ── element: weight number (+ format variants) ────────────────────────────────
/** One straight, centered, multi-outlined text line. */
function straightLine(body, { cx, cy, text, fontSize, family, weight, letterSpacing, fill, outlines, maskId }) {
  const make = ({ fill: f, stroke, strokeWidth, style }) =>
    `<text${attrs({
      x: cx,
      y: cy,
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
      'font-family': family,
      'font-size': fontSize,
      'font-weight': weight || '800',
      'letter-spacing': letterSpacing || 0,
      fill: f,
      stroke: stroke || null,
      'stroke-width': stroke ? strokeWidth : null,
      style: style || OUTLINE_STROKE_STYLE,
    })}>${escText(text)}</text>`;
  body.push(emitStyledText(make, fill, outlines));
}

// Stacked-unit lockup labels (Fable §2.7 varsity conventions for the weight mark). The
// lockup FORMAT chooses the style (dash / plain / spelled-out); the profile UNIT (lb|kg)
// chooses the word — so the LB/KG toggle actually re-letters the plate (Ryan 2026-07-20).
const STACKED_MARKS = {
  lb: { 'stacked-dash': '-LB-', 'stacked-lb': 'LB', 'stacked-lbs': 'LBS', 'stacked-pounds': 'POUNDS', 'stacked-lb-lower': 'lb' },
  kg: { 'stacked-dash': '-KG-', 'stacked-lb': 'KG', 'stacked-lbs': 'KGS', 'stacked-pounds': 'KILOS', 'stacked-lb-lower': 'kg' },
};

/** The number a ONE-character denomination is measured as when capping the unit line's width —
 *  Ryan 2026-08-02: *"the 'POUNDS' under a '5' matches what it would be on the next smallest
 *  number, presumably a '10'."* Capping a 6-letter word at the width of a single "5" would paint
 *  it at a third of the size and undo the readability this whole pass is about. */
const TWO_DIGIT_REF = '10';
const normUnit = (unit) => (unit === 'kg' ? 'kg' : 'lb');
const stackedMark = (fmt, unit) => STACKED_MARKS[normUnit(unit)][fmt] || (normUnit(unit) === 'kg' ? '-KG-' : '-LB-');
const inlineMark = (unit) => (normUnit(unit) === 'kg' ? 'KG' : 'LB'); // the "NNLB" one-liner
/**
 * The weight element's font sizing + text, shared by drawWeight (which paints it)
 * and weightLockupBox (which measures it) so the two never drift. `single` =
 * the plain / inline-lb one-liner; otherwise a stacked number + width-matched unit.
 */
function weightSizes(element, weightValue, font, zone, unit) {
  const boxH = slotBoxSide(zone, element && element.slot);
  const scale = element.sizeScale ?? 1;
  const wr = fontWidthRatio(font);
  /* ⛔ THE ONLY PLACE THE UNIT CHANGES THE NUMBER (Ryan 2026-08-02). `weightValue` is always
     POUNDS — see design-profile's "UNIT IS A LABEL, NOT A WEIGHT" — so KG is a re-lettering of the
     same plate and nothing below this line can move the geometry. Everything downstream (the fit,
     the width cap on the unit line, `weightLockupBox`'s measure) reads `num`, so they all size
     against the string the buyer will actually see: "20.4" is four glyphs where "45" was two. */
  const num = displayWeightText(weightValue, unit);
  const fmt = element.weightFormat || 'stacked-dash';
  const glyphW = (s, f) => s.length * f * wr;
  // The cap-height ceiling on the outline stack — see WEIGHT_OUTLINE_CAP_SHARE. Resolved per format
  // against the size that format actually paints, and returned so `drawWeight` paints the same
  // factor this function reserved room for.
  const capF = weightCapOf(font);
  const storedInk = outlineTotal(element);
  const storedBand = outlineMaxBand(element);
  // WD 493: `zone.textInk` is the cap face's logo-size factor (1 everywhere else) - see `capTextInkScale`.
  const inkOf = (fontSize) => outlineInkScale(storedBand, capF * fontSize, zone && zone.textInk > 1 ? zone.textInk : 1);
  // The widest a straight weight line (or the ruled rules) may run — the SLOT BOX side, so
  // it keeps the same side margin inside the ring band a wide logo does. Was 1.15·box, which
  // let "45LB" span the ring edge-to-edge with no gap (Ryan 2026-07-22).
  const maxW = boxH;              // default widest for a straight weight line
  /**
   * The "45LB" one-liners (inline + ruled) run a fraction narrower than the box → a slightly bigger
   * side gap (Ryan 2026-07-22).
   *
   * ⚠ AND ON A FACE WITH ROOM AROUND IT, THAT FRACTION IS THE WRONG QUESTION (Ryan 2026-07-30: *"the
   * weight number style written like '45LB' is too small relative to the percentage given to it on
   * both grip plates and dumbbells/barbells… even at 100% it's not taking up anywhere near as much
   * space as it should"*).
   *
   * Measured, the one-liner comes out at EXACTLY half the stacked number's size — on every product,
   * because both are bound by the same `boxH` in the same proportion. A four-character line fitted
   * into the width of a two-character one has to be half the size; that is arithmetic, not a bug.
   * What differs is whether anyone can see it: on the bumper's narrow ring there is nothing around
   * the number to compare it against, while on a grip gap or a dumbbell's open face it sits in
   * obvious empty space.
   *
   * So the box stops being the answer where the face has more to give, and the real wall takes over —
   * `ringBandMaxW`, the same geometry the stacked lockup is already capped by. The BUMPER keeps the
   * old expression untouched and byte-for-byte: it has no `inlineBoost`, and widening a number there
   * would move every plate already in the wild for a complaint nobody has made.
   */
  const inlineBoost = (zone && zone.inlineBoost) || 1;
  let inlineMaxW = boxH * 0.88;
  if (inlineBoost > 1) {
    // ⚠ SOLVED AT THE ONE-LINER'S OWN ASPECT, NOT AT 1. `maxOutlineTotal` uses aspect 1 because the
    // wall barely moves across the aspects a STACKED lockup takes; a one-liner is nothing like
    // square — four characters on a single line runs about 4.5:1 — and a wall solved for a square
    // box understates the width available to a wide flat one badly enough to come out BELOW the
    // limit it was replacing, i.e. to shrink the very number this is meant to enlarge.
    const wz = zone.band ? zoneForSlot(zone, element && element.slot) : zone;
    const theta = (zone.angles && zone.angles[element && element.slot]);
    const wall = theta == null ? 0 : ringBandMaxW(INLINE_NOMINAL_ASPECT, wz, theta);
    // ⚠ AND FLOORED AT WHAT IT ALREADY WAS. The wall is the real limit and at some slots it is
    // tighter than the box rule that preceded it — a number sitting far out on a disc has the rim
    // curving away either side of it. Letting that REDUCE a number would be answering "make it
    // bigger" by making some of them smaller, so the boost may only ever add.
    if (wall > 0) inlineMaxW = Math.max(inlineMaxW, Math.min(boxH * 0.88 * inlineBoost, INLINE_WALL_FILL * wall));
    else inlineMaxW = boxH * 0.88 * inlineBoost;
  }
  // ── FIT FIRST, THEN SCALE — the width-bound one-liners (Ryan 2026-07-28) ─────────────────────
  // "The 45LB only takes up about a third the width of the lines above/below it. It's tiny… they all
  // have tiny numbers for this style right now" — and, separately, "it's listed at 100% already so I
  // can't make it bigger, but it will let me click −, and then that greys out and the size stays at
  // 100% without going down at all."
  //
  // ONE ROOT. These two formats used to size as `base · box · scale` and then clamp the RESULT to the
  // slot width. For a four-character line like "45LB" the clamp binds across the ENTIRE dial range,
  // so the painted size is the same at 0.4 as at 1.35. `paintedTextSize` therefore returns a constant,
  // and `edgeOf` — which finds "100% of the room" by walking DOWN from the top until the painted size
  // changes — never sees it change and walks all the way to the bottom of its range, returning 0.45.
  // Everything downstream inherits that:
  //   · `seedDefaultSizes` seeds 75% of 0.45 = 0.338, the exact number entry 112 recorded and read as
  //     "correct for inline". It was never correct; it is the artefact.
  //   · the dial calls 0.45 its 100%, and its floor is 0.40 — so + is already at the edge and − has
  //     one step of nowhere to go. Both grey out and the readout never moves.
  //   · at 0.338 a narrow face paints "45LB" at about a THIRD of the rule width (Bebas measures 36%),
  //     which is the tiny number, and it is worse the narrower the font.
  //
  // Fitting BEFORE the scale is applied is what fixes all of it: the fitted size becomes what 100%
  // MEANS, the dial then scales linearly down from it, and the painted size moves with the dial for
  // every font. `edgeOf` finds a real edge of 1.0 instead of a floor.
  //   The fitted size is also the CEILING: `scale` may only take the piece DOWN from it. Without that
  // the clamp is gone entirely and the dial would happily paint past the slot at 135%. Clamping the
  // scaled result (rather than the scale) is what makes `edgeOf` report a real 1.0 — the painted size
  // stops changing above it, which is exactly the signal it looks for.
  const fitFor = (text, ceiling, wMax) => {
    const adv = Math.max(0.1, strAdvanceFrac(text, font));
    const fitted = Math.min(ceiling, wMax / adv);
    return Math.min(fitted * scale, fitted);
  };
  if (fmt === 'ruled') {
    // "45LB" framed by a top + bottom rule (a varsity banner). The 0.6·box stays as a HEIGHT ceiling
    // so the rules always have vertical room; on every font in the library the width fit is tighter,
    // so it rarely binds.
    const text = `${num}${inlineMark(unit)}`;
    const fontSize = fitFor(text, boxH * 0.6, inlineMaxW);
    // ⚠ THE RULES FOLLOW THE TEXT. They used to be pinned at `inlineMaxW` — a constant, wholly
    // independent of the number they frame — which is the other half of what Ryan saw: shrink the
    // number and the rules stayed edge to edge, so the lockup read as a tiny word lost inside a wide
    // banner. It also made `weightLockupBox` report a width that could not move, which is what made
    // the dial's room unmeasurable in the first place. A banner's rules are as long as its word.
    const ruleW = strAdvanceFrac(text, font) * fontSize;
    // Rule WEIGHT scales with the text too, for the same reason. 0.16·fontSize reproduces the old
    // 0.05·box at the size this format actually paints at, so a full-size lockup looks as it did.
    const ruleThick = Math.max(2.4, 0.16 * fontSize);
    // The clear space between the number's ink and the rule. Returned rather than recomputed by each
    // caller, for the reason this whole function is shared: `drawWeight` paints the gap and
    // `weightLockupBox` measures it, and the two must not drift. Scales with the TEXT (0.15·fontSize
    // reproduces the old 0.05·box at the size this format paints at) so turning the dial down closes
    // the gap with the lockup instead of leaving the rules stranded.
    const ruleGap = Math.max(0.02 * boxH, 0.15 * fontSize);
    return { boxH, single: true, ruled: true, text, fontSize, ruleW, ruleThick, ruleGap, inkScale: inkOf(fontSize) };
  }
  if (fmt === 'plain' || fmt === 'inline-lb') {
    const text = fmt === 'inline-lb' ? `${num}${inlineMark(unit)}` : num;
    // Plain "45" alone = the size of the NUMBER as it appears in a stacked lockup (0.62·box),
    // not the whole stack height (Ryan 2026-07-22). It is short enough that the width cap only bites
    // on the widest faces, so it keeps the scale-then-clamp form and a dial that already worked.
    if (fmt === 'plain') {
      let fontSize = boxH * 0.62 * scale;
      const w = glyphW(text, fontSize);
      if (w > maxW) fontSize *= maxW / w;
      return { boxH, single: true, text, fontSize, inkScale: inkOf(fontSize) };
    }
    // "45LB" as a one-liner — width-bound on every font, so it fits first (see the note above).
    const fontSize = fitFor(text, boxH * 0.86, inlineMaxW);
    return { boxH, single: true, text, fontSize, inkScale: inkOf(fontSize) };
  }
  // Deliberately NOT capped at `maxW` (the slot box) — that was measured on 2026-07-25 and rejected:
  // the stacked number sits alone on its line, so it may legitimately run wider than a straight
  // `45LB` line, and clamping it to the box shrinks the number on every existing design.
  //   It is capped at the RING BAND instead, which is the wall that actually exists. See the block
  // after the unit sizing, where the whole lockup is known.
  let numFont = boxH * 0.62 * scale;
  const unitText = stackedMark(fmt, unit);
  /* ── THE UNIT LINE IS A FIXED SHARE OF THE NUMBER (Ryan 2026-08-02) ────────────────────────────
     It used to WIDTH-MATCH: `unitFont` was solved so the unit's rendered width equalled the
     number's exactly, flush edges either side (Ryan 2026-07-22). That is a good-looking lockup on
     ONE plate and the wrong rule for a set, because it makes the unit's TYPE SIZE a function of
     the digit count. Measured on Montserrat with the number normalised to 100:

         "5"   → POUNDS at 13% of the number's height
         "45"  → 26%
         "150" → 39%          …the same word, three times the size

     Ryan, meeting it on the new 5–150 dumbbell ladder: *"on the smaller weights… the word POUNDS
     will adjust width-wise to match the number — is that true? It then appears too long under
     numbers like 150."* Both halves of that are this one rule seen from either end. A buyer's
     design has to look like ITSELF across their whole rack, and under width-matching it did not.

     So the unit is now a constant fraction of the number and the lockup's WIDTH varies instead —
     which is also how a real plate is marked. The flush-edge look is deliberately given up.

     0.30 is the middle of what the old rule produced across the common range (26% at two digits,
     39% at three), so a 45 — the plate every existing design was authored on — barely moves.

     ⚠ IT ALSO CLOSES A SILENT BREAK. The old ceiling `min(…, numFont * 0.42)` meant that at four
     characters ("12.5", a rubber-dumbbell rung) the match could not be met and the unit came out
     NARROWER than the number anyway — flush edges that quietly stopped being flush on exactly the
     denominations this ladder just added. A fixed share has no such cliff.

     `letterSpacing` still rides the unit's own size, so the tracking scales with it. */
  const UNIT_SHARE = 0.30;
  const LS_FRAC = 0.05;                                              // unit letter-spacing ÷ unitFont
  /* ── THE UNIT WIDTH-MATCHES THE NUMBER, WITH A TWO-DIGIT FLOOR (Ryan 2026-08-02) ──────────────
     Two reports, one rule between them.

     First: *"for some reason POUNDS is wider than the number on some of the things I quickly just
     tested, like the 10, 25, 35, and 45 lb grip plates."* A fixed share of the number's HEIGHT says
     nothing about its WIDTH, and "POUNDS" is six characters against two — at 0.30 it out-runs a
     two-digit number on every face in the app.

     Then, shown the measured ladder: *"always match the number."* So the flush-edge lockup is back
     and `UNIT_SHARE` is only the degenerate fallback. What made the earlier rule wrong was that a
     pure match makes the unit's TYPE SIZE a function of the digit count — 13% of the number's height
     under "5" against 39% under "150" — and the floor below is what removes the bad end of that
     range while keeping the look he asked for.

     ⚠ THE FLOOR IS HIS OWN RULE, from earlier the same day: *"on the shortest number of '5'… the
     word POUNDS probably can't match the number's width perfectly otherwise it'll be far too small
     to read… the 'POUNDS' under a '5' matches what it would be on the next smallest number,
     presumably a '10'."* So a one-character number is measured as if it were two, and the lockup is
     simply allowed to be wider than its number there — the ONLY case where it is.

     ⚠ AND `lockupW` BELOW IS WHAT KEEPS THAT HONEST. The ceiling that decides whether the piece fits
     the plate has to measure the WIDEST line, not the number. It measured only the number for as
     long as the two matched by construction, and the day they stopped matching, "POUNDS" — up to
     1.8× wider under a short number — walked straight through a wall the number had stopped at.
     ⚠ Under "150" the word is now as wide as the number again. That is the trade Ryan took with the
     numbers in front of him; if it reads long, the fix is a ceiling here, not a change to the floor. */
  const unitAdvance = strAdvanceFrac(unitText, font) + LS_FRAC * Math.max(0, unitText.length - 1);
  const numAdvance = Math.max(strAdvanceFrac(num, font), strAdvanceFrac(TWO_DIGIT_REF, font));
  /* ⚠ AND A CEILING ON THE SIZE, WHICH THE FIRST CUT OF THIS DROPPED AND `render-engine.smoke`
     caught within the minute: *"Big Badge a=1: margins match top and bottom (26.4u vs 21.2u)"*. A
     match only constrains the unit's WIDTH, so a SHORT unit word gets there by growing tall — "-LB-"
     under "50" solves at 0.47 of the number, which makes the whole lockup taller than `capBadgeRise`
     reserves for it and slides a Big Badge 5u off centre. 0.42 is the ceiling the pre-2026-08-02
     width-match rule already carried, restored here for the same reason it existed. POUNDS is six
     characters and lands at ~0.29, so the word Ryan is actually looking at matches exactly; only the
     short marks (-LB-, LB, lb) and the four-character denominations feel it. */
  /* ── A SHARE OF THE HEIGHT, CEILINGED BY A SHARE OF THE WIDTH ────────────────────────────────
     Three readings from Ryan on 2026-08-02, and exactly one rule satisfies all three:

       1. *"POUNDS is wider than the number… on the 10, 25, 35, and 45 lb grip plates."*  → it may
          never out-run the number.
       2. *"always match the number."*                                                     → the two
          lines belong to one lockup; the word tracks the number rather than floating free.
       3. *"POUNDS is now too big on… 12.5, 10, 15, 17.5, and some of the big ones too like 140,
          145, 150 — I'm pretty sure these big ones used to line up nicely."*               → a FLUSH
          match is already too much, and the big denominations looked right under the plain 0.30
          share they had before any of this.

     So the share survives as the SIZE rule (which is what made 140–150 look right), and the match
     survives as a WIDTH CEILING at 0.85 — close enough to read as one lockup, short enough that the
     word never reaches the number's edges. Measured, that lands every case where he put it: the
     grip's two-digit plates at 0.85 instead of >1, and 12.5 / 150 back at the ~0.6 they wore when he
     says they lined up nicely, because there the share binds first and the ceiling never fires.

     ⚠ 0.42 WAS TRIED AND WAS WRONG — a pure match with only a size ceiling. It read as "too big" on
     precisely the denominations where the number is widest, because that is where a match asks the
     word to grow most. Recorded because the failure is instructive: matching the WIDTH of a wide
     number is not the same as belonging to it.

     ⚠ THE FLOOR IS STILL RYAN'S: a one-character number is measured as if it were two, so the word
     under a "5" is the width it would have under a "10" and is the ONE case allowed to out-run its
     number — *"the exception… is with the 5 lb dumbbell, since the '5' is so much narrower."* */
  const UNIT_WIDTH_CAP = 0.85;
  let unitFont = Math.min(
    numFont * UNIT_SHARE,
    unitAdvance > 0 ? (UNIT_WIDTH_CAP * numAdvance * numFont) / unitAdvance : Infinity,
  );
  let unitLS = LS_FRAC * unitFont;                                   // absolute letter-spacing to paint with
  /** The lockup's true width: the WIDER of its two lines. See `weightLockupBox`, which must agree. */
  const lockupW = (nf, uf) => Math.max(strAdvanceFrac(num, font) * nf, unitAdvance * uf);

  // ── THE RING-BAND CEILING (Ryan 2026-07-26 — closes the open item from changelog 85) ─────────
  // Entry 85 named the right ceiling ("the ring band, not the slot box") and left it open as real
  // geometry. `ringBandMaxW` is that geometry: the largest box of this lockup's aspect, centred on
  // this slot's anchor, that stays inside the band — the same test the wide-logo sizing already runs.
  //
  // ⚠ CORRECTION TO ENTRY 85's DIAGNOSIS, measured here. It reads as though the SIZE DIAL alone
  // walks the number off the plate. It does not: at 3 o'clock the wall is ~70.3u and the bare glyph
  // runs 50.0u at scale 1.0 and only 67.5u at the dial's 1.35 top, so the glyph never leaves the ring
  // within the range the UI can even reach. The lever is the OUTLINE STACK, which no format accounted
  // for. `emitStyledText` strokes each band at `2 × cumulative thickness` and an SVG stroke is centred
  // on the path, so the ink grows OUTWARD by the full sum of the thicknesses on every side. Four
  // bands at 2.5 add 20u to a 67.5u number — 87.5u against a 70.3u wall. THAT is the spill.
  //   Which also explains why only this format shows it: `plain` / `inline-lb` / `ruled` cap their
  // glyph at the slot box (~66.5u), comfortably under the wall, so their ink still fits by accident.
  // The stacked number was the one allowed to run to the wall before any ink was added.
  //
  // So the glyph is fitted inside `wall − 2 × growth` rather than inside `wall`. Outline thickness is
  // read from the STORED stack, not the peeled one: `resolveStyle`'s plate-dye peel can drop a ring at
  // paint time, and assuming every ring is present sizes a hair conservatively rather than a hair too
  // large. Wrong in the safe direction is the only acceptable error for a print margin.
  //
  // All three values scale together to preserve the exact number↔unit width match computed above —
  // shrinking `numFont` alone would break the alignment that block exists to guarantee.
  //   …AND IT RESERVES WHAT IS PAINTED, NOT WHAT IS STORED. `inkScale` is resolved here against the
  // number's size BEFORE this clamp — the size the lockup is asking for — so the reservation and the
  // paint agree on one factor. Resolving it again after the clamp would be a second answer to the
  // same question (§0b), and the clamp only ever shrinks the glyph, so a stack that fits the ceiling
  // going in can sit a hair over it coming out. That is the safe direction: a shade thicker than the
  // ceiling on the rare heavily-outlined stack, never thinner than the buyer asked for.
  const inkScale = inkOf(numFont);
  const growth = storedInk * inkScale;
  /* ⛔ THE WIDER LINE, NOT THE NUMBER (Ryan 2026-08-02). This measured only the number, which was
     exactly right while the unit WIDTH-MATCHED it by construction and became wrong the day the unit
     became a fixed share of the number's height instead. The ring-band ceiling below then solved for
     a box narrower than the thing being painted, so the number stopped at the wall and "POUNDS" —
     up to 1.8× wider under a short number — walked straight through it. That is the 5 lb plate's
     overhanging P, and at the top of the dial it put the lockup 10 units past the rim band. */
  const natW = lockupW(numFont, unitFont);
  const natH = stackedLayout({ numFont, unitFont }, capF).total;
  if (natW > 0 && natH > 0) {
    // No angle for this slot (a preset authoring somewhere the zone doesn't name) → no wall to
    // measure against, so leave the size alone rather than invent one.
    const theta = (zone.angles && zone.angles[element.slot]);
    if (theta != null) {
      // Fit the INKED box, so the aspect the wall is solved for is the shape actually painted.
      // THE SLOT'S OWN VIEW. A zone that declares its band may put two slots at radically different
      // radii (the cap's `center` against its `bottom`), so measuring the wall at the zone's base
      // anchor answers for a place the number is not. Ring zones are handed back unchanged —
      // ZONE_PLATE has no per-slot views at all, and the grip's are deliberately left to the
      // reconstruction its sizes were calibrated against.
      const wallZone = zone.band ? zoneForSlot(zone, element.slot) : zone;
      const wall = ringBandMaxW((natW + 2 * growth) / (natH + 2 * growth), wallZone, theta);
      const room = wall - 2 * growth; // what's left for the glyph itself
      if (wall > 0 && natW > room) {
        // A stack so heavily outlined that the ink alone fills the band leaves no room for a glyph.
        // Floor it rather than emit a zero/negative font size: the rings will overlap, which is the
        // buyer's own choice, and the number stays legible.
        const shrink = Math.max(0.2, room / natW);
        numFont *= shrink;
        unitFont *= shrink;
        unitLS *= shrink;
      }
    }
  }
  // ⚠ RE-RESOLVED AGAINST THE SIZE THAT SURVIVED THE CLAMP. `inkOf` is one function asked at two
  // stages, which is not two answers to one question — the two stages ask different questions.
  // RESERVING (above) has to happen before the wall is solved, so it uses the size the lockup asked
  // for and over-reserves when the clamp then shrinks it, which is the safe error on a print margin.
  // PAINTING has to use the size actually painted, or the ceiling is solved against a number that
  // no longer exists: measured, a four-ring 10u stack came out at 28% of cap height — nearly twice
  // the ceiling — because the clamp had taken a third off the glyph after the factor was fixed.
  // Identical whenever the clamp doesn't fire, which is every stack a buyer is likely to build.
  return { boxH, single: false, num, numFont, unitText, unitFont, unitLS, inkScale: inkOf(numFont) };
}

/**
 * The weight element's outline stack AS PAINTED — the stored sum reduced by the cap-height ceiling
 * the renderer actually applies. Every site that RESERVES room for the stack has to use this rather
 * than the raw sum, or it holds space for ink that is no longer there and pushes the lockup off its
 * mark. Goes through `weightSizes` so there is exactly one implementation of the ceiling.
 */
function weightInkGrowth(element, weightValue, font, zone, unit) {
  return outlineTotal(element) * weightSizes(element, weightValue, font, zone, unit).inkScale;
}

/**
 * Rendered vertical extent of a weight element — top of the number to the bottom
 * of the unit line — so a sibling logo can be size-MATCHED to the "45 / -LB-"
 * stack (Ryan 2026-07-17: in a logo+weight layout both marks are taller-than-wide,
 * and the logo was rendering smaller than the weight). Mirrors drawWeight's y-offsets.
 */
/** Stacked lockup vertical layout, positioned off the ACTUAL per-font cap height so the
 *  number↔unit gap stays CONSTANT across fonts (Ryan 2026-07-22). Centre offsets are relative
 *  to cy; the gap opens only when thick outlines need it (innerNum/innerUnit = every outline
 *  band except the outermost, which may touch). Shared by drawWeight + weightLockupBox. */
function stackedLayout(s, cap, innerNum = 0, innerUnit = 0) {
  const numCap = cap * s.numFont;
  const unitCap = cap * s.unitFont;
  const baseGap = 0.16 * s.numFont;                                       // constant visual gap
  const gap = Math.max(baseGap, innerNum + innerUnit + 0.06 * s.numFont); // opened for thick outlines
  const total = numCap + gap + unitCap;
  return { total, numCap, unitCap, numCenter: -total / 2 + numCap / 2, unitCenter: total / 2 - unitCap / 2 };
}

function weightLockupBox(element, weightValue, font, zone, unit) {
  // The weight mark's rendered BOUNDING BOX (w × h) for the CURRENT number style — a sibling
  // logo is size-matched off this (drawLogo matches its diagonal). Uses the ACTUAL per-font
  // cap so it mirrors what drawWeight paints (Ryan 2026-07-22).
  const s = weightSizes(element, weightValue, font, zone, unit);
  const cap = weightCapOf(font);
  if (s.ruled) {
    const ruleY = (cap * s.fontSize) / 2 + s.ruleThick / 2 + s.ruleGap; // outline growth ≈ 0 (approx)
    // The WIDER of the two, not the rule alone — a box that reports only the rule cannot move with
    // the dial, and `sizeEdgeFor` reads this to find out where the room ends (Ryan 2026-07-28).
    return { w: Math.max(s.ruleW, strAdvanceFrac(s.text, font) * s.fontSize), h: 2 * (ruleY + s.ruleThick / 2) };
  }
  if (s.single) return { w: strAdvanceFrac(s.text, font) * s.fontSize, h: s.fontSize * cap };
  // The WIDER of the two lines — see `lockupW` in `weightSizes`, which this must agree with: a
  // logo is size-matched off this box, and `sizeEdgeFor` reads it to find where the room ends.
  const uText = String(s.unitText || '');
  const uW = strAdvanceFrac(uText, font) * s.unitFont + (s.unitLS || 0) * Math.max(0, uText.length - 1);
  return { w: Math.max(strAdvanceFrac(s.num, font) * s.numFont, uW), h: stackedLayout(s, cap).total };
}

/**
 * WHERE A WEIGHT LOCKUP ACTUALLY PAINTS — the zone's anchor, then every radial rule that moves it.
 *
 * ⚠ EXTRACTED BECAUSE A SECOND READER NEEDS THE SAME ANSWER (2026-08-04). `weightFocusPoint` aims
 * the close-up camera, and it was reading `zoneForSlot(...).anchorR` — the field's centre line,
 * which stopped being where the number is the moment `hangOuter` existed. On Crest that has been a
 * 25-unit miss on a 225-unit radius since 2026-07-30, and extending the rim hang to Signet and
 * Sidelines (Ryan 2026-08-04) would have added two more. One implementation, two readers.
 *
 * The order is the order the rules compose in: push out to the rim if the zone allows it, hang off
 * the target if the zone names one, then give back whatever the arch-balance drift owes the margin.
 */
function weightPaintAnchor(element, balanceDelta, zone, weightValue, font, unit, hangTo = null) {
  const view = zoneForSlot(zone, element && element.slot);
  const thetaDeg = pointAngle(element, balanceDelta || 0, zone);
  const t = (thetaDeg * Math.PI) / 180;
  const at = (r) => ({ cx: CENTER + r * Math.cos(t), cy: CENTER + r * Math.sin(t) });
  const lock = weightLockupBox(element, weightValue, font, zone, unit);
  const has = !!(lock && lock.w > 0 && lock.h > 0);
  let r = view.anchorR || 0;

  // The same rim push the mark gets, measured on the lockup's real box — see `edgeHangRadius`.
  if (view.hangEdge > 0 && has) r = edgeHangRadius(lock.w, lock.h, thetaDeg, view.hangEdge, r);

  const hang = hangTo > 0 ? hangTo : view.hangOuter;
  if (hang > 0) {
    /* ⚠ THE HALF-EXTENT THAT POINTS OUTWARD, WHICH IS NOT ALWAYS THE HEIGHT (2026-08-04).
     *
     * `hang` is where the lockup's outer ink edge should land, and this subtracts the piece's own
     * reach to get the anchor. It read `h / 2` — true at a top or bottom slot, where the lockup's
     * height does point radially, and that was the only place this rule had ever been used (Crest).
     * The moment Ryan asked for it on Signet's side slots (2026-08-04) the same line was subtracting
     * a 56-unit height where the radial reach is a 139-unit WIDTH, and the numbers hung well past
     * the margin they were asked to match: measured at r=221.6 against the arch text's 216.
     *   Projecting both half-extents onto the radial direction is the general form — `w/2` at 0°,
     * `h/2` at 90°, and the honest blend in between, which is where the arch-balance drift now puts
     * these pieces. Crest is byte-identical: its number sits at 90°, where this IS `h / 2`.
     *   The BADGE (`hangTo`) keeps the plain height, and the exception is the rule: there the target
     * is the top EDGE of a logo — a straight line, not a circle — so the gap Ryan tuned is the one
     * between two parallel edges, and it is measured on the axis the two share. */
    const t2 = (thetaDeg * Math.PI) / 180;
    const reach = has ? (Math.abs(Math.cos(t2)) * lock.w + Math.abs(Math.sin(t2)) * lock.h) / 2 : 0;
    r = Math.max(0, hang - (hangTo > 0 ? (has ? lock.h / 2 : 0) : reach));
  }

  // ── AND THE DRIFT PAYS ITS MARGIN BILL, LAST ────────────────────────────────────────────────
  // Every rule above places the piece on its own axis. `capPairedDrift` then swings it off that
  // axis while its box stays axis-aligned, so its outer corner reaches further out than anything
  // above accounted for. Last, because it is a correction to wherever the piece ended up rather
  // than a placement of its own.
  if (has) r = capDriftPullIn(zone, element, view, thetaDeg, lock, r);
  /* ── AND THE BUYER'S OWN OFFSET, AFTER EVERYTHING (Ryan 2026-08-09: *"we also need the same thing
   * on the weight tab/area to reposition the weight number"*) ──────────────────────────────────
   * The badge logo's nudge, on the number. Applied to the FINISHED anchor for the same reason
   * `bigLogoImageBox` applies its own last: it is an offset from wherever the layout put the piece,
   * not an input to the placement rule — so the rim push, the artwork hang and the drift's margin
   * bill all still resolve exactly as they did, and the nudge moves their answer.
   *   ⚠ THIS FUNCTION IS WHY THERE IS ONE PLACE TO PUT IT. `drawWeight` and `weightFocusPoint` both
   * take their anchor from here (that is what it was extracted for on 2026-08-04), so the paint and
   * the close-up camera cannot disagree about where the number is. Adding the offset in `drawWeight`
   * would have aimed the Weight tab's zoom at the number's OLD position.
   *   No layout gate here: the field is only ever written on a layout whose UI offers the pad, which
   * is the same contract the logo nudge has kept since it shipped. */
  const p = at(r);
  const du = clampBigNudge(element && element.nudgeU, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP) * PLATE_R;
  const dv = clampBigNudge(element && element.nudgeV, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP) * PLATE_R;
  return keepOnSurface({ cx: p.cx + du, cy: p.cy + dv }, zone, element, lock, has);
}

/**
 * ⛔ THE NUMBER MAY NOT LEAVE THE PAINTABLE SURFACE. EVER. (Ryan 2026-08-09.)
 *
 * *"I just chose BIG BADGE for the comp urethane grip plate, and the weight number had defaulted to
 * being off the regular paintable area altogether — which to someone who doesn't know any better
 * would probably be very confusing and frustrating."*
 *
 * 🔑 THIS IS NOT A NUDGE GUARD, WHICH IS WHY IT RUNS UNCONDITIONALLY. He hit it on a design he had
 * not nudged at all: a layout authored against the bumper's ring, resolved onto the grip plate's
 * much tighter band, seeds its number outside the paint. A guard that only bit when `nudgeU` was set
 * would have watched that happen and done nothing. The offset above and the seed below are the same
 * question — "is this piece on the surface?" — and it is asked once, last, about the finished point.
 *
 * The band is the zone's own (`zoneBand`), so every family answers with its real geometry: the
 * bumper's ring B, the grip's narrower lane, the cap's solid disc (inner 0, where only the outer
 * constraint can bind). The lockup's RADIAL reach is projected the same way `hangOuter` projects it
 * — `w/2` at 0°, `h/2` at 90°, the honest blend between — because a number sitting at 40° reaches
 * further than either half-extent alone.
 *
 * ⚠ IT PULLS ALONG THE RADIUS, never toward the centre of the plate: the buyer's angular choice is
 * theirs and is not what was out of bounds. And when the band is narrower than the piece is wide,
 * both constraints cannot hold — then the OUTER one wins, because ink over the hub reads as a
 * mistake while ink a few units proud of the band edge reads as a tight fit.
 */
function keepOnSurface(pt, zone, element, lock, has) {
  if (!has) return pt;
  const view = zoneForSlot(zone, element && element.slot);
  const band = zoneBand(view);
  if (!band || !(band.outer > 0)) return pt;
  const dx = pt.cx - CENTER;
  const dy = pt.cy - CENTER;
  const d = Math.hypot(dx, dy);
  if (!(d > 0)) return pt;
  const th = Math.atan2(dy, dx);
  const reach = (Math.abs(Math.cos(th)) * lock.w + Math.abs(Math.sin(th)) * lock.h) / 2;
  let r = d;
  if (band.inner > 0 && r - reach < band.inner) r = band.inner + reach;
  if (r + reach > band.outer) r = Math.max(0, band.outer - reach);   // outer wins — see above
  if (r === d) return pt;
  return { cx: CENTER + r * Math.cos(th), cy: CENTER + r * Math.sin(th) };
}

function drawWeight(body, element, palette, weightValue, font, balanceDelta = 0, zone = ZONE_PLATE, bodyColor = null, unit = 'lb', uid = 'w', hangTo = null) {
  // The weight renders at its slot, drifted by the auto-balance (§2.4) when it's a
  // left/right anchor opposite asymmetric arc text (Ryan 2026-07-13: in the
  // big-logo layouts the number keeps its regular spot — those have no top+bottom
  // text, so balanceDelta is 0 and this is a no-op).
  let { cx, cy } = pointAnchor(element, balanceDelta, zone);
  const { fill, outlines: storedOutlines } = resolveStyle(element, palette, bodyColor);
  const family = fontFamilyFor(font);
  const fweight = fontWeightFor(font);

  const s = weightSizes(element, weightValue, font, zone, unit);
  // THE CEILING, APPLIED — see WEIGHT_OUTLINE_CAP_SHARE. `weightSizes` resolved the factor against
  // the size this format paints at and reserved the wall for it; painting anything else here would
  // put the reservation and the ink at odds. 1 for the median design, so most plates don't move.
  const inkScale = s.inkScale ?? 1;
  const outlines = inkScale === 1 ? storedOutlines : storedOutlines.map((o) => ({ ...o, thickness: o.thickness * inkScale }));

  // The small unit line ("-LB-") can't carry full-thickness outlines, but DROPPING
  // one so the number and the unit disagree looked worse (Ryan 2026-07-17). Instead
  // keep the WHOLE outline stack on the unit line too — same colours, same order as
  // the number — just scale every outline's thickness down (≈40% reduction) so they
  // both still land legibly on the smaller text.
  //   Derived from the CEILED stack, so the two compose: the unit line stays 60% of whatever the
  // number is actually wearing rather than 60% of what the buyer stored.
  const UNIT_OUTLINE_SCALE = 0.6;
  const unitOutlines = outlines.map((o) => ({ ...o, thickness: o.thickness * UNIT_OUTLINE_SCALE }));

  // ── HANG THE LOCKUP OFF THE RIM, ONCE ITS REAL HEIGHT IS KNOWN (see the zone's `hangOuter`) ────
  // The zone can say where the outer ink edge should LAND but not where the anchor has to be for
  // that to happen — that depends on the typeface and the number style, neither of which exists when
  // a zone is built. Same shape of decision as the arc's `arcOuterRadius`, one layer later, and it
  // is what makes every face land on the same margin rather than only the house one.
  // `hangTo` (the caller's) wins over the zone's own target: on a badge the edge the number balances
  // against is the LOGO's top, which is not a property of the zone at all — it depends on the
  // artwork's aspect and on the size dial. See the badge block in `renderDesignToSVGString`.
  ({ cx, cy } = weightPaintAnchor(element, balanceDelta, zone, weightValue, font, unit, hangTo));
  // Build into a local buffer so an upside-down weight can be wrapped in one
  // rotate(180) about its own anchor (Ryan 2026-07-13 — e.g. weight-both-sides
  // with the right number inverted).
  const parts = [];
  if (s.ruled) {
    // "45LB" framed by a top + bottom rule (banner look). The number is INK-CENTERED (per-font
    // offset) and the rules are placed off its ACTUAL cap height, so the gap above/below stays
    // constant as you scroll fonts. The rules carry the SAME outline stack as the number, and
    // the gap grows with the outline thickness so they never crowd it (Ryan 2026-07-22).
    const numCap = weightCapOf(font) * s.fontSize;
    // Drawing at baseline (cy + ink·fontSize) lands the number's INK centre at cy — so the
    // rules must be symmetric about cy, NOT the baseline, or the offset makes the top/bottom
    // gaps unequal (Bebas one way, Oswald the other — Ryan 2026-07-22).
    straightLine(parts, { cx, cy: cy + weightInkOf(font) * s.fontSize, text: s.text, fontSize: s.fontSize, family, weight: fweight, fill, outlines, maskId: `${uid}-spk1` });
    const totalThick = outlines.reduce((a, o) => a + Math.max(0, o.thickness), 0); // ½·stroke each side
    const gap = s.ruleGap; // clean space between the number's outline edge and the rule's
    // centre-to-centre: clear the number's cap + its outline, a gap, then the rule's ½ + outline.
    const ruleY = numCap / 2 + s.ruleThick / 2 + 2 * totalThick + gap;
    const half = s.ruleW / 2;
    for (const dy of [-ruleY, ruleY]) {
      const y = cy + dy - s.ruleThick / 2;
      const makeRule = ({ fill: f, stroke, strokeWidth, style }) => el('rect', {
        x: cx - half, y, width: s.ruleW, height: s.ruleThick, rx: s.ruleThick / 2,
        fill: f, stroke: stroke || null, 'stroke-width': stroke ? strokeWidth : null, style: style || OUTLINE_STROKE_STYLE,
      });
      parts.push(emitStyledText(makeRule, fill, outlines));
    }
  } else if (s.single) {
    straightLine(parts, { cx, cy: cy + weightInkOf(font) * s.fontSize, text: s.text, fontSize: s.fontSize, family, weight: fweight, fill, outlines, maskId: `${uid}-spk1` });
  } else {
    // Stacked number + unit, positioned off the ACTUAL per-font cap height + re-centred by the
    // font's ink offset, so the number↔unit gap AND the overall centring stay CONSTANT across
    // fonts instead of jumping (Ryan 2026-07-22). A thick multi-outline stack grows both marks
    // outward — only the OUTERMOST band of each may touch — so the gap opens by the inner bands
    // (every band except the outermost) when the stack needs it, symmetric about cy.
    const sumT = (os) => os.reduce((a, o) => a + Math.max(0, o.thickness), 0);
    const lastT = (os) => (os.length ? Math.max(0, os[os.length - 1].thickness) : 0);
    const innerNum = sumT(outlines) - lastT(outlines); // all bands except the outermost
    const innerUnit = sumT(unitOutlines) - lastT(unitOutlines);
    const cap = weightCapOf(font);
    const ink = weightInkOf(font);
    const L = stackedLayout(s, cap, innerNum, innerUnit);
    straightLine(parts, { cx, cy: cy + L.numCenter + ink * s.numFont, text: s.num, fontSize: s.numFont, family, weight: fweight, fill, outlines, maskId: `${uid}-spkN` });
    straightLine(parts, { cx, cy: cy + L.unitCenter + ink * s.unitFont, text: s.unitText, fontSize: s.unitFont, family, weight: fweight, letterSpacing: s.unitLS, fill, outlines: unitOutlines, maskId: `${uid}-spkU` });
  }

  let inner = parts.join('');
  if (element.upsideDown === true) inner = `<g transform="rotate(180 ${cx} ${cy})">${inner}</g>`;
  body.push(inner);
}


/**
 * The DRAWN size of a plain (non-big) slot logo — extracted from drawLogo so the auto-balance can
 * ask how wide a logo actually paints BEFORE anchors are placed (Ryan 2026-07-25).
 *
 * Pure, and depends on nothing positional: the slot box, the aspect, the sibling weight's box for
 * the size-match, and the buyer's size dial. That is precisely why it can run first — the balance
 * drift moves the anchor, never the size.
 *   The balance used to GUESS this (slot box ÷ aspect) and was ~3x under for a wide mark: the Blue
 * Sky wordmark measured 17.6° across the ring where the guess said 5.7°, so every gap computed
 * around it was wrong.
 * @returns {{w:number, h:number}} tangential/radial extents in plate units
 */
function logoDrawSize(element, aspect, zoneIn = ZONE_PLATE, matchBox = null, thetaDeg = null, ceiling = null, legacy = false) {
  const zone = zoneForSlot(zoneIn, element && element.slot); // the room LEFT after any rim arc above it
  // Wider-than-tall AND at a side slot → allow the extra radial room (see SIDE_WIDE_BONUS). Top and
  // bottom are deliberately excluded: there the long axis is tangential and the annulus fit already
  // gives it room, so nothing is being pinched.
  const sideWide = aspect >= 1 && (element.slot === 'left' || element.slot === 'right');
  // `thetaDeg` is where the piece actually sits — the balance drift swings a side slot off-axis, and
  // the room it has depends on that angle. Falls back to the slot's base angle when unknown.
  const theta = Number.isFinite(thetaDeg) ? thetaDeg : (zone.angles[element.slot] ?? 0);
  const sideMax = sideWide ? sideWideMaxW(aspect, zone, theta) : 0;
  const box = sideWide && sideMax > 0 ? sideMax : slotBoxSide(zone);
  let drawW;
  let drawH;
  if (aspect >= 1) {
    drawW = box;
    drawH = box / aspect;
  } else {
    drawH = box;
    drawW = box * aspect;
  }
  // Size-match to a sibling weight lockup by the weight's VISUAL SIZE — equal DIAGONAL, so the
  // logo and the number inscribe the same circle (Ryan 2026-07-17/22). The diagonal tracks how
  // big the weight actually IS: a tall stacked "45 / -LB-" (unit below makes it taller) has a
  // bigger diagonal than the short-wide "45LB", so it earns a bigger — not smaller — logo. The
  // old model averaged in a plain AXIS-match (the weight's WIDTH for a square logo); the stacked
  // "45" is narrow, so that dragged the logo DOWN next to the very tallest numbers — backwards
  // (Ryan 2026-07-22). But pure equal-visual-size lets an EXTREME-aspect logo tower past an
  // opposite-shaped weight (a skinny logo next to a short-wide number), so BLEND back toward the
  // plain axis-match as the logo departs from square: `squareness` = 1 at square → 0 at extreme,
  // target = visual·squareness + axis·(1−squareness). A near-square logo gets ~pure visual size;
  // a skinny/wide one is pulled back to its own long axis. Capped at the slot box; relies on the
  // intake auto-trim so the box hugs the mark.
  if (matchBox && matchBox.w > 0 && matchBox.h > 0) {
    const wDiag = Math.hypot(matchBox.w, matchBox.h);
    const squareness = Math.min(aspect, 1 / aspect);
    if (aspect < 1) {
      const axisT = matchBox.h;                       // logo's long axis (height) matches weight height
      const visualT = wDiag / Math.hypot(1, aspect);  // equal visual size (skinny → would tower)
      drawH = Math.min((visualT * squareness + axisT * (1 - squareness)) * LOGO_FILL_COMPENSATION, box);
      drawW = drawH * aspect;
    } else {
      const axisT = matchBox.w;                        // logo's long axis (width) matches weight width
      const visualT = wDiag / Math.hypot(1, 1 / aspect);
      drawW = Math.min((visualT * squareness + axisT * (1 - squareness)) * LOGO_FILL_COMPENSATION, box);
      drawH = drawW / aspect;
    }
  }
  // Shape-aware room + a legibility FLOOR (Ryan 2026-07-25). Everything above sizes against the
  // SQUARE `box`, which is the ring's radial width — so a short/wide mark in the TOP slot (or a
  // tall one at left/right) was capped by the one direction it wasn't actually short of, and the
  // weight size-match then pulled it down again: a 3:1 wordmark printed ~19u tall and couldn't be
  // read, with the room either side of it empty. `slotAnnulusBox` is what genuinely fits there.
  //   Ceiling = max(today's box, the true fit) so NOTHING shrinks — a near-square logo keeps the
  // size it has (the exact annulus fit is slightly tighter than the square box, since the square
  // one lets its corners overhang, and quietly shaving 4% off every square logo is not the ask).
  //   Floor = 75% of the fit, which is what stops the size-match from printing a wide mark tiny:
  // equal-visual-size matches the DIAGONAL, and a wide logo sharing the weight's diagonal has
  // almost no height. Only bites when the mark is genuinely wide or tall — a near-square logo is
  // already bigger than its own floor, so it comes through untouched.
  // Purely a FLOOR — it can only ever grow a mark, never shrink one, so every design that reads
  // well today is untouched (the exact annulus fit is a hair tighter than the square box, because
  // the square box lets its corners overhang, and quietly shaving that off square logos is not
  // the ask).
  const fit = slotAnnulusBox(zone, element.slot, aspect);
  if (fit) {
    const floorW = fit.w * SLOT_TANGENTIAL_FILL;
    if (drawW < floorW) { drawW = floorW; drawH = drawW / aspect; }
  }
  // ⚠ `Math.max` ONLY WHERE THE WALL IS SOFT — which is what `hardWall` names. On a ring face the
  // annulus fit is a hair tighter than the square box, and letting a square mark's corners overhang
  // into rings A/C is a decision this function makes on purpose (see the note above): that is
  // unpainted metal, not a neighbour, and taking the larger of the two is what keeps every existing
  // bumper logo the size it is. On the cap the centre field's wall is the weight NUMBERS either side
  // of the mark, and taking the larger let it grow straight through them.
  //   Keyed on the flag rather than on "does this zone declare a band", which was the first cut and
  // was wrong for a reason worth keeping: ZONE_PLATE declares one too — its band is simply soft.
  let capW = fit
    ? (zone.hardWall === true ? fit.w : Math.max(fit.w, box))
    : (aspect >= 1 ? box : box * aspect);
  // ── AND A RADIAL CAP AT TOP/BOTTOM (Ryan 2026-07-29) ────────────────────────────────────────
  // Everything above caps the TANGENTIAL direction. At a top/bottom slot the mark's HEIGHT is the
  // radial one, and it was uncapped: the tangential floor sets `drawW` and then `drawH = drawW /
  // aspect` follows it, so the taller the mark the further it ran out of the ring. Measured at the
  // top slot against ring B (114.8–195.3): aspect 0.7 spanned 112.3–197.7, and 0.33 spanned
  // 64.4–245.7 — straight over the zinc hub and past the plate's outer edge. The corner-balance
  // below could not save it either; with `drawH` wider than the annulus there is no centre that
  // fits, and its `Ri + hh` clamp then pushes the OUTER edge out.
  //   `zone.boxSide` is the slot's radial allowance, so `boxSide · aspect` is the widest this mark
  // may be drawn before its height exceeds it. Deliberately NOT `fit.h` (the exact annulus fit):
  // that is a hair tighter than the square box, and letting a near-square mark's corners overhang
  // it is a decision this function already made on purpose — capping there would quietly shave ~4%
  // off every square logo, which was explicitly not the ask.
  //   Consequently this is a NO-OP for every mark at or wider than square (`boxSide · aspect` is
  // then never the smaller term) and for left/right slots (`fit` is null there — the radial
  // direction is the WIDTH, which `capW` already bounds). Only a genuinely tall top/bottom mark
  // moves, which is exactly the set that was out of bounds.
  if (fit) capW = Math.min(capW, zone.boxSide * aspect);
  // ── A WIDTH WALL THAT IS NOT A RADIUS (2026-07-30, the cap zone) ────────────────────────────────
  // Everything above bounds a mark against CIRCLES about the plate centre, which is the whole truth
  // on a ring face and only half of it on a disc. A logo dead-centre with a weight number either
  // side of it is bounded VERTICALLY by the face and HORIZONTALLY by the numbers — two different
  // distances, and no radius expresses that pair. Modelling the centre field as the largest circle
  // that clears the numbers would be sound and badly lossy: it would cap a mark at a third of a face
  // that has room for two thirds of it.
  //   So a zone may state a flat width wall for a slot, and only the cap's centre does. Left unset
  // everywhere else, which is why no ring plate can feel this.
  if (zone.maxW > 0) capW = Math.min(capW, zone.maxW);
  // …and a FLOOR for the same case, so the sibling-weight size-match can't give the room straight
  // back. Mirrors what SLOT_TANGENTIAL_FILL does for a top/bottom mark; it can only ever grow one.
  if (sideWide) {
    const floorW = capW * SIDE_WIDE_FILL;
    if (drawW < floorW) { drawW = floorW; drawH = drawW / aspect; }
  }
  // Everything to this point is the AUTO fit — the size the app would choose on its own, weight
  // size-match and legibility floors included. It is no longer what the dial multiplies (see below);
  // it is what the dial's DEFAULT is seeded from, so it is returned rather than discarded.
  const autoW = drawW;
  // Manual logo size (Ryan 2026-07-25) — a MULTIPLIER on everything above, never an absolute.
  // The auto-fit is what keeps a logo inside ring B, balanced against its sibling weight and clear
  // of the grooves; an absolute size would fight all of it. So the buyer's dial rides on top, and
  // the result is re-capped at the room this slot actually has — the control can never push art off
  // the paintable surface no matter how far it's dragged.
  //   APPLIED LAST on the way down (Ryan 2026-07-25: "something's blocking me from decreasing the
  // logo size to be way smaller"). It used to run BEFORE the two legibility floors, so on a wide or
  // side-slotted mark `SIDE_WIDE_FILL` immediately grew the logo back to 88% of the slot and the
  // dial did nothing below that — you could drag it to 50% and watch nothing happen.
  //   Those floors exist to stop the AUTOMATIC path printing a wide mark microscopically (equal-
  // visual-size matches the diagonal, and a wide logo sharing the weight's diagonal has almost no
  // height). They were never meant to overrule a deliberate choice: a buyer who asks for a small
  // logo has answered the legibility question themselves. So the floors shape the auto result, and
  // the dial has the last word under it. The CAP still binds — that one isn't taste, it's the edge
  // of the paintable surface.
  // ── THE DIAL MEASURES THE ROOM, NOT THE AUTO-FIT (Ryan 2026-07-28) ──────────────────────────
  // "100% for a logo size shouldn't mean different things depending on the numbering style chosen."
  //
  // It did, and everything above is why: the auto-fit includes the sibling-weight SIZE-MATCH, so it
  // moves when the number style moves. Multiplying the buyer's scale by it made `sizeScale` a
  // fraction of a quantity that changes underfoot — a 0.78 mark at "100%" painted anywhere between
  // 30.0 and 38.6 depending on which number style was selected (a 29% swing; a 0.5 mark swings 56%).
  //
  // So the scale now multiplies `room` — a fixed share of `capW`, which depends only on the slot,
  // the zone and the mark's own aspect. Nothing about the weight enters it, so 100% is the same
  // painted size on every number style, by construction.
  //
  // WHY 0.9 AND NOT 1.0, measured rather than chosen: on every logo where the size-match does NOT
  // bite — aspect 1.0, 1.6, 2.0, where the annulus floors pin the fit — today's 100% is already
  // exactly 90% of `capW`. Pegging there reproduces the size Ryan already likes, pixel for pixel,
  // for those marks, while giving the ~11% of headroom above 100% he asked for ("so long as
  // increasing a % does not result in something going out of bounds… you should be able to increase
  // it to 105%, 110%"). `capW` is that bound, and the clamp below is what makes 111% the hard stop.
  //
  // ⚠ THIS IS ALSO WHAT DISCONNECTS A HAND-SIZED LOGO FROM THE NUMBER STYLE, and it does it by
  // DELETING the special case rather than adding one: design-panel used to correct `sizeScale` on a
  // style change to hold the painted size, because the base underneath it had moved. The base no
  // longer moves, so there is nothing to correct — a stored scale paints the same size whatever the
  // number style is. The auto-fit's job is now purely to seed the DEFAULT (see seedDefaultSizes),
  // which is exactly the relationship he described: connected until you touch it, then not.
  const room = capW * LOGO_ROOM_FRACTION;
  let lscale = Number.isFinite(element.sizeScale) ? Math.min(Math.max(element.sizeScale, LOGO_SCALE_MIN), legacy ? LOGO_SCALE_MAX : LOGO_SCALE_REACH) : 1;
  // …and the arc text may claw back the part of that above 100% (Ryan 2026-07-29) — see the
  // MIN_ARC_GAP_DEG block. `ceiling` is never below 1, so this cannot touch a logo inside the safe
  // zone; it only ever undoes bonus headroom the words have taken over.
  if (Number.isFinite(ceiling)) lscale = Math.min(lscale, Math.max(1, ceiling));
  // THE STOP (Ryan 2026-09-26, see `LOGO_SCALE_REACH`): where the INK meets the band when the hull
  // says so and that is further than the box; otherwise EXACTLY the old stop — `room · 110%`, under
  // `capW` — so a mark with no ink answer paints what it always painted at every dial value.
  // `legacy` asks for the old stop outright; only the dial's readout re-basing asks that.
  const reachW = legacy ? 0 : logoInkReachW(element, aspect, zone, theta);
  const stopW = reachW > capW ? reachW : Math.min(capW, room * LOGO_SCALE_MAX);
  drawW = Math.min(room * lscale, stopW);
  drawH = drawW / aspect;
  return { w: drawW, h: drawH, autoW, capW };
}

// ── element: logo ─────────────────────────────────────────────────────────────
function drawLogo(defs, body, element, uid, ringClipId, balanceDelta = 0, discClipId, zoneIn = ZONE_PLATE, matchBox = null, ceiling = null) {
  const zone = zoneForSlot(zoneIn, element && element.slot); // the band less any rim-arc lane above it
  const logo = element.logo;
  if (!logo || !logo.dataUrl) return;
  // Composite the PEELED variant when the logo-outline peel is active (auto-conflict on
  // a dyed plate, or a manual band removal) — else the original. `renderUrl` is a
  // transient, regenerated raster (logo-outline.js / main.syncLogoPeel); never serialized.
  const src = logo.renderUrl || logo.dataUrl;
  const big = element.big === true;
  const faceMode = big && element.bigFace === 'disc';
  // When an added ring grew the raster, its padded aspect (renderAspect) governs — else
  // the original logo aspect. Peels don't change dimensions, so renderAspect stays null.
  const aspect = (logo.renderUrl && Number.isFinite(logo.renderAspect)) ? logo.renderAspect : resolveLogoAspect(logo);
  // A big logo is positioned by its own BOX (bigLogoImageBox / fullFaceLogoBox) and
  // clipped to the ring or the disc — it has no anchor point at all, and rotates
  // about the plate centre. Only the plain path anchors, and it drifts with the
  // auto-balance (§2.4). This used to read `big ? slotAnchor(...) : pointAnchor(...)`
  // and then discard the slotAnchor result on both big branches; slotAnchor had no
  // other caller, so it's gone and `null` now says outright that big has no anchor.
  const anchor = big ? null : pointAnchor(element, balanceDelta, zone);

  const parts = [];
  if (faceMode) {
    // Full-face logo (dumbbell family): centered, inscribes the WHOLE plate
    // diameter, clipped to the plate's outer circle (no ring/hub reasoning).
    const { x, y, w, h } = fullFaceLogoBox(aspect, element);
    parts.push(el('image', { href: src, x, y, width: w, height: h, preserveAspectRatio: 'none' }));
  } else if (big) {
    // Big logo: sized to the full paintable-ring height (+¼" bleed) on one side,
    // inner edge crossing ~75% of the hub, then CLIPPED to ring B — the only
    // paintable surface. preserveAspectRatio 'none' because the box IS the logo
    // aspect (bigLogoImageBox computes w = h·aspect), so no distortion.
    const { x, y, w, h } = bigLogoImageBox(element.slot, aspect, element);
    parts.push(el('image', { href: src, x, y, width: w, height: h, preserveAspectRatio: 'none' }));
  } else {
    let { cx, cy } = anchor;
    const { w: drawW0, h: drawH0 } = logoDrawSize(element, aspect, zone, matchBox, pointAngle(element, balanceDelta, zone), ceiling);
    let drawW = drawW0;
    let drawH = drawH0;
    // KEEP THE CORNERS IN (Ryan 2026-07-25). The slot anchor sits at `zone.anchorR` and the mark is
    // centred on it, so on a ROUND plate a wide logo's outer CORNERS reach further out than its top
    // edge does: the mid-top can look comfortably inside the ring while the two corners are about to
    // leave it. Sizing already knows this (`slotAnnulusBox` solves the same triangle) but it is
    // applied only as a floor/ceiling and deliberately lets corners overhang the square box.
    //   Rather than shrink the mark to fix that, slide it radially INWARD until the corners clear —
    // down for a top slot, up for a bottom one. Size is preserved and only the centre moves, never
    // outward, so nothing that fits today can be pushed out.
    //   Only where the width is TANGENTIAL (top/bottom). At left/right the long axis runs radially
    // and this geometry doesn't apply — the same case `slotAnnulusBox` declines.
    let lx = cx;
    let ly = cy;
    let aR = zone.anchorR || 0;
    // PUSH TO THE RIM FIRST, then let the corner-balance below have its say about what is left.
    // Order matters: the balance slides a piece INWARD to keep its corners in, so running it on an
    // un-pushed anchor and pushing afterwards would undo it.
    if (zone.hangEdge > 0 && aR > 0) {
      const theta = (Math.atan2(cy - CENTER, cx - CENTER) * 180) / Math.PI;
      const out = edgeHangRadius(drawW0, drawH0, theta, zone.hangEdge, aR);
      if (out > aR) {
        const k = out / aR;
        cx = CENTER + (cx - CENTER) * k;
        cy = CENTER + (cy - CENTER) * k;
        lx = cx;
        ly = cy;
        aR = out;
        // The anchor travels with it, so an inverted piece (Four Corners' bottom logo) spins about
        // where it actually IS — the same reason the corner-balance below writes the anchor back.
        anchor.cx = cx;
        anchor.cy = cy;
      }
    }
    // ⛔ …EXCEPT ON A MARK WHOSE CORNERS ARE MEANT TO LEAVE (2026-07-30). The cap's badge is sized
    // against a circle LARGER than the canvas precisely so its corners trim against the disc — that
    // is what `CAP_BADGE_CORNER_R` buys and what the disc clip exists to cut. Sliding it inward to
    // save those corners undoes the whole arrangement, and it did: a 2.5:1 badge was dragged 72u
    // DOWN the face, which then broke the top/bottom margin balance built on top of it. The clamp is
    // also reconstructing a ring annulus from `boxSide` — a shape this zone does not have.
    if (element.bigBox !== true && aR > 0 && drawW > 0 && drawH > 0) {
      const ux = (cx - CENTER) / aR; // unit vector out along the slot (includes any balance drift)
      const uy = (cy - CENTER) / aR;
      if (Math.abs(uy) > Math.abs(ux)) {
        const d = cornerBalanceShift(aR, drawW, drawH, zone.boxSide);
        if (d < -0.01) {
          lx = cx + d * ux;
          ly = cy + d * uy;
          // Move the anchor too, so an upside-down bottom logo spins about where it actually IS.
          anchor.cx = lx;
          anchor.cy = ly;
        }
      }
    }
    // …and the drift's margin bill, exactly as `drawWeight` pays it — see `capDriftPullIn`.
    {
      const thetaNow = (Math.atan2(cy - CENTER, cx - CENTER) * 180) / Math.PI;
      const back = capDriftPullIn(zone, element, zone, thetaNow, { w: drawW, h: drawH }, aR);
      if (back < aR - 0.01) {
        const k = back / aR;
        lx = CENTER + (lx - CENTER) * k;
        ly = CENTER + (ly - CENTER) * k;
        anchor.cx = lx;
        anchor.cy = ly;
      }
    }
    if (!logo.transparentBg) {
      // White backing for opaque uploads so they read on the black ring. An ELLIPSE, not a circle
      // (Ryan 2026-07-25): a circle is sized off the LONG side, which for a now-properly-wide mark
      // would swell past the ring's edges and bleed onto rings A/C. Identical to the old circle for
      // a square logo (rx == ry), and it hugs the mark for every other shape.
      parts.push(el('ellipse', { cx: lx, cy: ly, rx: drawW / 2 + 2, ry: drawH / 2 + 2, fill: '#FFFFFF' }));
    }
    parts.push(
      el('image', { href: src, x: lx - drawW / 2, y: ly - drawH / 2, width: drawW, height: drawH, preserveAspectRatio: 'xMidYMid meet' }),
    );
  }

  let inner = parts.join('');
  // upsideDown → rotate the whole logo 180° about the plate center (big) or its
  // own slot anchor (Ryan 2026-07-13 — e.g. logo top + bottom, bottom inverted).
  if (element.upsideDown === true) {
    const a = big ? { cx: CENTER, cy: CENTER } : anchor;
    inner = `<g transform="rotate(180 ${a.cx} ${a.cy})">${inner}</g>`;
  }
  // Big logo prints ONLY on the paintable ring — clip to the ring-B annulus so
  // the hub + rings A/C stay bare (in 3D the ring-B geometry masks it too).
  // Full-face logo clips to the plate's outer circle instead (no hub to avoid).
  // …and a `bigBox` badge, which is deliberately sized past the canvas so its corners trim (see
  // CAP_BADGE_CORNER_R). Without this it would simply paint over the edge of the artwork.
  if ((faceMode || element.bigBox === true) && discClipId) inner = `<g clip-path="url(#${escAttr(discClipId)})">${inner}</g>`;
  else if (big && ringClipId) inner = `<g clip-path="url(#${escAttr(ringClipId)})">${inner}</g>`;
  body.push(inner);
}

// ── plate chrome (flat 2D-editing context; ignored by the 3D ring sampler) ────
function drawPlateChrome(defs, body, uid, bodyColor) {
  const gid = `${uid}-hub`;
  defs.push(
    `<radialGradient id="${escAttr(gid)}" cx="42%" cy="38%" r="70%">` +
      `<stop offset="0%" stop-color="#e9ebee"/>` +
      `<stop offset="55%" stop-color="#c7cace"/>` +
      `<stop offset="100%" stop-color="#9aa0a6"/>` +
      `</radialGradient>`,
  );
  // Plate disc = the rubber body color (black by default; Pantone-dyed for B6).
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R, fill: bodyColor }));
  // Groove shadow lines at the ring-B boundaries.
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: RING_B_OUTER, fill: 'none', stroke: '#000', 'stroke-width': 1.2, opacity: 0.6 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: RING_B_INNER, fill: 'none', stroke: '#000', 'stroke-width': 1.2, opacity: 0.6 }));
  // Zinc hub + bore.
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: HUB_R, fill: `url(#${gid})` }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: BORE_R, fill: '#e5e7eb' }));
  // 6 bolts on the bolt circle (two on the horizontal axis).
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const bx = CENTER + BOLT_CIRCLE * Math.cos(a);
    const by = CENTER + BOLT_CIRCLE * Math.sin(a);
    body.push(el('circle', { cx: bx, cy: by, r: BOLT_HEAD_R, fill: '#b9bdc2', stroke: '#7f8489', 'stroke-width': 0.8 }));
    body.push(el('circle', { cx: bx, cy: by, r: BOLT_HEAD_R * 0.42, fill: '#6b7075' }));
  }
}

// ── product-aware 2D chrome (Ryan 2026-07-21) ─────────────────────────────────
// Each product family draws its OWN face behind the 2D layout previews so a dumbbell
// preview looks like a dumbbell end-cap, a grip plate like a grip plate — not a bumper
// for everything. Plus a shared top-lit "sheen" for realism (see faceSheen).
const D2R = Math.PI / 180;
const polarX = (r, deg) => CENTER + r * Math.cos(deg * D2R);
const polarY = (r, deg) => CENTER + r * Math.sin(deg * D2R);
/** Annular-sector path (inner ri, outer ro, from a1°→a2° screen). */
function annularSector(ri, ro, a1, a2) {
  const large = Math.abs(a2 - a1) > 180 ? 1 : 0;
  return (
    `M ${polarX(ro, a1).toFixed(2)} ${polarY(ro, a1).toFixed(2)} ` +
    `A ${ro} ${ro} 0 ${large} 1 ${polarX(ro, a2).toFixed(2)} ${polarY(ro, a2).toFixed(2)} ` +
    `L ${polarX(ri, a2).toFixed(2)} ${polarY(ri, a2).toFixed(2)} ` +
    `A ${ri} ${ri} 0 ${large} 0 ${polarX(ri, a1).toFixed(2)} ${polarY(ri, a1).toFixed(2)} Z`
  );
}

/** Annular sector with ROUNDED corners (radius rc), for the grip plate's hand cutouts — the
 *  real cutouts are stadium-ish, not sharp wedges (Ryan 2026-07-22). Each corner is a quadratic
 *  round through the true corner; the radial edges + arcs are shortened to meet the rounds. */
function roundedAnnularSector(ri, ro, a1, a2, rc) {
  const dAo = (rc / ro) / D2R; // angular inset at the outer radius
  const dAi = (rc / ri) / D2R; // angular inset at the inner radius
  const P = (r, deg) => `${polarX(r, deg).toFixed(2)} ${polarY(r, deg).toFixed(2)}`;
  const large = Math.abs((a2 - dAo) - (a1 + dAo)) > 180 ? 1 : 0;
  return (
    `M ${P(ro, a1 + dAo)} ` +
    `A ${ro} ${ro} 0 ${large} 1 ${P(ro, a2 - dAo)} ` +          // outer arc
    `Q ${P(ro, a2)} ${P(ro - rc, a2)} ` +                       // round the outer-a2 corner
    `L ${P(ri + rc, a2)} ` +                                    // radial edge at a2
    `Q ${P(ri, a2)} ${P(ri, a2 - dAi)} ` +                      // round the inner-a2 corner
    `A ${ri} ${ri} 0 ${large} 0 ${P(ri, a1 + dAi)} ` +          // inner arc (back)
    `Q ${P(ri, a1)} ${P(ri + rc, a1)} ` +                       // round the inner-a1 corner
    `L ${P(ro - rc, a1)} ` +                                    // radial edge at a1
    `Q ${P(ro, a1)} ${P(ro, a1 + dAo)} Z`                       // round the outer-a1 corner
  );
}

/** A top-lit sheen + soft bottom shade over the whole face — cheap realism (two radial
 *  gradients, no filter), drawn UNDER the design so the artwork stays crisp. `clipId`
 *  (optional) confines it to a masked face, e.g. the grip plate's holes. */
function faceSheen(defs, body, uid, clipId) {
  const hi = `${uid}-sheen`, lo = `${uid}-shade`;
  defs.push(
    `<radialGradient id="${escAttr(hi)}" cx="34%" cy="26%" r="74%">` +
      `<stop offset="0%" stop-color="#fff" stop-opacity="0.2"/>` +
      `<stop offset="46%" stop-color="#fff" stop-opacity="0.05"/>` +
      `<stop offset="100%" stop-color="#fff" stop-opacity="0"/></radialGradient>`,
    `<radialGradient id="${escAttr(lo)}" cx="64%" cy="84%" r="72%">` +
      `<stop offset="0%" stop-color="#000" stop-opacity="0.26"/>` +
      `<stop offset="58%" stop-color="#000" stop-opacity="0.07"/>` +
      `<stop offset="100%" stop-color="#000" stop-opacity="0"/></radialGradient>`,
  );
  const clip = clipId ? ` clip-path="url(#${escAttr(clipId)})"` : '';
  body.push(`<g${clip}>` +
    el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R, fill: `url(#${lo})` }) +
    el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R, fill: `url(#${hi})` }) +
    '</g>');
}

/** Economy / TRAINING bumper (Ryan 2026-07-22) — the rubber body has NO large zinc hub and
 *  NO bolts, just a SMALL steel bore flange (a thin metal ring around the collar hole). Same
 *  recessed ring-B brand channel as the competition plate; that's the only steel on the face. */
function drawEconomyPlateChrome(defs, body, uid, bodyColor) {
  const gid = `${uid}-flange`;
  defs.push(
    `<radialGradient id="${escAttr(gid)}" cx="42%" cy="38%" r="70%">` +
      `<stop offset="0%" stop-color="#e9ebee"/><stop offset="60%" stop-color="#c7cace"/><stop offset="100%" stop-color="#9aa0a6"/></radialGradient>`,
  );
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R, fill: bodyColor }));
  // Ring-B design-channel grooves (the brand face is recessed here, same as competition).
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: RING_B_OUTER, fill: 'none', stroke: '#000', 'stroke-width': 1.2, opacity: 0.6 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: RING_B_INNER, fill: 'none', stroke: '#000', 'stroke-width': 1.2, opacity: 0.6 }));
  // Small steel bore flange (≈1.6× the bore) + the collar hole — NO big hub, NO bolts.
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: BORE_R * 1.6, fill: `url(#${gid})` }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: BORE_R * 1.6, fill: 'none', stroke: '#000', 'stroke-width': 0.8, opacity: 0.35 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: BORE_R, fill: '#e5e7eb' }));
}

/** Dumbbell / fixed-barbell END-CAP — a clean round face (no hub/bore/bolts). The RUBBER head
 *  recesses the branded flat face inside a RAISED rubber rim (the real head's chamfer + ledge +
 *  riser), so the art doesn't reach the outer edge — drawn as a lit raised rim + a recess wall
 *  so the design clearly sits INSET. The urethane head is flatter (Ryan 2026-07-21/22). */
// Dumbbell/barbell brand-face radius as a FRACTION OF THE HEAD (from geometry/dumbbell.js):
// the RUBBER head recesses the design onto a small centre disc inside a WIDE raised rubber ring
// (HR − chamfer − ledge ≈ 0.068/0.085); the URETHANE is a large flat face (HR − edge-round ≈
// 0.0805/0.085). The 2D preview both DRAWS the ring here AND scales the design to this fraction
// (capDesignScale) so the flat preview matches the 3D head, where the design fills the BrandFace.
const CAP_BRAND_FRAC_RUBBER = 0.8;
const CAP_BRAND_FRAC_URETHANE = 0.947;
const capBrandFrac = (rubber) => (rubber ? CAP_BRAND_FRAC_RUBBER : CAP_BRAND_FRAC_URETHANE);

function drawCapChrome(defs, body, uid, bodyColor, rubber) {
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R, fill: bodyColor }));
  const faceR = capBrandFrac(rubber) * PLATE_R; // recessed design-disc radius
  const p0 = ((faceR / PLATE_R) * 100).toFixed(2);
  const crown = Math.min(99, (faceR / PLATE_R + (1 - faceR / PLATE_R) * 0.4) * 100).toFixed(2);
  // OUTER RAISED RUBBER RING (faceR → edge): its crown catches top light, then it cones down to
  // a darker outer edge — reads PROUD of the recessed centre. On rubber it's a WIDE ring.
  const rid = `${uid}-caprim`;
  defs.push(
    `<radialGradient id="${escAttr(rid)}" cx="50%" cy="50%" r="50%">` +
      `<stop offset="${p0}%" stop-color="#000" stop-opacity="0"/>` +
      `<stop offset="${p0}%" stop-color="#fff" stop-opacity="0.10"/>` +
      `<stop offset="${crown}%" stop-color="#fff" stop-opacity="0.16"/>` +
      `<stop offset="100%" stop-color="#000" stop-opacity="0.34"/></radialGradient>`,
  );
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R, fill: `url(#${rid})` }));
  // RECESSED CENTRE (0 → faceR): a soft shadow near the wall so it sits BELOW the ring.
  const rec = `${uid}-caprec`;
  defs.push(
    `<radialGradient id="${escAttr(rec)}" cx="50%" cy="50%" r="${p0}%">` +
      `<stop offset="70%" stop-color="#000" stop-opacity="0"/>` +
      `<stop offset="100%" stop-color="#000" stop-opacity="0.32"/></radialGradient>`,
  );
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: faceR, fill: `url(#${rec})` }));
  // Recess WALL: a dark groove where the ring steps down to the face + a lit lip on the rim side.
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: faceR, fill: 'none', stroke: '#000', 'stroke-width': 2.4, opacity: 0.6 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: faceR + 2, fill: 'none', stroke: '#fff', 'stroke-width': 1.2, opacity: 0.14 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R * 0.99, fill: 'none', stroke: '#000', 'stroke-width': 1.2, opacity: 0.3 }));
}

/** Grip plate — a disc with THREE hand cutouts (at screen 90 / 210 / 330, the holes the
 *  buyer's logo + weights sit BETWEEN) + a centre Olympic bore, PUNCHED transparent (they
 *  read as real holes against the card, on any plate color) + a clean outer rim groove. */
function drawGripChrome(defs, body, uid, bodyColor) {
  // Fixed 50.4 mm Olympic bore + metal collar bushing (geometry/grip-plate.js), as fractions of
  // the 45 lb plate the preview shows: bore 0.112R, collar 0.144R (Ryan 2026-07-22).
  const boreR = 0.112 * PLATE_R;
  const collarR = 0.144 * PLATE_R;
  // Three hand cutouts with ROUNDED corners (stadium-ish), at screen 90/210/330 — the gaps the
  // buyer's logo + weights sit BETWEEN. Punched transparent so they read on any plate colour.
  const cutouts = [90, 210, 330].map((c) => roundedAnnularSector(0.6 * PLATE_R, 0.85 * PLATE_R, c - 26, c + 26, 0.05 * PLATE_R));
  // Rubber face = outer disc MINUS cutouts MINUS the metal COLLAR hole (the centre is steel).
  const facePath = `${circleSubPath(CENTER, CENTER, PLATE_R)} ${cutouts.join(' ')} ${circleSubPath(CENTER, CENTER, collarR)}`;
  const clipId = `${uid}-gripface`;
  defs.push(`<clipPath id="${escAttr(clipId)}"><path d="${facePath}" clip-rule="evenodd"/></clipPath>`);
  body.push(`<path d="${facePath}" fill="${escAttr(bodyColor)}" fill-rule="evenodd"/>`);
  // METAL COLLAR RING: a steel bushing from the bore out to the collar edge; the Olympic bore
  // (0 → boreR) stays a real transparent hole inside it (even-odd).
  const gid = `${uid}-gripcollar`;
  defs.push(
    `<radialGradient id="${escAttr(gid)}" cx="42%" cy="38%" r="70%">` +
      `<stop offset="0%" stop-color="#e9ebee"/><stop offset="60%" stop-color="#c7cace"/><stop offset="100%" stop-color="#9aa0a6"/></radialGradient>`,
  );
  body.push(`<path d="${circleSubPath(CENTER, CENTER, collarR)} ${circleSubPath(CENTER, CENTER, boreR)}" fill="url(#${escAttr(gid)})" fill-rule="evenodd"/>`);
  // Groove + hairline around every cut edge, the collar + bore edges, and the outer rim, for depth.
  const edge = (d) => { body.push(`<path d="${d}" fill="none" stroke="#000" stroke-width="1.6" stroke-opacity="0.55"/>`); body.push(`<path d="${d}" fill="none" stroke="#fff" stroke-width="0.8" stroke-opacity="0.1"/>`); };
  cutouts.forEach(edge);
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: collarR, fill: 'none', stroke: '#000', 'stroke-width': 1.2, opacity: 0.45 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: boreR, fill: 'none', stroke: '#000', 'stroke-width': 1.4, opacity: 0.55 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: 0.906 * PLATE_R, fill: 'none', stroke: '#000', 'stroke-width': 1.4, opacity: 0.4 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R * 0.985, fill: 'none', stroke: '#fff', 'stroke-width': 1.2, opacity: 0.1 }));
  faceSheen(defs, body, uid, clipId); // sheen confined to the holed face
}

/**
 * THE CUTOUT-FREE GRIP PLATES (2.5 / 5 lb) — a plain annular face (Ryan 2026-08-02).
 *
 * These two shipped their geometry, their zone and their own six layouts on 2026-07-31, and the
 * flat 2D face was the piece left behind: `drawGripChrome` hard-draws three hand holes, so every
 * layout thumbnail for a 2.5 or a 5 advertised a plate with cutouts the buyer will never receive —
 * and the small-grip presets (Four Corners et al) place art at `bottom`, which on the drawn face
 * was a hand hole. Ryan, seeing it: *"need to add new SVG templates for the 2D layouts for 2.5 and
 * 5 lb grip plates."*
 *
 * Ryan's own description of the face, 2026-07-31: *"there's an outer edge which doesn't get art,
 * and inner ring which doesn't get art, then the space in between gets the art. A lot like the
 * training bumper in that regard."* So: rim band, hub ring, art annulus between them, and the
 * Olympic bore + steel collar through the middle like every other plate.
 *
 * ⛔ EVERY RADIUS IS DERIVED FROM THE PLATE'S REAL SIZE — none of them may be a constant fraction
 * of R, and that is the entire reason this function takes a weight.
 *
 * The bore, the collar and the two no-art bands are ABSOLUTE physical distances (the same steel
 * parts and the same measured rings on both plates), while the drawing is normalised to `PLATE_R`.
 * A 2.5 and a 5 therefore disagree about what those facts are AS RATIOS — the hub reads 0.431R on
 * one and 0.353R on the other, the rim band 0.122R against 0.102R. `drawGripChrome` above gets
 * away with fixed fractions because it only ever draws one plate's proportions (the 45's) and the
 * cutouts really are ratios; here a fraction fitted to either plate is wrong on the other by a
 * tenth of its radius. Same measurement record as `SMALL_GRIP_HUB_RADIUS` in geometry/grip-plate.js.
 */
function drawSmallGripChrome(defs, body, uid, bodyColor, weight) {
  // The plate's REAL radius, in inches, to divide the absolute distances by.
  const dia = GRIP_DIAMETER_IN_LB[weight] ?? GRIP_DIAMETER_IN_LB[5];
  const rIn = dia / 2;
  const frac = (inches) => (inches / rIn) * PLATE_R;
  const boreR = frac(BORE_RADIUS_IN);
  const collarR = frac(COLLAR_RADIUS_IN);
  const hubR = frac(SMALL_GRIP_HUB_IN);          // art starts outside this raised ring
  const rimInnerR = frac(rIn - SMALL_GRIP_RIM_BAND_IN); // …and stops inside this raised band

  // Face = the whole disc MINUS the collar hole. No cutouts: that is the point of these two.
  const facePath = `${circleSubPath(CENTER, CENTER, PLATE_R)} ${circleSubPath(CENTER, CENTER, collarR)}`;
  const clipId = `${uid}-smgripface`;
  defs.push(`<clipPath id="${escAttr(clipId)}"><path d="${facePath}" clip-rule="evenodd"/></clipPath>`);
  body.push(`<path d="${facePath}" fill="${escAttr(bodyColor)}" fill-rule="evenodd"/>`);
  // The two RAISED rings that bound the printable band. Drawn as a lit top edge over a dark
  // shadow — the same read the bumper's rings get — rather than as flat outlines, so the annulus
  // between them looks recessed and the buyer can see where their art may go.
  const raised = (r) => {
    body.push(el('circle', { cx: CENTER, cy: CENTER, r, fill: 'none', stroke: '#000', 'stroke-width': 2.2, opacity: 0.5 }));
    body.push(el('circle', { cx: CENTER, cy: CENTER, r: r - 1.2, fill: 'none', stroke: '#fff', 'stroke-width': 1, opacity: 0.12 }));
  };
  raised(hubR);
  raised(rimInnerR);
  // METAL COLLAR: a steel bushing from the bore out to the collar edge, with the Olympic bore left
  // as a real transparent hole inside it (even-odd) — identical treatment to the cutout plates', so
  // the two grip faces read as the same product family.
  const gid = `${uid}-smgripcollar`;
  defs.push(
    `<radialGradient id="${escAttr(gid)}" cx="42%" cy="38%" r="70%">` +
      `<stop offset="0%" stop-color="#e9ebee"/><stop offset="60%" stop-color="#c7cace"/><stop offset="100%" stop-color="#9aa0a6"/></radialGradient>`,
  );
  body.push(`<path d="${circleSubPath(CENTER, CENTER, collarR)} ${circleSubPath(CENTER, CENTER, boreR)}" fill="url(#${escAttr(gid)})" fill-rule="evenodd"/>`);
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: collarR, fill: 'none', stroke: '#000', 'stroke-width': 1.2, opacity: 0.45 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: boreR, fill: 'none', stroke: '#000', 'stroke-width': 1.4, opacity: 0.55 }));
  body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R * 0.985, fill: 'none', stroke: '#fff', 'stroke-width': 1.2, opacity: 0.1 }));
  faceSheen(defs, body, uid, clipId);
}

/** Draw the flat 2D chrome for a product MODEL id, then the shared top-lit sheen. `opts`
 *  carries the finish/variant: `economy` (training bumper → no zinc hub), `rubber`
 *  (dumbbell/barbell → recessed brand face) and `weight` (the grip family is TWO faces). */
function drawChrome(product, defs, body, uid, bodyColor, opts = {}) {
  if (product === 'grip') {
    // Two faces, one product — see SMALL_GRIP_MAX_DIA_IN. `isSmallGrip` is the one predicate the
    // whole codebase asks, so the 2D asks it too rather than inventing a second threshold.
    if (isSmallGrip(opts.weight ?? 45)) drawSmallGripChrome(defs, body, uid, bodyColor, opts.weight ?? 45);
    else drawGripChrome(defs, body, uid, bodyColor);
    return; // both are self-contained (holes + clipped sheen)
  }
  if (product === 'dumbbell' || product === 'barbell') drawCapChrome(defs, body, uid, bodyColor, opts.rubber === true);
  else if (opts.economy === true) drawEconomyPlateChrome(defs, body, uid, bodyColor);
  else drawPlateChrome(defs, body, uid, bodyColor);
  faceSheen(defs, body, uid, null);
}

// ── public API ────────────────────────────────────────────────────────────────
/**
 * Renders a Design Profile to a complete, self-contained SVG string.
 *
 * @param {import('./design-profile.js').DesignProfile} profile
 * @param {Object} [opts]
 * @param {number} [opts.weight]     - override weightValue for the weight element.
 * @param {number} [opts.size=450]   - pixel width/height of the root <svg>.
 * @param {boolean} [opts.chrome=true] - draw the flat plate chrome (2D context).
 * @param {string} [opts.uid]        - stable id-namespacing token (for tests).
 * @param {string} [opts.product]    - MODEL id ('plate' | 'grip' | 'dumbbell' |
 *   'barbell'; products.js's MODELS keys) — picks the ZONE (where each slot lands)
 *   AND the 2D CHROME (the face drawn behind the design: bumper rings/hub, a
 *   dumbbell/barbell end-cap, or the grip plate's cutouts — Ryan 2026-07-21).
 *   Omitted reads as the bumper. The 3D texture path passes chrome:false, so the
 *   product face only shows in the flat previews (layout thumbnails + first frame).
 * @param {string} [opts.bodyColorOverride] - force the ground fill to this hex instead
 *   of `profile.bodyColor` — for products that can't dye (Fable §4.3: dumbbells,
 *   barbells, grip plates are always black regardless of what the buyer's bumper
 *   is dyed). Callers building a texture for a non-dyeable product pass this;
 *   the profile itself is never mutated (a re-render for the bumper stays dyed).
 * @returns {string} SVG markup.
 */
export function renderDesignToSVGString(profile, opts = {}) {
  const p = profile || {};
  /* The render palette — the buyer's palette plus the hints `effectiveTextStyle` reads (the swatch
     `pool`, `logoOutlined`, `logoGap`, `logoBandsVisible`, `logoOuterTone/Weight`, `logoInkWeight`).
     ⛔ ONE BUILDER, THREE CALLERS — see `renderPaletteFor`. This was a hand-built object here, a
     second in the design panel and a third in the corpus runner, and on 2026-09-16 the runner's
     copy went stale the day a hint was added: the moat changed in the picture and not in the dump.
     ⚠ It is built BELOW `bodyColor` now, because half its hints are plate-dependent and there is
     no longer a two-stage build to hang them off. Nothing between here and there reads it. */
  const elements = Array.isArray(p.elements) ? p.elements : [];
  // ⚠ THE PRODUCT'S OWN REFERENCE, NOT A LITERAL 45 (2026-08-02). It labels the plate AND builds
  // the zone, and since `capZone` started reading the denomination a 45 on a dumbbell is a real
  // rung five below its sample — it would both mis-letter the face and compensate it. Unchanged
  // for the bumper and the grip, whose reference IS 45. See `zoneWeight`.
  const weight = zoneWeight(opts.product, p.weightValue, opts.weight);
  const size = opts.size ?? 450;
  const chrome = opts.chrome !== false;
  const uid = opts.uid ?? nextUid();

  const defs = [];
  const body = [];

  // Plate rubber color (B6 §4) — black default. In the flat 2D view it's the full
  // plate disc; for the 3D texture (chrome:false) it's the ring-B GROUND behind the
  // design (else the brand face samples the cleared-canvas black, not the dye).
  const bodyColor = normalizeHex(opts.bodyColorOverride) || normalizeHex(p.bodyColor) || DEFAULT_BODY_COLOR;
  const palette = renderPaletteFor(p, bodyColor);
  // HEIGHT-MAP mode (Ryan 2026-07-17): render the INK as a white-on-black height field
  // to drive a bump map — the design reads with ~⅓mm of physical depth in 3D at no
  // per-frame cost. Ground is flat black (low); the ink becomes opaque white (raised)
  // regardless of its real colours, via one feColorMatrix filter over the design group
  // (so nothing threads through the colour logic). No chrome — the 3D geometry is the
  // hub/rings, and only ring-B (the brand face) samples this texture.
  const heightMap = opts.heightMap === true;
  if (heightMap) body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R, fill: '#000' }));
  // ⚠ `weight` REACHES THE CHROME, not just the zone. The grip family is two different FACES under
  // one product — the cutout-free 2.5/5 are a plain annulus — so the chrome cannot be chosen from
  // the product id alone the way every other model's can.
  else if (chrome) drawChrome(opts.product, defs, body, uid, bodyColor, { economy: opts.economy === true, rubber: opts.rubber === true, weight }); // product-aware 2D face
  else body.push(el('circle', { cx: CENTER, cy: CENTER, r: PLATE_R, fill: bodyColor }));

  // ONE font for the whole plate (words + numbers) — Ryan 2026-07-11.
  const font = p.font || DEFAULT_FONT_ID;
  // The weight unit (lb|kg) — re-letters the weight mark (LB↔KG) on the plate.
  const unit = normUnit(p.unit);
  // Plate-wide caps mode (§2.7) — ALL CAPS unless the buyer chose "As typed".
  const allCaps = p.allCaps !== false;
  const soloArc = isSoloArc(elements); // only ONE arc word → it may use the wider span
  // WHERE this product's slots live. The bumper/dumbbell/barbell all resolve to the
  // ring zone (unchanged); the grip plate's slots are the gaps between its hand
  // cutouts, and its geometry varies by denomination — hence `weight`.
  // MUST be declared before the balance drift below, which now reads it to size a top/bottom
  // logo or weight in DEGREES about the ring (Ryan 2026-07-25) — and before the link-clamp, which
  // as of 2026-07-30 fits each arc to ITS OWN lane (a grip gap, not the bumper's whole ring).
  let zone = resolveZone(opts.product, weight, elements);
  // Link-clamp (Ryan 2026-07-22): arcs sharing a sizeScale (the top/bottom chain) paint at
  // ONE size — the smallest of their individual fits — so top & bottom match exactly.
  const arcClamp = sharedArcFonts(elements, font, allCaps, zone);
  // Size-match a plain (non-big) logo to a sibling weight lockup so they read as a balanced
  // pair (Ryan 2026-07-17, dominant-dimension model 2026-07-22). Computed once from the first
  // weight; drawLogo matches the logo's dominant dimension to this box's dominant dimension.
  let logoMatchBox = null;
  const weightEl = elements.find((e) => e && e.type === 'weight');
  const hasPlainLogo = elements.some((e) => e && e.type === 'logo' && e.big !== true && e.logo && e.logo.dataUrl);
  // Matched to the weight at its FACTORY size (DEFAULT_ELEMENT_SIZE_SCALE), not its dialled one (Ryan 2026-07-25: "if I adjust
  // the number way up or down, I see the logo change size too — the logo should stay locked in at
  // whatever it's at").
  //   The size-match is the DEFAULT relationship and it still holds exactly as before: a logo beside
  // a number opens sized against that number. But once the two dials exist, feeding the buyer's
  // weight dial into the match makes the number's control secretly a logo control as well — you
  // shrink the number and the logo shrinks with it, having never touched its dial. Reading the
  // weight at its factory scale keeps the default intact (nothing moves until someone dials) while making
  // each control own exactly one piece. The reverse direction was already clean: the logo's dial
  // multiplies only the logo.
  if (weightEl && hasPlainLogo) logoMatchBox = weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, unit);
  // WD 493 — on a dumbbell / bar / grip face the number's rings follow the logo's painted size (see `capTextInkScale`). The
  // match box is re-taken on the new zone so the logo is sized against the number as it will actually paint.
  {
    const textInk = capTextInkScale(p, elements, zone, logoMatchBox, opts.product, weight);
    if (textInk > 1) {
      zone = { ...zone, textInk };
      if (weightEl && hasPlainLogo) logoMatchBox = weightLockupBox({ ...weightEl, sizeScale: DEFAULT_ELEMENT_SIZE_SCALE }, weight, font, zone, unit);
    }
  }

  // Auto-balance drift (§2.4) — computed once, applied to left/right point anchors.
  // MUST follow logoMatchBox above: the drift sizes a side logo through it (Ryan 2026-07-25).
  const balanceDelta = computeBalanceDelta(elements, font, allCaps, arcClamp, zone, logoMatchBox, { weight, unit });

  // A big logo prints ONLY on the paintable ring — clip it to the ring-B annulus
  // (the hub + rings A/C never take ink; in 3D the ring-B geometry masks it too).
  // A full-face (bigFace:'disc') logo clips to the plate's outer circle instead —
  // no hub to avoid on a product without one (dumbbell family).
  let ringClipId = null;
  let discClipId = null;
  if (elements.some((e) => e && e.type === 'logo' && e.big === true && e.bigFace !== 'disc')) {
    ringClipId = `${uid}-ringclip`;
    defs.push(
      `<clipPath id="${escAttr(ringClipId)}" clip-rule="evenodd">` +
        `<path d="${ringBAnnulusPath()}" clip-rule="evenodd"/>` +
        `</clipPath>`,
    );
  }
  // …and a `bigBox` mark joins it (2026-07-30). It is sized against a circle LARGER than the canvas
  // so its corners may trim — see CAP_BADGE_CORNER_R — which is only sound if something does the
  // trimming. Same clip, same reason: on a face with no hub, the disc is the whole of the boundary.
  if (elements.some((e) => e && e.type === 'logo' && ((e.big === true && e.bigFace === 'disc') || e.bigBox === true))) {
    discClipId = `${uid}-discclip`;
    defs.push(
      `<clipPath id="${escAttr(discClipId)}" clip-rule="evenodd">` +
        `<path d="${circleSubPath(CENTER, CENTER, PLATE_R)}" clip-rule="evenodd"/>` +
        `</clipPath>`,
    );
  }

  // ── A BADGE AND ITS NUMBER SHARE ONE MARGIN (Ryan 2026-07-30: *"on Big Badge, make the gap from
  // top of logo to top of weight equal gap from bottom of weight number to bottom of weight"*) ────
  //
  // Equal margins top and bottom is the same statement as: the number's outer INK edge sits at the
  // same radius as the mark's top edge. Written that way it needs no reference to the face at all,
  // which is what makes it hold when either piece is resized.
  //
  // ⚠ IT CANNOT BE ANSWERED BY THE ZONE, which is why it is here. The mark's top depends on the
  // artwork's ASPECT and on where the size dial is standing — a zone is built before either is
  // known. So the zone owns the margins it can compute (`hangOuter`, for a number facing rim type)
  // and this owns the one it cannot. Same division of labour as `arcOuterRadius`.
  //
  // Measured through `logoDrawSize` and `pointAnchor` — the very calls `drawLogo` is about to make —
  // rather than re-derived, so the number cannot balance against a mark of a different size than the
  // one that gets painted. The mark's box overruns the disc at its CORNERS only (that is what
  // `CAP_BADGE_CORNER_R` buys and what the disc clip trims); its top EDGE is inside, so the box top
  // is the visible top.
  /**
   * THE PAINTED BOX OF A MARK, and where its anchor put it. Both are taken from the very calls
   * `drawLogo` is about to make rather than re-derived, so a number placed against a mark cannot be
   * placed against a mark of a different size than the one that gets painted.
   */
  const markGeom = (el) => {
    const lg = el.logo;
    const aspect = (lg.renderUrl && Number.isFinite(lg.renderAspect)) ? lg.renderAspect : resolveLogoAspect(lg);
    const d = logoDrawSize(el, aspect > 0 ? aspect : 1, zone, logoMatchBox, null, logoCeilingFor(p, el, { product: opts.product, weight }));
    if (!d || !(d.h > 0)) return null;
    const a = pointAnchor(el, balanceDelta, zone);
    // …AND WHERE THE CLAMP WILL PUT IT. `drawLogo` slides a wide mark inward so its corners balance,
    // and a follower measuring the un-shifted anchor is measuring a mark that will not be there: the
    // shift grows with the mark, so the "constant" gap drifted 11u across the size dial. Same
    // function the painter calls, so the two cannot disagree.
    const view = zoneForSlot(zone, el.slot);
    const aR = view.anchorR || 0;
    if (aR > 0 && el.bigBox !== true) {
      const ux = (a.cx - CENTER) / aR;
      const uy = (a.cy - CENTER) / aR;
      if (Math.abs(uy) > Math.abs(ux)) {
        const shift = cornerBalanceShift(aR, d.w, d.h, view.boxSide);
        if (shift < -0.01) { a.cx += shift * ux; a.cy += shift * uy; }
      }
    }
    return { d, a };
  };
  // AS PAINTED, not as stored — the cap-height ceiling can thin the stack, and a gap measured off
  // ink that isn't there opens by the difference. Only ever called with the weight element.
  const inkGrowth = (el) => weightInkGrowth(el, weight, font, zone, unit);

  // ── A NUMBER FACING A MARK FOLLOWS IT (Ryan 2026-07-30) ──────────────────────────────────────
  // Its zone places it against a fixed line, which is all a zone can do — the mark's painted size
  // depends on the artwork's aspect and on the size dial, neither of which exists when a zone is
  // built. So the pair drifted apart and then INTO each other as the mark was resized: 64u of gap at
  // half size, 24u at the default, and minus 6u at the top of the dial, printing the number on the
  // logo. Following the mark's near edge holds the gap at whatever the mark is doing.
  //   Only the slots the zone marked `followMark` — a number BESIDE a mark, not one at 3 o'clock
  // with the mark at 12 (Four Corners), and not one balancing rim type (Crest).
  const CAP_OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
  const followHang = (el) => {
    const view = zoneForSlot(zone, el.slot);
    if (!view.followMark) return null;
    const mark = elements.find((m) => m && m.type === 'logo' && m.logo && m.logo.dataUrl && m.slot === CAP_OPPOSITE[el.slot]);
    if (!mark) return null;
    const g = markGeom(mark);
    const lock = weightLockupBox(el, weight, font, zone, unit);
    if (!g || !lock || !(lock.h > 0)) return null;
    // Along the NUMBER's own outward direction: the mark's anchor sits at a negative radius, so its
    // near edge is `halfExtent − anchor` — which goes positive once the mark crosses the middle of
    // the face, exactly as Stacked's does.
    const vertical = el.slot === 'top' || el.slot === 'bottom';
    const half = vertical ? g.d.h / 2 : g.d.w / 2;
    const markAnchor = Math.hypot(g.a.cx - CENTER, g.a.cy - CENTER);
    const near = half - markAnchor;
    // `hangTo` is where the OUTER ink edge lands, so add the lockup and its own ring back on.
    const hang = near + CAP_PAIR_GAP + inkGrowth(el) + lock.h;
    // …and never off the face. A mark at the top of its dial can ask for more room than there is;
    // giving up a little gap is the right way to fail, and the mark's own field bounds how far it
    // can push before that bites.
    return Math.min(hang, CAP_ART_R - inkGrowth(el));
  };

  let badgeHang = null;
  {
    const badge = elements.find((e) => e && e.type === 'logo' && e.bigBox === true && e.logo && e.logo.dataUrl);
    if (badge) {
      const lg = badge.logo;
      const aspect = (lg.renderUrl && Number.isFinite(lg.renderAspect)) ? lg.renderAspect : resolveLogoAspect(lg);
      const d = logoDrawSize(badge, aspect > 0 ? aspect : 1, zone, logoMatchBox, null, logoCeilingFor(p, badge, { product: opts.product, weight }));
      if (d && d.h > 0) {
        const a = pointAnchor(badge, balanceDelta, zone);
        badgeHang = CENTER - (a.cy - d.h / 2); // the mark's top edge, as a radius from the middle
        // ⛔ …AND IT MAY NEVER PUT THE NUMBER ON THE MARK. Non-negotiable (Ryan 2026-07-30), and the
        // balanced answer alone cannot promise it: the clear space is `2·rise − lockupHeight`, so a
        // taller number style or a wider face eats into it without anything upstream noticing.
        //   `CAP_BADGE_RISE` is set so the balanced answer clears by `CAP_BADGE_CLEAR` at nominal
        // metrics; this is the floor under that, measured against the REAL lockup and the REAL
        // outline stack. When it binds, the number moves OUTWARD — the design gives up perfect
        // symmetry rather than legibility, which is the right way round to fail.
        const numEl = elements.find((e) => e && e.type === 'weight');
        if (numEl) {
          const lock = weightLockupBox(numEl, weight, font, zone, unit);
          if (lock && lock.h > 0) {
            const markBottom = (a.cy + d.h / 2) - CENTER; // how far below the middle the mark reaches
            // An SVG stroke is centred on its path, so each ring grows the ink outward by its full
            // thickness — the same reservation `weightSizes` and `arcOuterRadius` both make.
            const growth = weightInkGrowth(numEl, weight, font, zone, unit);
            // TWICE the growth, and the second one is not belt-and-braces. A stroke is centred, so
            // the stack grows the ink outward by `growth` at the TOP of the lockup — that is the
            // edge facing the mark. The other one covers `stackedLayout`'s own response to a thick
            // stack: it OPENS the gap between the number and its unit line, so the painted lockup is
            // taller than `weightLockupBox` reports. Measured with four 4u rings, one growth left
            // the number 2.2u ON the mark.
            badgeHang = Math.max(badgeHang, markBottom + CAP_BADGE_CLEAR + lock.h + 2 * growth);
          }
        }
      }
    }
  }

  // Paint elements. Logos/weights are point-anchored; arctext follows the ring.
  // Collected separately so height-map mode can wrap the whole ink group in the
  // white-out filter (the ground stays black behind it).
  const designBody = [];
  /* ── THE EMBOSS COMES OFF THE MARK ON BIGGEST BADGE (Ryan 2026-08-12) ──────────────────────────
   * *"The tool adds an emboss to logos… for any of the [Claude Logos] where I'll go with the
   * Biggest Badge layout type on plates, the emboss is a bad look. Can you remove the emboss around
   * logos only when the Biggest Badge layout type is used?"*
   *
   * The emboss is not a separate effect to switch off — it IS this height map, the whole design
   * re-rendered white-on-black to drive the 3D bump. So "no emboss on the logo" means the logo does
   * not contribute to the height field, while everything else still does. Its indices are recorded
   * here and dropped from the height-map group below; the COLOUR render is untouched, and on every
   * other layout so is the height map.
   *
   * ⚠ WHY THIS LAYOUT AND NOT A SIZE TEST. Biggest Badge is the mark at the largest size the
   * printable ring allows, so the emboss stops being a hairline around a small mark and becomes a
   * bevel running through the middle of the artwork — which is what reads badly. That is a property
   * of the LAYOUT, which is what Ryan named, not of any threshold this code could pick.
   */
  const BIGGEST_BADGE = 'big-logo-c-weight-b';
  const noLogoEmboss = heightMap && (p.layoutPreset === BIGGEST_BADGE);
  const logoParts = [];
  elements.forEach((element, i) => {
    const eUid = `${uid}-e${i}`;
    if (element.type === 'arctext') drawArcText(defs, designBody, element, palette, eUid, font, allCaps, bodyColor, arcClamp.get(element.sizeScale ?? 1) ?? null, soloArc, zone);
    else if (element.type === 'weight') drawWeight(designBody, element, palette, weight, font, balanceDelta, zone, bodyColor, unit, eUid, badgeHang || followHang(element));
    else if (element.type === 'logo') {
      // How much of its bonus headroom the arc text has left this logo (Ryan 2026-07-29). Computed
      // per element because two logos in one design can sit at different angles and face different
      // words; `logoCeilingFor` is the same entry the dial's probe uses.
      const ceiling = logoCeilingFor(p, element, { product: opts.product, weight });
      const from = designBody.length;
      drawLogo(defs, designBody, element, eUid, ringClipId, balanceDelta, discClipId, zone, logoMatchBox, ceiling);
      for (let q = from; q < designBody.length; q++) logoParts.push(q);
    }
  });
  // In the 2D PREVIEW (chrome on) a dumbbell/barbell scales its design into the recessed brand
  // disc (capBrandFrac) so the flat image matches the 3D head — the art lands on the inner
  // circle with the raised rubber ring blank around it, and NOT out to the head edge (Ryan
  // 2026-07-22). The 3D texture (chrome off) is untouched: its UV already fits the BrandFace.
  const isCap = opts.product === 'dumbbell' || opts.product === 'barbell';
  if (heightMap) {
    const htId = `${uid}-ht`;
    // Any coverage → opaque WHITE, alpha preserved (soft edges ramp the emboss).
    defs.push(
      `<filter id="${escAttr(htId)}" x="-10%" y="-10%" width="120%" height="120%" color-interpolation-filters="sRGB">` +
        `<feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0"/>` +
        `</filter>`,
    );
    // …minus the logo on Biggest Badge — see `noLogoEmboss`. A `Set` because a design can hold more
    // than one logo element and each contributes several strings.
    const skip = noLogoEmboss ? new Set(logoParts) : null;
    const field = skip ? designBody.filter((_, q) => !skip.has(q)) : designBody;
    body.push(`<g filter="url(#${escAttr(htId)})">${field.join('')}</g>`);
  } else if (isCap && chrome) {
    const s = capBrandFrac(opts.rubber === true);
    body.push(`<g transform="translate(${CENTER} ${CENTER}) scale(${s}) translate(${-CENTER} ${-CENTER})">${designBody.join('')}</g>`);
  } else {
    for (const s of designBody) body.push(s);
  }

  // font-kerning:none + geometricPrecision → size-independent metrics so the 2D
  // preview (≈520px) and the 3D raster (2048px) lay text out identically.
  return (
    `<svg xmlns="${SVG_NS}" viewBox="0 0 450 450" width="${size}" height="${size}" style="display:block">` +
    `<style>text{font-kerning:none;text-rendering:geometricPrecision}</style>` +
    `<defs>${defs.join('')}</defs>` +
    body.join('') +
    `</svg>`
  );
}

/**
 * Back-compat adapter for callers still passing a (profile, template, opts)
 * signature (texture-pipeline / preview). The template arg is ignored — the new
 * model is template-free.
 */
export function renderProfileToSVGString(profile, _template, opts = {}) {
  return renderDesignToSVGString(profile, opts);
}
